import { z } from "zod";
//#region src/ask-user.ts
/**
* `ask_user`: the built-in tool whose call always pauses for the user (epic #303, #309).
*
* A model that needs a decision it cannot make itself asks for one, and the loop stores the
* call, ends the turn `requires_action` and waits — no timeout, no guessing — until a
* `user.tool_confirmation` answers it. The user's answer **is** the tool's result: `ask_user`
* is never run, which is why this module and not `@openharness/hands` carries what a call and
* its answer look like.
*
* Two schemas, one for each direction, and one validator, because every side has to agree
* about them: the model is asked for {@link AskUserInputSchema}'s shape (the AI SDK turns it
* into the JSON Schema the provider sends), the brain checks a call against it before it
* pauses, the answers a client sends are checked against {@link AskUserAnswerSchema} **and**
* against the questions the call asked, and the answers are written into the log as the
* tool's result in {@link formatAskUserAnswers}'s wording.
*
* The narrowness is deliberate: a call that does not fit is answered with an `is_error`
* result the model can fix rather than stored as a question nobody can answer, and an answer
* that does not fit is a 400 rather than a result the model would have to interpret.
*/
/** The name the model calls this tool by, and the name a setting speaks of it as. */
const ASK_USER_TOOL_NAME = "ask_user";
/** The fewest questions one call may ask. */
const ASK_USER_MIN_QUESTIONS = 1;
/** The most questions one call may ask. */
const ASK_USER_MAX_QUESTIONS = 4;
/**
* The longest a question's `header` may be.
*
* A header is the short label a client puts above the question — a chip, a column heading —
* so it is bounded by what one fits on screen rather than by what a sentence needs. Twelve is
* Anthropic's own bound for the field.
*/
const ASK_USER_HEADER_MAX_LENGTH = 12;
/** The fewest options a `choice` question may offer. */
const ASK_USER_MIN_OPTIONS = 2;
/** The most options a `choice` question may offer. */
const ASK_USER_MAX_OPTIONS = 6;
/**
* One option of a `choice` question: the label the answer names it by, and what it means.
*
* A `description` is what a client shows beside the label when there is room — the
* difference between "production" and "the live one" — and carries nothing but a sentence.
*/
const AskUserChoiceOptionSchema = z.object({
	/** The option's name, the way an answer refers to it. */
	label: z.string().min(1),
	/** What choosing it means, when the label is not enough. */
	description: z.string().min(1).optional()
});
/** The fields every question carries, whatever its type. */
const questionBase = {
	/** What the user is being asked, in a sentence. */
	question: z.string().min(1),
	/** A short label a client shows above the question: at most twelve characters. */
	header: z.string().min(1).max(12)
};
/**
* A question with a fixed set of answers.
*
* `multi_select` asks for more than one of them; absent means one at most. A user may always
* answer "Other" with free text instead, which is why a client does not have to be told to
* offer one — it is part of the type, not an option a call lists.
*/
const AskUserChoiceQuestionSchema = z.object({
	...questionBase,
	type: z.literal("choice"),
	/** The options to choose from: at least two, at most six. */
	options: z.array(AskUserChoiceOptionSchema).min(2).max(6),
	/** Whether several options may be chosen. Absent means one at most. */
	multi_select: z.boolean().optional()
});
/** A question answered in the user's own words. */
const AskUserTextQuestionSchema = z.object({
	...questionBase,
	type: z.literal("text"),
	/** What an empty field suggests, when the call offers one. */
	placeholder: z.string().min(1).optional()
});
/** A yes/no question. */
const AskUserConfirmQuestionSchema = z.object({
	...questionBase,
	type: z.literal("confirm")
});
/** One question of an `ask_user` call. */
const AskUserQuestionSchema = z.discriminatedUnion("type", [
	AskUserChoiceQuestionSchema,
	AskUserTextQuestionSchema,
	AskUserConfirmQuestionSchema
]);
/**
* What an `ask_user` call asks: one to four questions (epic #303, X6; #309).
*
* The questions must be **distinct in their `question` text**, because that text is how an
* answer names what it answers ({@link AskUserAnswerSchema}), and two questions a reader
* cannot tell apart are two questions whose answers could be swapped without anyone
* noticing. A call that breaks any of this is a malformed call: the loop answers it with an
* `is_error` result and does not pause, so the model fixes it and asks again.
*/
const AskUserInputSchema = z.object({ questions: z.array(AskUserQuestionSchema).min(1).max(4) }).refine((input) => new Set(input.questions.map((question) => question.question)).size === input.questions.length, { error: "each question must be distinct: an answer names the question it answers" });
/**
* One answer to one question of an `ask_user` call.
*
* The `question` is the text of the question being answered — the call's own words, which the
* distinctness rule makes unambiguous — and which of the other three fields an answer carries
* is decided by that question's type: `labels` for a `choice` (several when it is
* `multi_select`, and `text` as well when the user chose to write their own answer),
* `text` for a `text` question, and `confirmed` for a `confirm` one. Anything else is a 400.
*/
const AskUserAnswerSchema = z.object({
	/** The `question` text of the question this answers. */
	question: z.string().min(1),
	/** The labels chosen, for a `choice` question. */
	labels: z.array(z.string().min(1)).readonly().optional(),
	/** The user's own words: a `text` answer, or the write-in answer to a `choice`. */
	text: z.string().min(1).optional(),
	/** The answer to a `confirm` question. */
	confirmed: z.boolean().optional()
});
/**
* The answers to every question of a call, in the order they are given.
*
* A set rather than a map because the wire spelling of a list is an array: the pairing is by
* the `question` each answer names, and {@link askUserAnswerProblems} is what says whether
* every question got exactly one.
*/
const AskUserAnswersSchema = z.array(AskUserAnswerSchema).readonly();
/**
* The questions a value asks, or `null` when it is not a well-formed `ask_user` input.
*
* The one parser both sides use: the brain checks a call with it before it pauses, and the
* server checks the call an answer is for with it before it validates the answers against it.
*/
function parseAskUserInput(value) {
	const parsed = AskUserInputSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}
/** What is wrong with a value as an `ask_user` input, one `path: message` per problem. */
function askUserInputProblems(value) {
	const parsed = AskUserInputSchema.safeParse(value);
	return parsed.success ? [] : issuesOf(parsed.error.issues);
}
/**
* What is wrong with a set of answers for the questions a call asked, one `path: message`
* per problem — empty when the answers are usable.
*
* Every question has to be answered exactly once, by the text it asked, and the answer has to
* be one the question's own type can carry: a label the question offers (and one at most
* unless it is `multi_select`), the user's own words for a `text` question or beside a chosen
* label, and a yes/no for a `confirm`. This is the rule a malformed **answer** is refused by —
* a 400 at the server, never a result the model has to interpret.
*
* @param input the questions the call asked, already parsed
* @param answers the answers a client sent
*/
function askUserAnswerProblems(input, answers) {
	const problems = [];
	const byQuestion = /* @__PURE__ */ new Map();
	for (const answer of answers) {
		const bucket = byQuestion.get(answer.question);
		if (bucket === void 0) byQuestion.set(answer.question, [answer]);
		else bucket.push(answer);
	}
	for (const question of input.questions) {
		const given = byQuestion.get(question.question) ?? [];
		if (given.length === 0) {
			problems.push(`${question.header}: no answer was given`);
			continue;
		}
		if (given.length > 1) {
			problems.push(`${question.header}: answered more than once`);
			continue;
		}
		problems.push(...problemsOf(question, given[0]));
	}
	for (const answer of answers) if (!input.questions.some((question) => question.question === answer.question)) problems.push(`(answers): "${answer.question}" is not a question this call asked`);
	return problems;
}
/** What is wrong with one answer for one question. */
function problemsOf(question, answer) {
	const where = question.header;
	const labels = answer.labels ?? [];
	const text = answer.text;
	const confirmed = answer.confirmed;
	if (confirmed !== void 0 && question.type !== "confirm") return [`${where}: a yes/no answer does not answer a ${question.type} question`];
	if (question.type === "confirm") {
		if (confirmed === void 0) return [`${where}: a yes/no question needs \`confirmed\``];
		if (labels.length > 0 || text !== void 0) return [`${where}: a yes/no answer carries nothing but \`confirmed\``];
		return [];
	}
	if (question.type === "text") {
		if (text === void 0) return [`${where}: a text question needs \`text\``];
		if (labels.length > 0) return [`${where}: a text question has no options to choose from`];
		return [];
	}
	const offered = new Set(question.options.map((option) => option.label));
	const unknown = labels.filter((label) => !offered.has(label));
	if (unknown.length > 0) return [`${where}: ${unknown.map((label) => `"${label}"`).join(", ")} is not an option this question offers`];
	if (labels.length > 1 && question.multi_select !== true) return [`${where}: this question takes one option, not ${labels.length}`];
	if (labels.length === 0 && text === void 0) return [`${where}: choose an option, or write an answer of your own`];
	return [];
}
/**
* The answers as the text the model is shown, one line per question (epic #303, X6; #309).
*
* The answer is what the loop writes as the call's `agent.tool_result` — the tool never runs,
* so this text **is** what the model gets — and it is the one place the wording lives, so a
* client showing the answers and a model reading them cannot drift. A line leads with the
* question the model asked rather than the header, because that is what the model wrote and
* what it can match against its own call:
*
* ```
* Which environment should I deploy to?: staging
* Anything else I should know?: the release is on Thursday
* ```
*
* Callers pass answers that {@link askUserAnswerProblems} accepted; an answer for an unknown
* question, or a missing one, is simply not rendered here.
*/
function formatAskUserAnswers(input, answers) {
	const byQuestion = new Map(answers.map((answer) => [answer.question, answer]));
	return input.questions.map((question) => {
		const answer = byQuestion.get(question.question);
		return `${question.question}: ${answer === void 0 ? "(no answer)" : rendered(question, answer)}`;
	}).join("\n");
}
/** What one answer reads as, for the question it answers. */
function rendered(question, answer) {
	if (question.type === "confirm") return answer.confirmed === true ? "Yes" : "No";
	if (question.type === "text") return answer.text ?? "";
	const labels = answer.labels ?? [];
	if (answer.text === void 0) return labels.join(", ");
	return labels.length === 0 ? answer.text : `${labels.join(", ")} (write-in: ${answer.text})`;
}
/** The zod issues as the `path: message` lines a caller can act on. */
function issuesOf(issues) {
	return issues.map((issue) => `${issue.path.join(".") || "(input)"}: ${issue.message}`);
}
//#endregion
//#region src/bedrock.ts
/**
* Amazon Bedrock's regions, and the non-secret facts a Bedrock credential reports (epic #245,
* A3c).
*
* A Bedrock request is addressed by **region**: `https://bedrock-runtime.<region>.amazonaws.com`
* for a model call and `https://bedrock.<region>.amazonaws.com` for the control plane — AWS's
* own hosts, derived from the region, never a URL a user typed. That is why this type has no
* endpoint field and no `safeFetch`: there is no user-supplied address for an SSRF guard to
* check. What a user *does* type is the region, and it goes straight into a hostname — so it is
* validated against the list below rather than accepted as a string. An arbitrary region would
* not resolve to an AWS host at all, and a hostile one (`evil.example`) would turn a stored
* credential into a request to somewhere nobody chose.
*
* The list is every region where AWS serves the Bedrock control plane: each of these answers
* `bedrock.<region>.amazonaws.com` with the service's own `Authorization header is missing`
* refusal, which is what a region that has no Bedrock endpoint there does not do. AWS's
* **gov-cloud** and China regions are deliberately out of v1: their endpoints are a different
* partition, and `aws4fetch` (the AWS SDK's signer here) derives the signing partition from the
* region's shape, so `us-gov-west-1` would be signed as a commercial request — a wrong
* signature rather than a working one. Adding a partition is a change to this list and to the
* signer's partition rule, which is a deliberate, separate step.
*
* // extension: Anthropic has no notion of a credential's region — it holds its users' keys.
* The list is openharness's, like the provider list beside it.
*/
/**
* How a region is spelled: an AWS region id, e.g. `us-east-1`.
*
* The shape is checked as well as the membership, so a value that reached the schema by hand
* cannot be one only a human can tell from a region.
*/
const BEDROCK_REGION_PATTERN = /^[a-z]{2}(?:-[a-z]+)+-\d+$/;
/**
* The regions a Bedrock credential may name, in AWS's own order: US, then the Americas, then
* Europe, then Asia Pacific, then the rest.
*
* Every entry is a region AWS serves the Bedrock control plane in — the list is checked as a
* whole by `bedrock.test.ts`, which asserts each one parses against
* {@link BEDROCK_REGION_PATTERN} and that the list has no duplicate.
*/
const BEDROCK_REGIONS = [
	"us-east-1",
	"us-east-2",
	"us-west-1",
	"us-west-2",
	"ca-central-1",
	"ca-west-1",
	"sa-east-1",
	"eu-central-1",
	"eu-central-2",
	"eu-north-1",
	"eu-south-1",
	"eu-south-2",
	"eu-west-1",
	"eu-west-2",
	"eu-west-3",
	"ap-east-2",
	"ap-northeast-1",
	"ap-northeast-2",
	"ap-northeast-3",
	"ap-south-1",
	"ap-south-2",
	"ap-southeast-1",
	"ap-southeast-2",
	"ap-southeast-3",
	"ap-southeast-4",
	"ap-southeast-5",
	"ap-southeast-6",
	"ap-southeast-7",
	"af-south-1",
	"il-central-1",
	"mx-central-1"
];
/**
* The region a Bedrock credential takes when the user picks none: the oldest and the one AWS
* documents every model in. A form preselects it; the schema still requires a region.
*/
const DEFAULT_BEDROCK_REGION = "us-east-1";
/** Whether `value` is a region a Bedrock credential may name. */
function isBedrockRegion(value) {
	return BEDROCK_REGIONS.includes(value);
}
/**
* The `details` a Bedrock credential reports — its region, and nothing else — as the credential
* type's own variant of {@link ProviderCredentialDetails} (`{ region }`, #245 A3c). The one
* field of a stored Bedrock credential that is neither a secret nor its `last4`, and what a
* settings list shows so two Bedrock rows are told apart.
*/
function bedrockCredentialDetails(region) {
	return { region };
}
/** The region a stored Bedrock credential reports, or `undefined` for one that reports none. */
function bedrockRegionOf(details) {
	const region = details?.region;
	return region === void 0 || region === "" ? void 0 : region;
}
//#endregion
//#region src/common.ts
/**
* An RFC 3339 timestamp, for example `2026-03-15T10:00:00Z`.
*
* Matches the `format: date-time` fields of Anthropic's Managed Agents API. Both the UTC
* (`Z`) and the numeric-offset (`+02:00`) forms are accepted; values are never normalised,
* so a timestamp round-trips byte for byte.
*/
const TimestampSchema = z.iso.datetime({ offset: true });
/** Maximum number of key/value pairs Anthropic documents for a metadata map. */
const METADATA_MAX_PAIRS = 16;
/** Maximum length of a metadata key. */
const METADATA_MAX_KEY_LENGTH = 64;
/** Maximum length of a metadata value. */
const METADATA_MAX_VALUE_LENGTH = 512;
/**
* An arbitrary string-keyed metadata map, bounded the way Anthropic bounds it: at most
* {@link METADATA_MAX_PAIRS} pairs, keys up to {@link METADATA_MAX_KEY_LENGTH} characters and
* values up to {@link METADATA_MAX_VALUE_LENGTH}.
*/
const MetadataSchema = z.record(z.string().max(64), z.string().max(512)).refine((metadata) => Object.keys(metadata).length <= 16, { error: `metadata holds at most 16 pairs` });
/** Page size used when a list request omits `limit`. Matches Anthropic's default. */
const DEFAULT_PAGE_LIMIT = 20;
/** Largest page size a list request may ask for. Matches Anthropic's maximum. */
const MAX_PAGE_LIMIT = 100;
/**
* The `limit` query parameter of every list endpoint.
*
* Query strings arrive as text, so this schema coerces: `'20'` and `20` both parse to `20`.
*/
const PageLimitSchema = z.coerce.number().int().min(1).max(100);
/**
* Sort direction for list endpoints. Anthropic's `order` parameter; `asc` (oldest first) is
* the default everywhere in this package.
*/
const ListOrderSchema = z.enum(["asc", "desc"]);
/**
* // extension: a calendar day in a reader's own time zone, as `YYYY-MM-DD` (issue #247).
*
* Usage is reported per **local** day — "what did I spend today" means the reader's today, not
* UTC's — so the usage routes take and answer with days rather than instants, and the zone they
* were read in travels beside them. The shape is the date half of an RFC 3339 timestamp, which
* is what `Intl`'s `en-CA` locale and `Date.prototype.toISOString().slice(0, 10)` both produce
* and what a `date` column answers with.
*
* It carries no zone of its own: the same day means different instants in different zones,
* which is exactly why every response that holds one says which zone it was read in.
*/
const LocalDaySchema = z.iso.date();
//#endregion
//#region src/providers.ts
/**
* The providers, in the order every side lists them: the common ones a reader is likeliest to
* have first, then the rest. The order is the one the credentials flow has always used, and
* the catalogue and the frontends keep it rather than retyping it.
*/
const PROVIDERS = [
	{
		id: "anthropic",
		name: "Anthropic",
		credential: "api_key",
		modelsDevKey: "anthropic",
		keyUrl: "https://console.anthropic.com/settings/keys"
	},
	{
		id: "openai",
		name: "OpenAI",
		credential: "api_key",
		modelsDevKey: "openai",
		keyUrl: "https://platform.openai.com/api-keys"
	},
	{
		id: "google",
		name: "Google",
		credential: "api_key",
		modelsDevKey: "google",
		keyUrl: "https://aistudio.google.com/apikey"
	},
	{
		id: "openrouter",
		name: "OpenRouter",
		credential: "api_key",
		modelsDevKey: "openrouter",
		keyUrl: "https://openrouter.ai/keys"
	},
	{
		id: "groq",
		name: "Groq",
		credential: "api_key",
		modelsDevKey: "groq",
		keyUrl: "https://console.groq.com/keys"
	},
	{
		id: "deepseek",
		name: "DeepSeek",
		credential: "api_key",
		modelsDevKey: "deepseek",
		keyUrl: "https://platform.deepseek.com/api_keys"
	},
	{
		id: "fireworks",
		name: "Fireworks AI",
		credential: "api_key",
		modelsDevKey: "fireworks-ai",
		keyUrl: "https://fireworks.ai/"
	},
	{
		id: "mistral",
		name: "Mistral",
		credential: "api_key",
		modelsDevKey: "mistral",
		keyUrl: "https://console.mistral.ai/api-keys"
	},
	{
		id: "together",
		name: "Together AI",
		credential: "api_key",
		modelsDevKey: "togetherai",
		keyUrl: "https://api.together.ai/"
	},
	{
		id: "xai",
		name: "xAI",
		credential: "api_key",
		modelsDevKey: "xai",
		keyUrl: "https://console.x.ai/"
	},
	{
		id: "cerebras",
		name: "Cerebras",
		credential: "api_key",
		modelsDevKey: "cerebras",
		keyUrl: "https://cloud.cerebras.ai/"
	}
];
/** Every provider id, in {@link PROVIDERS} order. */
const PROVIDER_IDS = PROVIDERS.map((provider) => provider.id);
/**
* The named credential types, in the order every side lists them — the list above, widened to
* {@link CredentialTypeDefinition} so a caller reads an optional fact (`keyUrl`, `modelsDevKey`)
* without the literal union refusing the member that omits it.
*/
const CREDENTIAL_TYPES = [
	{
		type: "azure_openai",
		name: "Azure OpenAI",
		defaultName: "azure",
		modelsDevKey: "azure",
		keyUrl: "https://portal.azure.com/",
		modelIdHint: "deployment"
	},
	{
		type: "openai_compatible",
		name: "Custom (OpenAI-compatible)",
		defaultName: "custom",
		modelIdHint: "model"
	},
	{
		type: "bedrock",
		name: "Amazon Bedrock",
		defaultName: "bedrock",
		modelsDevKey: "amazon-bedrock",
		keyUrl: "https://console.aws.amazon.com/iam/home#/security_credentials",
		modelIdHint: "model id"
	},
	{
		type: "vertex",
		name: "Google Vertex",
		defaultName: "vertex",
		modelsDevKey: "google-vertex",
		keyUrl: "https://console.cloud.google.com/iam-admin/serviceaccounts",
		modelIdHint: "model"
	}
];
/**
* The character shape a credential name must have: lowercase letters, digits and single dashes.
*
* The name is the `provider` half of a model id, so it has to survive being typed, sent in a
* URL path and matched against a `provider/model` string. Lowercase-only keeps two spellings
* from meaning the same credential; the 11 fixed provider ids are already lowercase and fit it.
*/
const CREDENTIAL_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** How long a credential name may be. Short is the point: it prefixes every model id. */
const CREDENTIAL_NAME_MAX_LENGTH = 32;
/** The facts for a credential type, or `undefined` for `api_key` (which has no default name). */
function credentialTypeInfo(type) {
	return CREDENTIAL_TYPES.find((entry) => entry.type === type);
}
/** What to call a credential type, or `undefined` for one this list does not carry. */
function credentialTypeName(type) {
	return credentialTypeInfo(type)?.name;
}
/**
* The name a credential of `type` defaults to when the user is adding their first one, or
* `undefined` for `api_key` — whose name is always the fixed provider id, never a default.
*/
function defaultCredentialName(type) {
	return credentialTypeInfo(type)?.defaultName;
}
/**
* Whether `name` is a legal credential name in isolation: short, lowercase, dash-separated.
*
* This says nothing about collisions — see {@link isReservedCredentialName} — because which
* names are taken is a per-user question the server answers against the store.
*/
function isValidCredentialName(name) {
	return name.length > 0 && name.length <= 32 && CREDENTIAL_NAME_PATTERN.test(name);
}
/**
* Whether `name` is one the eleven fixed provider ids already own.
*
* A named credential may not take one: `openai` names the OpenAI provider, and a second
* credential calling itself `openai` would make `openai/gpt-5` ambiguous between them. The
* server refuses such a name on save.
*/
function isReservedCredentialName(name) {
	return PROVIDER_IDS.includes(name);
}
//#endregion
//#region src/constants.ts
/**
* Values every part of openharness agrees on: route prefix, header names, page limits and
* the session → partition function.
*
* Nothing here does I/O; the server, the store and the clients all import these constants so
* that they cannot drift apart.
*/
/** Prefix of every HTTP route in the API (`/v1/agents`, `/v1/sessions`, ...). */
const API_VERSION_PREFIX = "/v1";
/** Header carrying the API version date (Anthropic: `2023-06-01`). */
const ANTHROPIC_VERSION_HEADER = "anthropic-version";
/** Header opting into beta features (Anthropic Managed Agents: `managed-agents-2026-04-01`). */
const ANTHROPIC_BETA_HEADER = "anthropic-beta";
/**
* API version date this server speaks, sent in {@link ANTHROPIC_VERSION_HEADER}. Anthropic's
* `2023-06-01` sentinel; the Managed Agents surface itself is versioned by the
* `managed-agents-2026-04-01` beta header.
*/
const API_VERSION_DATE = "2023-06-01";
/**
* SSE resume header. On a stream request it carries the `id` of the last `sevt_` event the
* client already saw; the server replays everything after that event's `seq`.
*
* `last-event-id` is the standard `EventSource` header, so browsers send it automatically.
*/
const LAST_EVENT_ID_HEADER = "last-event-id";
/** Header carrying the server-assigned request id. Mirrored in error bodies as `request_id`. */
const REQUEST_ID_HEADER = "request-id";
/** `content-type` of JSON request and response bodies. */
const JSON_CONTENT_TYPE = "application/json";
/** `content-type` of the SSE event stream. */
const SSE_CONTENT_TYPE = "text/event-stream";
/**
* Default number of session partitions.
*
* A `sessionId` hashes to exactly one of these; a server instance owns a set of partitions.
* See {@link partitionOf}.
*/
const DEFAULT_PARTITION_COUNT = 64;
/** FNV-1a 32-bit offset basis. */
const FNV_OFFSET_BASIS = 2166136261;
/** FNV-1a 32-bit prime. */
const FNV_PRIME = 16777619;
const utf8 = new TextEncoder();
/**
* MurmurHash3's 32-bit finalizer: spreads the low bits of `hash` across the whole word.
*
* FNV-1a alone is not a good mixer for the low bits, which is exactly the part `% n` keeps.
* Measured over `sesn_0` … `sesn_255`, plain FNV-1a maps consecutive ids to consecutive
* partitions in a near-cyclic pattern — χ² across 64 bins is 2 rather than ~63, and only 26
* of the 64 possible step sizes between neighbours appear. Uniform *counts* are not enough:
* partitions would follow id order, so any structured id space (a test fixture, a migration,
* a client that mints its own ids) could be steered into one partition. fmix32 restores
* avalanche: every step size appears, and χ² sits at the expected ~63.
*/
function fmix32(hash) {
	let h = hash;
	h ^= h >>> 16;
	h = Math.imul(h, 2246822507);
	h ^= h >>> 13;
	h = Math.imul(h, 3266489909);
	h ^= h >>> 16;
	return h >>> 0;
}
/** FNV-1a over the UTF-8 bytes of `value`. */
function fnv1a32(value) {
	let hash = FNV_OFFSET_BASIS;
	for (const byte of utf8.encode(value)) {
		hash ^= byte;
		hash = Math.imul(hash, FNV_PRIME);
	}
	return hash >>> 0;
}
/**
* The partition a session belongs to, in `[0, partitionCount)`.
*
* A stable, pure hash of `sessionId`: the server and the store call it with the same id and
* get the same partition, so ownership survives restarts and deploys. FNV-1a alone would put
* a `sesn_` id's ULID timestamp in the high bits of the result, which would cluster sessions
* created in the same millisecond, so the hash is finished with {@link fmix32}.
*
* @param sessionId the `sesn_` session id (any string works; it is hashed as UTF-8 bytes)
* @param partitionCount number of partitions; defaults to {@link DEFAULT_PARTITION_COUNT}
* @throws RangeError if `partitionCount` is not a positive integer
*/
function partitionOf(sessionId, partitionCount = 64) {
	if (!Number.isInteger(partitionCount) || partitionCount <= 0) throw new RangeError(`partitionCount must be a positive integer, got ${partitionCount}`);
	return fmix32(fnv1a32(sessionId)) % partitionCount;
}
//#endregion
//#region src/content.ts
/**
* Message content blocks.
*
* v1 carries text and nothing else. Anthropic's user messages also accept image, document
* and file blocks, and its agent messages can contain a `redacted` placeholder; both are out
* of scope here, so {@link ContentBlockSchema} rejects them (see `AGENTS.md`).
*/
/** A block of plain text. Matches Anthropic's `text` block, including its non-empty rule. */
const TextBlockSchema = z.object({
	type: z.literal("text"),
	text: z.string().min(1)
});
/**
* One content block of a message.
*
* A discriminated union with a single member in v1 — new block types are added here, and
* every schema built on it picks them up.
*/
const ContentBlockSchema = z.discriminatedUnion("type", [TextBlockSchema]);
/**
* The `content` array of a `user.message` or `agent.message` event.
*
* Not bounded below: a model can legitimately answer with no text at all, and an empty
* `content` is how that is represented.
*/
const ContentBlocksSchema = z.array(ContentBlockSchema);
/**
* // extension: one web source a reply is built on (epic #303, X14; issue #335).
*
* Every provider that can search the web normalizes what it found its own way — Anthropic sends
* a `web_search_result_location` citation on a text block, OpenAI a `url_citation` annotation,
* Gemini a `groundingMetadata` chunk — and each spelling carries different extras (an encrypted
* index, a character range, a page age). What a reader is owed is the same three things from all
* of them: where it came from, what it was called, and the sentence the reply leans on.
*
* So this is the one `sources` list a client renders. It is a **readable** record and nothing
* else: it is never sent back to a provider (the provider's own unreadable form — an encrypted
* index, an encrypted result body — is replayed from
* `agent.opaque_content` instead), and it carries no cost of its own.
*/
const SourceSchema = z.object({
	/** The page's URL, absolute, as the provider gave it. */
	url: z.string().min(1),
	/** The page's title, when the provider gave one. */
	title: z.string().optional(),
	/**
	* The passage of the reply this source supports, when the provider said which — the cited
	* text of an Anthropic citation, or nothing for a provider that cites the whole message.
	*/
	cited_text: z.string().optional()
});
/** The `sources` a reply cites, in the order the provider gave them. */
const SourcesSchema = z.array(SourceSchema);
//#endregion
//#region src/cost.ts
/**
* What a pile of tokens costs (epic #245, A2; issue #247).
*
* Cost is **never stored and never estimated**: every number here is computed when it is read,
* from the token counters a `span.model_request_end` really reported and the list prices the
* model catalog carries (`ModelEntry.cost`, from the vendored models.dev snapshot). Two
* readers — the server's usage routes and the frontends' reply metadata — price the same
* tokens with the same arithmetic, which is why the arithmetic lives in the protocol they all
* depend on rather than in one of them.
*
* The rules, and there are only four:
*
* - **Rates are per million tokens, in USD.** models.dev publishes them that way; the division
*   happens here so no caller has to remember it.
* - **Cache tokens are priced separately where models.dev has those rates** — `cache_read` and
*   `cache_write` are their own prices, not a fraction of the input rate, because a cached
*   token really is a different price at every provider.
* - **A token count we have no rate for is `null`, never `0`.** A model with no price at all
*   contributes its tokens and reports no cost; one that publishes an input and output rate but
*   no cache rates reports no cost for a request that used cache tokens, because charging
*   nothing for tokens that were really spent would understate the bill. A count of zero costs
*   zero whatever the rate is — nothing was spent — which is what keeps a request without cache
*   tokens priceable on a model that has no cache rate.
* - **A total sums the parts it can price and counts the ones it cannot** (#247, decided
*   2026-10-09). One request nobody prices no longer makes a whole session unreadable: the
*   total is the priced part, and the unknown part is *named* — an `unpriced_requests` count
*   beside the money — rather than guessed. The total is `null` only when nothing in it could
*   be priced.
*/
/**
* The cost of the tokens in `usage`, in USD, at `cost`'s rates — or `null` when it cannot be
* known: the model has no price, or it has none for a counter that is not zero.
*
* @param usage the token counters of one request, or the totals of many
* @param cost the model's list price, or `null` when the catalog has none for it
*/
function usageCost(usage, cost) {
	if (cost === null) return null;
	const counters = [
		[usage.input_tokens, cost.input],
		[usage.output_tokens, cost.output],
		[usage.cache_creation_input_tokens, cost.cache_write],
		[usage.cache_read_input_tokens, cost.cache_read]
	];
	let total = 0;
	for (const [tokens, rate] of counters) {
		if (tokens === 0) continue;
		if (rate === null) return null;
		total += tokens * rate;
	}
	return total / 1e6;
}
/**
* The total of a set of costs: the priced ones summed, the unpriced ones counted.
*
* One request nobody publishes a price for no longer makes a whole session's cost unknown — the
* total is what the priced requests came to, and `unpriced_requests` says how many were left out
* (#247, decided 2026-10-09). The total is `null` only when **nothing** in it could be priced,
* an empty set included: nothing was spent, but nothing was priced either, and a reader that must
* show a number can treat that case as it likes.
*
* @param costs each part's cost, or `null` for a part nobody prices
*/
function totalCost(costs) {
	let cost = 0;
	let priced = false;
	let unpriced_requests = 0;
	for (const part of costs) {
		if (part === null) {
			unpriced_requests += 1;
			continue;
		}
		cost += part;
		priced = true;
	}
	return {
		cost: priced ? cost : null,
		unpriced_requests
	};
}
/**
* // extension: what one **provider-executed web search** costs, per provider (epic #303, X14;
* issue #335).
*
* A search runs inside the model request, on the user's own model key, and the model catalog is
* no help: models.dev prices tokens and records nothing about a tool. Two of the three providers
* this build can search with charge nothing *beside* the tokens — OpenAI and xAI price their
* search through the model's own rates, which `usageCost` already accounts for — and one charges
* a flat fee on top, recorded here from the provider's published price:
*
* | provider    | per search | source                                                  |
* | ----------- | ---------- | ------------------------------------------------------- |
* | `anthropic` | $0.01      | $10 per 1,000 searches, on top of tokens (published rate) |
*
* A provider **absent** from the table is not "unknown": it is a provider with no separate fee,
* which is a statement this table can make because it names every provider this build can attach
* a search tool to. A search is still counted wherever it cannot be priced (`searches` in the
* usage responses), so nothing is invented to fill a hole — and nothing is charged twice: the
* tokens a search's results add to the request are in the request's own counters.
*/
const SEARCH_PRICE_PER_CALL = { anthropic: 10 / 1e3 };
/**
* What one request's provider-executed searches cost, in USD — `0` for a provider that charges
* nothing beside the tokens (`@openharness/protocol`'s `SEARCH_PRICE_PER_CALL`).
*
* @param provider the provider half of the request's model id
* @param searches how many searches the provider ran inside it
*/
function searchCost(provider, searches) {
	return (SEARCH_PRICE_PER_CALL[provider] ?? 0) * searches;
}
//#endregion
//#region src/errors.ts
/**
* The Anthropic HTTP error envelope, shared by every endpoint.
*
* ```json
* {
*   "type": "error",
*   "error": { "type": "not_found_error", "message": "The requested resource could not be found." },
*   "request_id": "req_011CSHoEeqs5C35K2UUqR7Fy"
* }
* ```
*
* Note that the HTTP error types here (`not_found_error`, `rate_limit_error`, ...) are a
* different vocabulary from the types carried inside a stored `session.error` event
* (`model_overloaded_error`, ...): the first describes a failed HTTP request, the second
* describes a failed turn. Both mostly use the `_error` suffix; the exceptions are this
* package's own extensions, `invalid_provider_credential` here and
* `missing_provider_credential` in `events/session.ts`.
*
* Authentication failures are the `authentication_error` (401) type: a request with no
* session cookie, or with a bearer token that is missing, malformed, expired or revoked.
* Sign-in itself is not an HTTP API of this protocol — it is Better Auth's `/api/auth/*`
* surface (epic #65, A1).
*/
/** Every error type the API returns. */
const API_ERROR_TYPES = [
	"invalid_request_error",
	"authentication_error",
	"billing_error",
	"permission_error",
	"not_found_error",
	"conflict_error",
	"request_too_large",
	"invalid_provider_credential",
	"mode_unavailable_error",
	"mcp_connection_error",
	"skill_import_error",
	"rate_limit_error",
	"api_error",
	"timeout_error",
	"overloaded_error"
];
/** An error type string accepted by the API. */
const ApiErrorTypeSchema = z.enum(API_ERROR_TYPES);
/**
* The HTTP status each error type is served with, as Anthropic documents it in
* "Claude API errors". The SDKs branch on the status and the `error.type` string.
*
* Typed as a total record, so adding an entry to {@link API_ERROR_TYPES} without giving it a
* status is a compile error.
*/
const API_ERROR_STATUS_BY_TYPE = {
	/** 400 — malformed request, bad parameters, or a request rejected on its content. */
	invalid_request_error: 400,
	/** 401 — not signed in: no session cookie or bearer token, or the one presented is invalid or expired. */
	authentication_error: 401,
	/** 402 — the caller cannot pay for the request (billing or payment problem). */
	billing_error: 402,
	/** 403 — the caller is signed in but not allowed to use this resource. */
	permission_error: 403,
	/**
	* 404 — the resource id in the path does not exist — or it exists and belongs to another
	* user. v1 has no sharing, so another user's agent, session or credential is answered as
	* missing rather than forbidden (epic #65, A4): a 403 would confirm it exists.
	*/
	not_found_error: 404,
	/** 409 — the request conflicts with the resource's current state. */
	conflict_error: 409,
	/** 413 — the request body is too large. */
	request_too_large: 413,
	/** 422 — a provider credential failed validation against its provider on save. */
	invalid_provider_credential: 422,
	/**
	* 422 — a chat asked for — or continues on — a mode whose model cannot be used: no
	* credential for its provider, or "my default model" with no default set. Unprocessable
	* rather than a conflict: the request is well-formed, but the mode's model is not usable
	* right now (epic #245, M6).
	*/
	mode_unavailable_error: 422,
	/**
	* 422 — an MCP server could not be reached, its OAuth discovery failed, or it offers no
	* dynamic client registration (epic #303, X10). Unprocessable rather than a conflict: the
	* request is well-formed, but the server could not be brought into a usable state.
	*/
	mcp_connection_error: 422,
	/**
	* 422 — a skill import could not turn its URL into a snapshot (epic #349, K3): the host was
	* unreachable, what answered was not a repository, the repository is private, the ref or the
	* path is not there, the path holds no `SKILL.md`, the clone broke a limit of K4, or the
	* address is one this deployment refuses. Unprocessable rather than a conflict: the request
	* is well-formed, but no snapshot can be read from it. Which of those it was is
	* `SKILL_IMPORT_MESSAGES` (`resources/skill.ts`), one sentence per failure.
	*/
	skill_import_error: 422,
	/** 429 — rate limited, or a spend limit was reached. */
	rate_limit_error: 429,
	/** 500 — an unexpected internal error. */
	api_error: 500,
	/** 504 — the request timed out while processing. */
	timeout_error: 504,
	/** 529 — the API is temporarily overloaded. */
	overloaded_error: 529
};
/** The `error` object of {@link ApiErrorBodySchema}: a type and a human-readable message. */
const ApiErrorSchema = z.object({
	type: ApiErrorTypeSchema,
	message: z.string()
});
/**
* The body of every non-2xx JSON response.
*
* `request_id` is the same value as the `request-id` response header; it is optional here
* because a proxy, or an error raised before the request reaches the app, may not set it.
*/
const ApiErrorBodySchema = z.object({
	type: z.literal("error"),
	error: ApiErrorSchema,
	request_id: z.string().optional()
});
/**
* Build an error body for the wire.
*
* @param type an error type from {@link API_ERROR_TYPES}
* @param message human-readable description
* @param requestId value of the `request-id` header, when there is one
*/
function apiErrorBody(type, message, requestId) {
	return {
		type: "error",
		error: {
			type,
			message
		},
		...requestId === void 0 ? {} : { request_id: requestId }
	};
}
/** The HTTP status an error type is served with. */
function httpStatusForErrorType(type) {
	return API_ERROR_STATUS_BY_TYPE[type];
}
/** Whether `value` is one of the API's error types. */
function isApiErrorType(value) {
	return typeof value === "string" && API_ERROR_TYPES.some((type) => type === value);
}
//#endregion
//#region src/ids.ts
/**
* Resource and event identifiers.
*
* Every id is `<prefix><ULID>`, using the same prefixes as Anthropic's Managed Agents API —
* `agent_` for agents, `sesn_` for sessions and `sevt_` for stored session events — plus
* `pcred_` for provider credentials, `mode_` for modes, `mcps_` for remote MCP servers and
* `skl_` for skills and `task_` for the background tasks a chat starts (openharness
* extensions; Anthropic has none of them). A ULID is 26 Crockford base32 characters: a 48-bit millisecond
* timestamp followed by 80 random bits, so ids sort by creation time and are globally unique
* without coordination.
*
* The ids this module produces are also **branded** at the type level
* ({@link AgentId}, {@link SessionId}, {@link EventId}, {@link ProviderCredentialId},
* {@link ModeId}, {@link McpServerId}, {@link TaskId}, {@link SkillId}), so a session id cannot
* be passed where an event id is expected.
*/
/** The id prefixes this protocol uses, keyed by the kind of thing they name. */
const ID_PREFIXES = {
	agent: "agent_",
	session: "sesn_",
	event: "sevt_",
	providerCredential: "pcred_",
	mode: "mode_",
	mcpServer: "mcps_",
	skill: "skl_",
	task: "task_"
};
const ID_PREFIX_ENTRIES = [
	["agent", ID_PREFIXES.agent],
	["session", ID_PREFIXES.session],
	["event", ID_PREFIXES.event],
	["providerCredential", ID_PREFIXES.providerCredential],
	["mode", ID_PREFIXES.mode],
	["mcpServer", ID_PREFIXES.mcpServer],
	["skill", ID_PREFIXES.skill],
	["task", ID_PREFIXES.task]
];
/** Length of the ULID part of an id. */
const ULID_LENGTH = 26;
/** Crockford base32: `0-9` then `A-Z` without `I`, `L`, `O` and `U`. */
const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/**
* 26 Crockford base32 characters, with the first one capped at `7`.
*
* A ULID's 10-character time prefix carries 50 bits but only 48 are a timestamp: the top two
* bits must be zero, which limits the first character to `7`. Without the cap, `isUlid` would
* accept a value encoding an instant past 2^48 ms — one {@link ulid} can never produce and
* that is not comparable with the timestamps next to it.
*/
const ULID_PATTERN = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const ULID_TIME_LENGTH = 10;
const ULID_RANDOM_BYTES = 10;
/** Largest 48-bit millisecond timestamp a ULID can encode. */
const ULID_MAX_TIME = 2 ** 48 - 1;
/**
* Encode a millisecond timestamp as the 10-character time prefix of a ULID.
*
* @param timestampMs milliseconds since the Unix epoch, `0 <= timestampMs <= 2^48 - 1`
*/
function encodeTime(timestampMs) {
	let remaining = timestampMs;
	let encoded = "";
	for (let i = 0; i < ULID_TIME_LENGTH; i += 1) {
		encoded = ULID_ALPHABET.charAt(remaining % 32) + encoded;
		remaining = Math.floor(remaining / 32);
	}
	return encoded;
}
/** Encode 10 random bytes as the 16-character random suffix of a ULID. */
function encodeRandom(bytes) {
	let value = 0n;
	for (const byte of bytes) value = value << 8n | BigInt(byte);
	let encoded = "";
	for (let i = 0; i < 16; i += 1) {
		encoded = ULID_ALPHABET.charAt(Number(value & 31n)) + encoded;
		value >>= 5n;
	}
	return encoded;
}
/**
* Generate a ULID from the current time and a fresh 80-bit random value.
*
* @param timestampMs creation time to encode; defaults to `Date.now()`
* @param randomBytes exactly 10 bytes of randomness; defaults to `crypto.getRandomValues`.
*   Pass your own only in tests, where a deterministic id is worth more than uniqueness.
* @throws RangeError if `timestampMs` is outside the 48-bit range or `randomBytes` is the
*   wrong length
*/
function ulid(timestampMs = Date.now(), randomBytes = crypto.getRandomValues(new Uint8Array(ULID_RANDOM_BYTES))) {
	const time = Math.floor(timestampMs);
	if (!Number.isFinite(time) || time < 0 || time > ULID_MAX_TIME) throw new RangeError(`ULID timestamp out of range: ${timestampMs}`);
	if (randomBytes.length !== ULID_RANDOM_BYTES) throw new RangeError(`ULID randomness must be ${ULID_RANDOM_BYTES} bytes`);
	return encodeTime(time) + encodeRandom(randomBytes);
}
/**
* Whether `value` is a ULID this package could have generated: 26 Crockford base32
* characters, no `I`/`L`/`O`/`U`, and a time prefix within the 48-bit range.
*/
function isUlid(value) {
	return typeof value === "string" && ULID_PATTERN.test(value);
}
/**
* Whether `value` is an id of `type` (or of any type when `type` is omitted).
*
* @param value candidate id
* @param type when given, the id must name exactly this kind of thing
*/
function isId(value, type) {
	const parsed = typeof value === "string" ? tryParseId(value) : null;
	return parsed !== null && (type === void 0 || parsed.type === type);
}
/** Whether `value` is an `agent_` id. */
function isAgentId(value) {
	return isId(value, "agent");
}
/** Whether `value` is a `sesn_` id. */
function isSessionId(value) {
	return isId(value, "session");
}
/** Whether `value` is a `sevt_` id. */
function isEventId(value) {
	return isId(value, "event");
}
/** Whether `value` is a `pcred_` id. */
function isProviderCredentialId(value) {
	return isId(value, "providerCredential");
}
/** Whether `value` is a `mode_` id. */
function isModeId(value) {
	return isId(value, "mode");
}
/**
* Parse an id into its prefix and ULID.
*
* @throws RangeError if `value` is not a known prefix followed by a valid ULID
*/
function parseId(value) {
	for (const [type, prefix] of ID_PREFIX_ENTRIES) if (value.startsWith(prefix)) {
		const suffix = value.slice(prefix.length);
		if (isUlid(suffix)) return {
			type,
			prefix,
			ulid: suffix
		};
	}
	throw new RangeError(`not a valid id: ${JSON.stringify(value)}`);
}
/** {@link parseId}, returning `null` instead of throwing. */
function tryParseId(value) {
	try {
		return parseId(value);
	} catch {
		return null;
	}
}
/**
* Generate a new id of the given kind: prefix + ULID.
*
* @param type which prefix to use
* @param timestampMs creation time to encode; defaults to `Date.now()`
*/
function generateId(type, timestampMs) {
	return ID_PREFIXES[type] + ulid(timestampMs);
}
/** A new `agent_` id. */
function newAgentId(timestampMs) {
	return AgentIdSchema.parse(generateId("agent", timestampMs));
}
/** A new `sesn_` id. */
function newSessionId(timestampMs) {
	return SessionIdSchema.parse(generateId("session", timestampMs));
}
/** A new `sevt_` id. */
function newEventId(timestampMs) {
	return EventIdSchema.parse(generateId("event", timestampMs));
}
/** A new `pcred_` id: a provider credential's metadata row. */
function newProviderCredentialId(timestampMs) {
	return ProviderCredentialIdSchema.parse(generateId("providerCredential", timestampMs));
}
/** A new `mode_` id: a user's mode. */
function newModeId(timestampMs) {
	return ModeIdSchema.parse(generateId("mode", timestampMs));
}
/** A new `mcps_` id: a user's remote MCP server. */
function newMcpServerId(timestampMs) {
	return McpServerIdSchema.parse(generateId("mcpServer", timestampMs));
}
/** A new `task_` id: a background task a chat started (epic #340, S4; #343). */
function newTaskId(timestampMs) {
	return TaskIdSchema.parse(generateId("task", timestampMs));
}
/** Whether `value` is an `mcps_` id. */
function isMcpServerId(value) {
	return isId(value, "mcpServer");
}
/** A new `skl_` id: a user's imported skill. */
function newSkillId(timestampMs) {
	return SkillIdSchema.parse(generateId("skill", timestampMs));
}
/** Whether `value` is an `skl_` id. */
function isSkillId(value) {
	return isId(value, "skill");
}
/** Whether `value` is a `task_` id. */
function isTaskId(value) {
	return isId(value, "task");
}
/**
* Branded `agent_` id. Any agent id in this package is interchangeable with `string`, but a
* session or event id is not.
*/
const AgentIdSchema = z.string().refine(isAgentId, { error: "must be an `agent_` id" }).brand();
/** Branded `sesn_` id. */
const SessionIdSchema = z.string().refine(isSessionId, { error: "must be a `sesn_` id" }).brand();
/** Branded `sevt_` id. */
const EventIdSchema = z.string().refine(isEventId, { error: "must be a `sevt_` id" }).brand();
/**
* Branded `pcred_` id. // extension: Anthropic's Managed Agents API has no provider
* credentials, so this prefix is openharness' own.
*/
const ProviderCredentialIdSchema = z.string().refine(isProviderCredentialId, { error: "must be a `pcred_` id" }).brand();
/**
* Branded `mode_` id. // extension: Anthropic's Managed Agents API has no modes, so this
* prefix is openharness' own.
*/
const ModeIdSchema = z.string().refine(isModeId, { error: "must be a `mode_` id" }).brand();
/**
* Branded `mcps_` id. // extension: Anthropic's Managed Agents API has no per-user MCP server
* resource, so this prefix is openharness' own.
*/
const McpServerIdSchema = z.string().refine(isMcpServerId, { error: "must be an `mcps_` id" }).brand();
/**
* Branded `skl_` id. // extension: Anthropic's Managed Agents API has no per-user skill
* resource, so this prefix is openharness' own.
*/
const SkillIdSchema = z.string().refine(isSkillId, { error: "must be an `skl_` id" }).brand();
/**
* Branded `task_` id. // extension: Anthropic's Managed Agents API has no background-task
* resource, so this prefix is openharness' own. A task is what a chat's `agent` call starts
* (epic #340, S4): the id `session.task_started` carries, the handle `agent_message` /
* `agent_wait` / `agent_stop` name, and the value `Session.parent_task_id` holds on the child
* session the task runs.
*/
const TaskIdSchema = z.string().refine(isTaskId, { error: "must be a `task_` id" }).brand();
//#endregion
//#region src/tools.ts
/**
* The tool vocabulary both halves of a tool call share: what the model may do, what the log
* says about it, and what a call's input looks like on the wire.
*
* The shapes follow Anthropic's Managed Agents API — `agent.tool_use`/`agent.tool_result` and
* `agent.mcp_tool_use`/`agent.mcp_tool_result` in `events/agent.ts`, and an
* `evaluated_permission` on each call — for the subset epic #303 builds. This module also holds
* the one spelling both halves of an MCP tool call share: the model-facing name a remote tool
* is offered under ({@link mcpToolOfferedName}).
*/
/**
* What the policy in force said about one tool call.
*
* `allow` runs the call, `deny` refuses it without running it, and `ask` is a pause: the turn
* stops and waits for the user's `user.tool_confirmation`. The value is what the loop
* **evaluated** — not what the tool's own default says — so the log records the decision the
* call was actually made under, and a settings change later does not rewrite it.
*
* `ask` is the pausing half of epic #303 (#309) and is not honoured yet: nothing produces it
* today, and a policy resolver that answers it is treated as a refusal until #309 lands (see
* `packages/brain/AGENTS.md`).
*/
const ToolPermissionSchema = z.enum([
	"allow",
	"ask",
	"deny"
]);
/**
* Where a tool comes from.
*
* `builtin` is a tool this build runs itself (epic #305); `mcp` is a tool of a remote MCP
* server the user added (epic #303, X10; #312). The two are the epic's decision X6: an MCP
* tool is the server's, not this build's, and the only thing a user chooses about one is the
* permission a call to it is evaluated under.
*
* `provider` is the third (epic #303, X14; issue #335): a tool the **model provider** runs
* inside the request it was attached to, so nothing of ours executes it — `web_search` today.
* It is recorded on a call and on the request's offer for the same reason the other two are:
* a reader has to be able to tell which of the build's own tools ran, which a remote server's
* did, and which a provider answered by itself.
*/
const ToolSourceSchema = z.enum([
	"builtin",
	"mcp",
	"provider"
]);
/**
* The permission a call to an MCP tool is evaluated under when the user has chosen nothing.
*
* `ask` rather than a built-in's `allow`: a remote server is somebody else's code doing
* something this build cannot see, so the epic's default (X6) is that the user is asked the
* first time — and the answer can be remembered per tool (#309). It is the `permission` a
* remote tool's definition carries, which is what makes the default live in one place.
*/
const DEFAULT_MCP_TOOL_PERMISSION = "ask";
/**
* One tool a model request offered: its name, where it comes from, and which MCP server it is
* the tool of.
*
* Recorded on `span.model_request_start` rather than left to be inferred from the registry,
* because the registry is the deployment's and moves: a request's span has to say what *it*
* offered, the way it says which model and which mode it ran.
*/
const ToolReferenceSchema = z.object({
	/** The name the model calls it by, e.g. `web_fetch` or `notes__search`. */
	name: z.string().min(1),
	/** Where the tool comes from. */
	source: ToolSourceSchema,
	/**
	* // extension: the MCP server the tool belongs to, by **name** — the same name the call
	* event carries as `mcp_server_name` (epic #303, X10; #312).
	*
	* Absent for a built-in tool, whose `name` already says everything. Present so a reader of
	* the span can tell two servers' tools apart without parsing the offered name, which is this
	* protocol's own spelling of a pair and not a fact either side owns.
	*/
	server: z.string().min(1).optional()
});
/**
* The characters a model-facing MCP tool name may use.
*
* The rule every provider's tool-name check has in common: a name of letters, digits,
* underscores and dashes. It is stated here because the offered name has to satisfy it
* *before* it reaches a provider, and the sanitizer below is where that happens.
*/
const MCP_TOOL_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
/**
* The most characters a model-facing MCP tool name may have.
*
* 64 is the tightest of the providers' limits (Anthropic and OpenAI both cap at 64), so a name
* this long is one every provider accepts. A server's name is at most
* `MCP_SERVER_NAME_MAX_LENGTH` (32) and the separator two more, leaving at least 30 characters
* of the tool's own name.
*/
const MCP_TOOL_NAME_MAX_LENGTH = 64;
/**
* The separator between an MCP server's name and a tool's own name in the offered name.
*
* A double underscore because neither half may contain one: a server name is lowercase
* letters, digits and dashes (`MCP_SERVER_NAME_PATTERN`), and a single underscore is common in
* a tool's own name. It also means an MCP tool can never collide with a built-in, whose names
* (`web_fetch`, `todo_write`, `ask_user`) carry single underscores only.
*/
const MCP_TOOL_NAME_SEPARATOR = "__";
/**
* The name a model is offered an MCP tool under: the server's name, the separator, and the
* tool's own name, sanitized to what every provider's tool-name rule accepts.
*
* It is a **pure** function of the pair, and deliberately so: a reader that has only the log —
* `agent.mcp_tool_use` records the server's name and the tool's own name — recomputes exactly
* the name the request offered, which is what lets a tool result be capped by its tool's
* declaration, a `remember: session` approval be matched to the tool it remembered, and a
* model request be rebuilt from the log, all without a second lookup. Characters outside
* {@link MCP_TOOL_NAME_PATTERN} are replaced by `_`, and the result is truncated to
* {@link MCP_TOOL_NAME_MAX_LENGTH}.
*
* Two pairs that sanitize to the same name are a **collision**, and the offer drops the later
* one rather than renaming it (see `@openharness/brain`): a rename would make the name depend
* on what else was offered, and the name has to be recomputable from the log alone. A dropped
* tool is not offered, so no call to it can exist and nothing is lost but the second spelling.
*/
function mcpToolOfferedName(serverName, toolName) {
	const sanitized = `${serverName}__${toolName}`.replace(/[^a-zA-Z0-9_-]/g, "_");
	return sanitized.length > 64 ? sanitized.slice(0, 64) : sanitized;
}
/** Whether `value` is a model-facing MCP tool name — the rule {@link MCP_TOOL_NAME_PATTERN} states. */
function isMcpToolOfferedName(value) {
	return value.length > 0 && value.length <= 64 && MCP_TOOL_NAME_PATTERN.test(value);
}
/**
* A JSON value: what a tool call's input may hold, and nothing else.
*
* The log is read back by other processes and by clients, and every reader of it goes through
* JSON — so a value that only survives in this process (a function, a `Date`, a `BigInt`, a
* cycle) would be stored as something no reader can parse back. Being this narrow is what
* makes `agent.tool_use.input` round-trip, the same reason `ModelUsageSchema` demands integers
* (see the note in `packages/protocol/AGENTS.md` about issue #39).
*/
const JsonValueSchema = z.lazy(() => z.union([
	z.string(),
	z.number(),
	z.boolean(),
	z.null(),
	z.array(JsonValueSchema),
	z.record(z.string(), JsonValueSchema)
]));
/**
* The input of a tool call: a JSON object, as a model is asked for one.
*
* An object rather than any JSON value because a tool's input is described by an object
* schema — a call whose arguments are a bare string or an array is not a call any registered
* tool can be run with, and the loop answers it with an `is_error` result rather than storing
* a shape nothing can read.
*/
const ToolInputSchema = z.record(z.string(), JsonValueSchema);
//#endregion
//#region src/events/common.ts
/**
* The event vocabulary, and the fields every stored event carries.
*
* Event type strings follow Anthropic's `{domain}.{action}` convention — `user.message`,
* `span.model_request_end` — with one exception, the preview events `event_start` and
* `event_delta`, whose names are Anthropic's verbatim.
*
* See `AGENTS.md` for how to add a new event type.
*/
/** Every event type string in the protocol. */
const EVENT_TYPES = {
	/** A user message: the user talking to the agent. */
	userMessage: "user.message",
	/** Stop the agent mid-execution. */
	userInterrupt: "user.interrupt",
	/** The agent's reply, as text blocks. */
	agentMessage: "agent.message",
	/**
	* The agent asked for a tool.
	*
	* The event's own `id` is the call's id — the same identity trick `agent.message` uses for
	* the chunks it replaces — and the `agent.tool_result` that answers it names that id in its
	* `tool_use_id`. Written by the brain, never by a client: a tool is something the model
	* asked for, and only the brain talks to the model. See {@link AgentToolUseEventSchema}.
	*/
	agentToolUse: "agent.tool_use",
	/**
	* What a tool call produced.
	*
	* Written by the brain, whatever came of the call — a result, a refusal, a timeout, a
	* crash that lost the execution. A client never writes one (epic #303, X1): the loop owns
	* the log, and a tool result is the loop's report of what it ran. See
	* {@link AgentToolResultEventSchema}.
	*/
	agentToolResult: "agent.tool_result",
	/**
	* The agent asked a remote MCP server's tool (epic #303, X1/X10; #312).
	*
	* Anthropic's event, with Anthropic's fields: `mcp_server_name` names the server the tool
	* belongs to and `name` is the tool's own name **on that server** — not the model-facing name
	* this build offers it under, which is a spelling the protocol owns and the log does not have
	* to carry (see `mcpToolOfferedName`). The event's own `id` is the call's id, exactly as for
	* `agent.tool_use`, and `agent.mcp_tool_result.mcp_tool_use_id` names it; a
	* `user.tool_confirmation` answers it the same way (its `tool_use_id` is that id). See
	* {@link AgentMcpToolUseEventSchema}.
	*/
	agentMcpToolUse: "agent.mcp_tool_use",
	/**
	* What a remote MCP tool call produced.
	*
	* Written by the brain, like `agent.tool_result` and for the same reason: the loop owns the
	* log, and a client never writes a tool result. The content is text blocks like any other
	* result — an MCP answer's text and structured content become text, and anything else it
	* carried (an image, a resource) is replaced by a marker — so every reader of a tool result
	* reads one shape. See {@link AgentMcpToolResultEventSchema}.
	*/
	agentMcpToolResult: "agent.mcp_tool_result",
	/**
	* // extension: the parts of a step only the provider that produced them can read back
	* (epic #303, X13; issue #334).
	*
	* A thinking model's next request has to carry back what this build cannot read: an
	* Anthropic `thinking` block with its `signature`, an OpenAI or xAI Responses reasoning item
	* with its id, a DeepSeek step's `reasoning_content`. None of it is the reply — the reply is
	* the `agent.message` beside this event — and a reader that does not know this event skips it
	* like any other event it does not know. See {@link AgentOpaqueContentEventSchema}.
	*/
	agentOpaqueContent: "agent.opaque_content",
	/**
	* // extension: the user answered a call that was waiting on them (epic #303, X6; #309).
	*
	* One event answers every pause: an approval the settings demanded (`result`), or the
	* answers to an `ask_user` call (`answers`). A client sends it; the server writes it, once
	* it has checked that the call it names is really waiting, and the brain turns it into the
	* `agent.tool_result` the call is owed — a client never writes a tool result. It is stored
	* processed, not queued: the event itself is the record, so an approval survives compaction
	* and replay and a rewind past it takes it back. See
	* {@link UserToolConfirmationEventSchema}.
	*/
	userToolConfirmation: "user.tool_confirmation",
	/** The agent started working. */
	sessionStatusRunning: "session.status_running",
	/** The agent finished its turn and is waiting for input. */
	sessionStatusIdle: "session.status_idle",
	/** A transient error; the session is retrying automatically. */
	sessionStatusRescheduled: "session.status_rescheduled",
	/** Something went wrong during the turn. */
	sessionError: "session.error",
	/** A model request started. */
	modelRequestStart: "span.model_request_start",
	/** A model request finished, with its token usage. */
	modelRequestEnd: "span.model_request_end",
	/**
	* // extension: the session's running totals, after a model request (epic #245, A2; #247).
	*
	* Anthropic has the event; openharness writes it on a different cadence and with the cost
	* left out — see {@link SessionUsageEventSchema} for what it carries and why.
	*/
	sessionUsage: "session.usage",
	/**
	* // extension: the older history was summarized for the model (epic #277, K1; #278).
	*
	* The brain writes it when a chat's context fills: `summary` is the text, `covers.to_seq` the
	* last event it replaces for the model, and the request a client makes of it is the context
	* strategy's — nothing is deleted or superseded. See {@link ContextSummaryEventSchema} for
	* what it carries and why.
	*/
	sessionContextSummary: "session.context_summary",
	/**
	* // extension: a summary is being written, one pass at a time (epic #277, C2; #279).
	*
	* The brain writes it before each pass of the compaction engine, in the same log the summary
	* itself lands in, because everything a client is told goes in the log (D9): a client that
	* reconnects mid-compaction — or loads a session it happened in — sees the same progress the
	* live stream carried. It carries the pass being taken and how many the plan holds; a client
	* shows "summarizing (2/3)" and reads nothing else out of it. Deleting old progress events is
	* follow-up work (#285), not this event's job. See {@link ContextSummaryProgressEventSchema}.
	*/
	sessionContextSummaryProgress: "session.context_summary_progress",
	/**
	* // extension: the user asked for a manual compaction, `/compact [instructions]` (epic #277,
	* K8; #283).
	*
	* A client-requested event like `session.rewind` (#238): the server writes it on the caller's
	* behalf when `POST /v1/sessions/{id}/compact` lands, and the brain answers it — at the next
	* request boundary when a turn is running, or in a turn of its own when the session is idle —
	* with a {@link EVENT_TYPES.sessionCompaction}. It carries the user's optional guidance, which
	* the compaction engine folds into the summary prompt as the user's own instruction. A request
	* already waiting for an answer is not appended again: the route is idempotent while one is
	* pending. See {@link SessionCompactEventSchema}.
	*/
	sessionCompact: "session.compact",
	/**
	* // extension: the answer to a manual compaction request (epic #277, K8; #283).
	*
	* The brain writes it once it has handled the newest pending `session.compact`, whatever came
	* of it: `summarized` (the summary itself is a `session.context_summary` with reason
	* `manual`), `nothing_to_summarize` (there was no older history to fold, or the chat is short),
	* or `failed` (the summarizer failed; the chat carries on). This is the clear, stored outcome
	* a client shows — the request is never a silent no-op — and it is what makes a request no
	* longer pending. See {@link SessionCompactionEventSchema}.
	*/
	sessionCompaction: "session.compaction",
	/**
	* // extension: a skill's instructions were loaded for a slash invocation (epic #349, K7).
	*
	* The brain writes it when it first handles a `user.message` carrying a `skill`: `content` is
	* the skill's body with the arguments substituted, exactly as the model was given it, so the
	* log says what a request ran on rather than what the skill happens to say now. It is written
	* **once** per invoked message — an edit or a refresh never rewrites a chat — and every later
	* reader (the context builder, the summarizer, compaction) reads this event rather than the
	* current snapshot. See {@link SessionSkillLoadedEventSchema}.
	*/
	sessionSkillLoaded: "session.skill_loaded",
	/** A previewed event started generating. A stored chunk since D9; stream-only before it. */
	eventStart: "event_start",
	/** Incremental content for a previewed event. A stored chunk since D9; stream-only before it. */
	eventDelta: "event_delta",
	/**
	* // extension: the session restarts from an earlier `user.message` (#238).
	*
	* The log stays append-only: nothing already stored is changed. A rewind is a superseding
	* event like the one that finishes a reply (see {@link SupersedesSchema}) — it carries a
	* range, replay skips it, and compaction deletes it after the retention window — but the
	* range covers the whole tail of the log from the edited message on, not a reply's chunks.
	* The edited text is then an ordinary `user.message` appended right behind the rewind, so a
	* reader sees the conversation restart from the edit and the model never sees the original.
	*
	* Anthropic has no equivalent: editing a sent message is an openharness extension.
	*/
	sessionRewind: "session.rewind",
	/**
	* // extension: a session started a background task and did not wait for it (epic #340, S4;
	* issue #343).
	*
	* Background tasks are a general mechanism — a subagent is the first kind, a sandboxed `bash`
	* will be the second — and this is the event that puts one in the **parent's** log: what kind
	* it is, the session it runs in when it has one, and what it is for. Starting one returns at
	* once and the conversation goes on, so what the task does reaches the parent model as a
	* {@link EVENT_TYPES.sessionTaskNotification} rather than as a tool result. See
	* {@link SessionTaskStartedEventSchema}.
	*/
	sessionTaskStarted: "session.task_started",
	/**
	* // extension: what a background task is doing (epic #340, S4; issue #343).
	*
	* Written to the **parent's** log as the task changes state — `running`, `idle`, `failed`,
	* `stopped` — so a client draws a task's card from the parent's log alone. Live activity is
	* read from the child's own stream; a task never reports progress. See
	* {@link SessionTaskStatusEventSchema}.
	*/
	sessionTaskStatus: "session.task_status",
	/**
	* // extension: something reached the parent model from a background task (epic #340, S4;
	* issue #343).
	*
	* The one **queued** thing a task produces: stored with `processed_at: null` and claimed by
	* the parent's brain the way a `user.message` is — by the `consumes` list of the event that
	* answers it — so it is delivered exactly once and never between a tool call and its result.
	* Its `source_key` makes the append idempotent: a second notification for the same finished
	* turn changes nothing. To the model it is a framed user-role message whose text is **data,
	* not instructions** — a task's output can carry what that session read from the web. See
	* {@link SessionTaskNotificationEventSchema} and [`docs/threat-model.md`](../../../docs/threat-model.md).
	*/
	sessionTaskNotification: "session.task_notification",
	/**
	* // extension: the session this stream was following no longer exists (#111).
	*
	* Stream-only: it is **not** in {@link STORED_EVENT_TYPES} and never lands in a log — the
	* session it names, and the log with it, is gone. A server sends it as the last event on
	* every open stream for the session and then closes the stream, so a subscriber learns that
	* the session was deleted rather than watching a reconnect loop for a session that can
	* never answer. Anthropic has no equivalent event: deleting a session is an openharness
	* extension (epic #116, U5).
	*/
	sessionDeleted: "session.deleted"
};
/**
* Whether an event type is **queued**: stored unprocessed, and claimed by a turn.
*
* Queued is "the log holds something the brain has not been told yet", and every side of the
* log asks it: a store decides by it whether an event's `processed_at` is left `null` (its
* timestamp is then derived from the claim that takes it) and whether a `consumes` list may
* name it; the brain asks it to tell the log as a request sees it (a queued event not yet
* claimed is not part of the prompt) and to decide what a turn still owes an answer to.
*
* Two families are queued. The user's own events — `user.message` and `user.interrupt` — and,
* since epic #340 S4 (#343), `session.task_notification`: a background task's news is addressed
* to the parent **model**, exactly as a message is, so it is claimed the same way and delivered
* exactly once. Everything else a session stores is written by a server or a brain that already
* knows what it did.
*/
function isQueuedEventType(type) {
	return type === EVENT_TYPES.userMessage || type === EVENT_TYPES.userInterrupt || type === EVENT_TYPES.sessionTaskNotification;
}
/**
* Event types that are persisted in the session log.
*
* Every event type except {@link EVENT_TYPES.sessionDeleted}: that one is stream-only — it
* announces a session's deletion, so there is no log left to store it in (#111).
*/
const STORED_EVENT_TYPES = [
	EVENT_TYPES.userMessage,
	EVENT_TYPES.userInterrupt,
	EVENT_TYPES.userToolConfirmation,
	EVENT_TYPES.agentMessage,
	EVENT_TYPES.agentToolUse,
	EVENT_TYPES.agentToolResult,
	EVENT_TYPES.agentMcpToolUse,
	EVENT_TYPES.agentMcpToolResult,
	EVENT_TYPES.agentOpaqueContent,
	EVENT_TYPES.sessionStatusRunning,
	EVENT_TYPES.sessionStatusIdle,
	EVENT_TYPES.sessionStatusRescheduled,
	EVENT_TYPES.sessionError,
	EVENT_TYPES.modelRequestStart,
	EVENT_TYPES.modelRequestEnd,
	EVENT_TYPES.sessionUsage,
	EVENT_TYPES.sessionContextSummary,
	EVENT_TYPES.sessionContextSummaryProgress,
	EVENT_TYPES.sessionCompact,
	EVENT_TYPES.sessionCompaction,
	EVENT_TYPES.sessionSkillLoaded,
	EVENT_TYPES.sessionTaskStarted,
	EVENT_TYPES.sessionTaskStatus,
	EVENT_TYPES.sessionTaskNotification,
	EVENT_TYPES.eventStart,
	EVENT_TYPES.eventDelta,
	EVENT_TYPES.sessionRewind
];
/**
* // extension: the per-session running number of a stored event.
*
* Anthropic orders the event log by `processed_at`; timestamps collide at millisecond
* resolution, and they are not monotonic across a crash, so openharness also stamps each
* stored event with `seq`. It starts at `1` for the first event of a session and increases by
* exactly one per event, making it the ordering key, the pagination cursor and the SSE
* `last-event-id` resume position all at once. Every event in the log has one; the stream-only
* previews that had none were removed in phase P4.
*/
const EventSeqSchema = z.number().int().positive();
/**
* The `after_seq` query parameter, an openharness extension.
*
* Returns only events with a `seq` strictly greater than this, so `after_seq=0` means "from
* the beginning of the session" and `after_seq=<last seen seq>` is an exact resume.
*/
const AfterSeqSchema = z.coerce.number().int().nonnegative();
/**
* `processed_at` on a user event.
*
* User events are appended to the log immediately but processed later, when the brain folds
* them into a turn, so their `processed_at` is `null` while they are queued. Anthropic
* documents the field as `optional string or null`; openharness always writes the key.
*/
const QueuedProcessedAtSchema = TimestampSchema.nullable();
/**
* `processed_at` on an event the server produced itself (agent, session and span events).
* These are written when they happen, so their timestamp is never null.
*/
const ProcessedAtSchema = TimestampSchema;
/**
* // extension: the range of stored events a later event replaces (D9, issue #46; #238).
*
* Two events supersede a range, and the event that carries it says which kind it is:
*
* - **A reply.** A streamed reply is stored twice over: once as the chunks it arrived in — the
*   stored `event_start` and `event_delta` events — and once as the finished `agent.message`.
*   The finished event carries `supersedes` over the chunks it replaces: `from_seq` is its own
*   `event_start` and `to_seq` its last `event_delta`, inclusive on both ends. An interrupt
*   stores the partial `agent.message` the same way, and a request that ends without one — a
*   crash the recovering brain closes with `span.model_request_end { brain_lost }`, or a
*   message that streamed no text at all — carries the range on that span end instead. Only
*   chunks are ever covered: a range from one of these events replaces the reply's previews
*   and nothing else.
* - **A rewind** ({@link EVENT_TYPES.sessionRewind}). `from_seq` is the `user.message` the
*   session restarts from and `to_seq` the last event before the rewind, so the range covers
*   the whole tail of the log: the message, its reply, every span and status event between
*   them. Unlike a reply's range it covers events of any type, and it reaches the log's end —
*   the event that carries it is the next one written.
*
* Readers use it for one thing: replay (and the client's transcript) **skips superseded
* events**, so a client that resumes by `seq` sees a reply once, whole, however far into the
* stream it was when it disconnected, and a conversation restarted from an edit without the
* messages the edit replaced. A later background job deletes the range physically, after the
* retention window, without changing what any reader sees.
*
* `from_seq <= to_seq`, and both are positive `seq` values of the same session — this is a
* position in the log, not an `EventId`, which is why the fields are `seq`-shaped rather than
* ids.
*/
const SupersedesSchema = z.object({
	/** The `seq` of the first replaced event: the reply's `event_start`. */
	from_seq: EventSeqSchema,
	/** The `seq` of the last replaced event: the reply's final `event_delta`. */
	to_seq: EventSeqSchema
}).refine((range) => range.from_seq <= range.to_seq, { error: "from_seq must not be greater than to_seq" });
//#endregion
//#region src/events/agent.ts
/**
* Events the agent produces.
*
* Written by the brain, never by a client. The `id` of an `agent.message` is the same `sevt_`
* id its stream preview carried in `event_start.event.id` / `event_delta.event_id`, which is
* how a client reconciles an accumulated preview with the authoritative message.
*/
/**
* The agent's reply.
*
* `content` holds the text blocks the model produced for this model request. Anthropic also
* allows a `redacted` block here, a placeholder for content withheld by model policy;
* openharness does not produce one (see `AGENTS.md`).
*/
const AgentMessageEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.agentMessage),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	content: ContentBlocksSchema,
	/**
	* // extension: the chunk range this message replaces (D9, issue #46).
	*
	* Since D9 the streamed chunks are stored events, and the finished message supersedes them:
	* the range runs from its own `event_start` to its last `event_delta`. Replay skips the
	* chunks in the range; a reader that saw them live does not need to — it reconciles them by
	* id, as it always has. See {@link SupersedesSchema}.
	*
	* Optional only so a log stored before D9 (issue #46) keeps validating: a reply whose chunks
	* were never stored has no range to carry. Every reply the brain stores from phase P3 on has
	* one; a reader without one falls back to the position of the preview it replaces.
	*/
	supersedes: SupersedesSchema.optional()
});
/**
* The agent asked for a tool (epic #303, X1).
*
* The model's call, stored as an event of its own so the log says what was asked for and when
* — not streamed (tool input is stored when the call is complete, X1), and not a content block
* of `agent.message`, so a step that produced four calls and no text still reads as four
* calls. The **event's own id is the call's id**: `agent.tool_result.tool_use_id` names it,
* and a reader pairing the two has one identity to key on, the way an `agent.message` and its
* chunks share the `sevt_` id the preview announced.
*
* `input` is the arguments the model produced, validated by nobody here: whether they match the
* tool's own schema is the registry's business (`@openharness/hands`), and a call that does not
* match is answered with an `is_error` result rather than refused at the log's door — the log
* records what the model asked for, including a call that cannot be run.
*
* `evaluated_permission` is what the policy in force said about **this** call: recorded per
* call, so a later settings change does not rewrite what happened.
*/
const AgentToolUseEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.agentToolUse),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The tool's name, as it was offered to the model. */
	name: z.string().min(1),
	/** The arguments the model produced: a JSON object; see `ToolInputSchema`. */
	input: ToolInputSchema,
	/** What the policy in force said about this call: `allow`, `ask` or `deny`. */
	evaluated_permission: ToolPermissionSchema,
	/**
	* // extension: where this call's tool comes from (epic #303, X14; issue #335).
	*
	* Absent is `builtin` — every call this event carried before #335 — so a log stored then
	* replays exactly as it did. `provider` is a call the model's own provider ran inside the
	* request, which this build neither offered through `@openharness/hands` nor could have run:
	* it is stored for the reader, and the request is rebuilt from `agent.opaque_content`
	* instead (§ `@openharness/brain`'s `./context`, `./opaque`).
	*/
	source: ToolSourceSchema.optional()
});
/**
* What a tool call produced (epic #303, X1).
*
* Always written, and always by the brain: a call the model made is answered exactly once, by
* the loop that ran it — a result, a refusal under a `deny` policy, a timeout, an interrupt, or
* the `execution lost` a turn that crashed before running it leaves for its successor (X3).
* A client never writes one, so "the model asked and nothing answered" is always a brain that
* died, never a shape a reader has to guess at.
*
* `content` is text blocks, the same shape a message's content has; `is_error: true` is what
* says the model should read it as a failed call rather than an answer.
*/
const AgentToolResultEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.agentToolResult),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The `agent.tool_use` this answers — its event id, which is the call's id. */
	tool_use_id: EventIdSchema,
	/** What the call produced, as text blocks. */
	content: ContentBlocksSchema,
	/** Whether the call failed — a refusal, a timeout, an interrupt or the tool's own error. */
	is_error: z.boolean(),
	/**
	* // extension: the web sources a provider-executed search answered with (epic #303, X14;
	* issue #335).
	*
	* Present only on the result of a `source: 'provider'` call, and only for a search: the
	* provider's citations normalized to one list (`SourceSchema`), so a client renders where a
	* reply got its facts without knowing which provider produced them. Absent everywhere else —
	* every result stored before #335, every result of a tool this build ran, every remote tool's
	* — so a reader that does not know the field sees the shape it always did.
	*
	* It is a **reading**, not the request's material: what goes back to the provider is the
	* provider's own opaque content, never this.
	*/
	sources: SourcesSchema.optional(),
	/**
	* // extension: the queued events this result answers (epic #340, S5; issue #343).
	*
	* A claim belongs on the event that ends the work it consumed (P4), and one case has no other
	* event to carry it: a **task wait** whose result *is* the notifications that resolved it. A
	* `session.task_notification` is a queued event, so "the model has been told" must be a claim
	* — and the append that resolves the wait writes the notifications into the result's content
	* and lists their ids here, which is what stops the parent's next request from being handed
	* the same notification a second time as steering.
	*
	* Absent for every other result this build writes, which is why the field is optional and why
	* the remote pair ({@link AgentMcpToolResultEventSchema}) has none: the tool that waits is a
	* built-in of this deployment, so only the built-in pair ever carries the list.
	*/
	consumes: z.array(EventIdSchema).optional()
});
/**
* The agent asked a remote MCP server's tool (epic #303, X1/X10; #312).
*
* The same shape as {@link AgentToolUseEventSchema} — the event's id **is** the call's id, and
* `evaluated_permission` is what the policy in force said about *this* call — plus the one
* field the built-in pair has no use for: `mcp_server_name`, the server the tool belongs to.
*
* `name` is the tool's own name **as the MCP server named it**, not the name the model was
* offered it under. The two differ: a chat offers a remote tool as `<server>__<tool>` so a
* model can tell two servers' `search` apart, and that spelling is openharness's invention —
* a reader recomputes it from this pair with `mcpToolOfferedName` whenever it needs the name a
* registry or a cap is keyed by. Recording the server's own names is what keeps the log true
* to what happened even if that spelling ever changes.
*
* A tool-level failure is not special here: as with a built-in, the call is stored and then
* answered with an `is_error` result, because the log records what the model asked for.
*/
const AgentMcpToolUseEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.agentMcpToolUse),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The MCP server's name — the user-unique name it was configured under. */
	mcp_server_name: z.string().min(1),
	/** The tool's own name on that server, e.g. `search`. */
	name: z.string().min(1),
	/** The arguments the model produced: a JSON object; see `ToolInputSchema`. */
	input: ToolInputSchema,
	/** What the policy in force said about this call: `allow`, `ask` or `deny`. */
	evaluated_permission: ToolPermissionSchema
});
/**
* What a remote MCP tool call produced (epic #303, X1/X10; #312).
*
* Always written, and always by the brain — the same rule as {@link AgentToolResultEventSchema},
* with the id field named for the MCP pair (`mcp_tool_use_id`).
*
* `content` is text blocks, whatever the server sent: the answer's text and its structured
* content become text, and content this protocol cannot carry as text — an image, an embedded
* resource — becomes a marker naming it, so a model is told something was there rather than
* shown a shape no reader accepts. `is_error: true` is the tool's own `isError`, or the loop's
* report of a call that failed to run.
*/
const AgentMcpToolResultEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.agentMcpToolResult),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The `agent.mcp_tool_use` this answers — its event id, which is the call's id. */
	mcp_tool_use_id: EventIdSchema,
	/** What the call produced, as text blocks. */
	content: ContentBlocksSchema,
	/** Whether the call failed — the tool's own error, a timeout, an interrupt or a lost turn. */
	is_error: z.boolean()
});
/**
* // extension: one part of a step, kept exactly as the provider that produced it sent it
* (epic #303, X13; issue #334).
*
* The AI SDK's own assistant part, plus the provider's payload for it: a reasoning part's text
* and whatever metadata its provider attached (`signature`, `redactedData`, `itemId`,
* `reasoningEncryptedContent`). The payload is the part's *whole* opaque half — nothing is
* summarized, re-encoded or cut — because a signature or an encrypted payload the provider
* cannot verify is not one it accepts.
*/
const OpaquePartSchema = z.object({
	/**
	* The AI SDK assistant-part type: `reasoning` (#334), or `tool-call` / `tool-result` for a
	* call the provider ran itself inside the request (#335). See
	* `@openharness/brain`'s `./opaque` for which types a family captures and how each goes back
	* out.
	*/
	type: z.string().min(1),
	/**
	* The part's text: a reasoning part's thinking, or `''` when the provider withheld it.
	*
	* Empty for the tool parts #335 captures — what a tool call says is its id, its name and its
	* arguments ({@link OpaquePartSchema.shape.payload}), not prose.
	*/
	text: z.string(),
	/**
	* // extension: how much of the step's own text was streamed **before** this part — in
	* characters — which is where the part goes in the assistant turn it is replayed on
	* (epic #303, X14; issue #335).
	*
	* A provider's assistant content has an order, and one this build must rebuild exactly: a
	* Claude step that wrote "Let me look that up." then called `server_tool_use` then answered
	* from the results is text → call → result → text, and a request that sent the parts first
	* would be asking Anthropic to read a turn it never wrote. A part's place is therefore its
	* own offset into that step's text, and the strategy splits the step's `agent.message` text
	* at those offsets and lays the parts between the pieces.
	*
	* Absent reads as `0`: every part of a step streamed before any of its text, which is the
	* order this event was written with before the offsets existed (#334 — parts ahead of the
	* text), so a log stored then replays exactly as it did.
	*/
	text_before: z.number().int().nonnegative().optional(),
	/**
	* // extension: the provider's own metadata for the part, exactly as it sent it — the AI
	* SDK's `providerOptions` key for that provider (`signature` for Anthropic, `itemId` and
	* `reasoningEncryptedContent` for OpenAI and xAI, `thoughtSignature` for Gemini, the
	* `encryptedIndex` of a citation or the `encryptedContent` of a search result).
	*
	* Absent for a family whose reasoning is replayed as plain text and carries no payload
	* (DeepSeek thinking mode through an OpenAI-compatible endpoint). See
	* `@openharness/brain`'s `./opaque` for which key each family files it under.
	*/
	provider_options: JsonValueSchema.optional(),
	/**
	* // extension: the part's own data, for a part that is more than text (#335).
	*
	* A provider-executed tool call and its result are the AI SDK's own part shapes — a call's
	* `toolCallId`, `toolName` and arguments, a result's `toolCallId`, `toolName` and output —
	* and there is no place in `text` or in the provider's metadata for any of it: the metadata
	* is the provider's key namespace, and cramming a call's arguments into it would put them
	* under a name the provider's client reads for something else.
	*
	* Stored exactly as the AI SDK delivered it, because that is the only form the provider will
	* accept back (a search result's `encryptedContent` is opaque to this build). Absent for a
	* `reasoning` part, which is its text and its metadata and nothing else.
	*/
	payload: JsonValueSchema.optional()
});
/**
* // extension: the parts of one step only the provider that produced them can read back
* (epic #303, X13; issue #334).
*
* One event per model request — **one step's worth of parts**, in the order the provider
* produced them — written by the brain and never shown as the reply. It is stored whether or
* not the step streamed text, because the step this exists for is the one with no text at all:
* a thinking model's call. A step that produced nothing opaque writes no event, which is every
* step of every session stored before #334, so those logs replay unchanged.
*
* `provider` and `model` are what the parts may be replayed to, and they are both recorded
* because both are required: a signature, an encrypted payload and an item id are all bound to
* the conversation *and* the model that produced them, so the brain's context strategy
* re-attaches a step's parts only when the next request goes to the same family **and** the
* same model, and drops them on any switch (the AI SDK's provider clients then write the
* assistant message without them, which is what a provider that cannot read them wants).
*
* A step's assistant turn has an **order**, and this event is where the parts' place in it is
* recorded: each part carries {@link OpaquePartSchema.shape.text_before}, the characters of the
* step's own `agent.message` text that came before it, and the strategy rebuilds the turn by
* splitting that text at those offsets and laying the parts between the pieces. Text → a
* provider's call → its answer → the text that answers from it is a step's real shape, and a
* request that sent the parts ahead of the text would be a turn the provider never wrote.
* Absent reads as `0`, so a log written before the offsets existed (#334's parts-ahead-of-text
* order) replays exactly as it did.
*
* Nothing here is summarized, and a rewind takes it back with the step it belongs to —
* `session.rewind` covers every event in the tail it restarts, this one included. It is never
* sent to another provider: see `@openharness/brain`'s `./opaque`, the one place the rule
* lives.
*/
const AgentOpaqueContentEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.agentOpaqueContent),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/**
	* // extension: the client family the parts must be replayed to.
	*
	* The name the family's AI SDK client reads its per-part options under — `anthropic`,
	* `bedrock`, `openai`, `xai` — or `openai-compatible` for the chat-completions families
	* whose reasoning is plain text. It is the key an entry's `provider_options` is filed under
	* on the way back out.
	*/
	provider: z.string().min(1),
	/** The `provider/model` that produced the parts — the other half of "where may this go?". */
	model: z.string().min(1),
	/** The parts, in the order the provider produced them. Empty only when all were dropped. */
	parts: z.array(OpaquePartSchema),
	/**
	* // extension: how many of the step's parts were too large to keep, when any were.
	*
	* A part is kept whole or not at all: no family in the table may replay a truncated payload
	* (an Anthropic signature and an OpenAI encrypted payload are opaque to this build, and a
	* DeepSeek step's reasoning is one string whose value is in being whole), so an oversized
	* part is dropped instead of cut and the count says the step had reasoning the log does not
	* carry. Absent when nothing was dropped.
	*/
	dropped: z.number().int().nonnegative().optional()
});
/** Any stored agent event. */
const AgentEventSchema = z.discriminatedUnion("type", [
	AgentMessageEventSchema,
	AgentToolUseEventSchema,
	AgentToolResultEventSchema,
	AgentMcpToolUseEventSchema,
	AgentMcpToolResultEventSchema,
	AgentOpaqueContentEventSchema
]);
//#endregion
//#region src/pagination.ts
/**
* Opaque pagination cursors.
*
* Every list endpoint in the API answers with the Anthropic list envelope
* (`{ data, next_page }`). `next_page` is a cursor the client hands back verbatim as the
* `page` query parameter; it is never parsed by clients.
*
* The cursor is `page_` followed by the URL-safe base64 of a small JSON payload, tagged with
* the kind of resume position it carries:
*
* - `seq` — the events list. The log has a running sequence number, so the position is the
*   `seq` of the last event on the page.
* - `key` — the agent and session lists. Both are ordered by `(created_at, id)`, so the
*   position is that keyset for the last item on the page, and the next page is the items
*   strictly before it. An item offset would not do: sessions are listed newest first, so a
*   session created between two page fetches would shift every later offset, duplicating or
*   skipping items that were already returned.
*
* Clients must not depend on any of this: the payload is an implementation detail and can
* change without notice.
*/
/** Prefix of every page cursor. */
const PAGE_CURSOR_PREFIX = "page_";
/**
* A position in the event log: the `seq` of the last event returned.
*
* The next page starts after it, so `seq` is never the sequence number of an event the client
* has not seen. `0` is the start of the log, mirroring `after_seq` on the events query.
*/
const SeqCursorSchema = z.object({
	kind: z.literal("seq"),
	seq: z.number().int().nonnegative()
});
/**
* A keyset position in a list ordered by `(created_at, id)`: the `created_at` and `id` of the
* last item on the page.
*
* `created_at` alone is not enough — two items can be created in the same millisecond — so
* `id` breaks the tie. Ids are ULIDs, which sort by creation time as well, making
* `(created_at, id)` a total order the store can seek into with a single comparison.
*/
const KeyCursorSchema = z.object({
	kind: z.literal("key"),
	created_at: TimestampSchema,
	id: z.string().min(1)
});
/** The decoded payload of a page cursor: a `seq` position or a keyset `key` position. */
const PageCursorSchema = z.discriminatedUnion("kind", [SeqCursorSchema, KeyCursorSchema]);
/** `next_page` in a list response: an encoded cursor, or `null` when the page was the last. */
const NextPageSchema = z.string().nullable();
/**
* Whether `value` looks like a page cursor, by its prefix.
*
* A cheap guard for branching, not a validity check: {@link tryDecodePageCursor} is what
* decides whether the payload inside is one of ours.
*/
function isPageCursor(value) {
	return typeof value === "string" && value.startsWith("page_");
}
/**
* The `page` query parameter of a list request. Rejects a cursor this package cannot decode.
*
* Either kind decodes here: which one an endpoint expects is for the server to decide, and
* the cursor stays opaque to clients either way.
*/
const PageCursorStringSchema = z.string().refine((value) => tryDecodePageCursor(value) !== null, { error: "must be a `page_` pagination cursor" });
/**
* Encode the `seq` of the last event on a page, for the `next_page` field of an events list
* response.
*
* Refuses a position that would not survive the round trip, so a server cannot hand a client
* a `next_page` that {@link decodePageCursor} — and therefore `ListEventsQuerySchema`, which
* validates `page` — would reject.
*
* @param seq the resume position, see {@link SeqCursor}
* @throws RangeError if `seq` is not a non-negative integer
*/
function encodeSeqCursor(seq) {
	return encodeCursor({
		kind: "seq",
		seq
	});
}
/**
* Encode the keyset position of the last item on a page, for the `next_page` field of an
* agent or session list response.
*
* Refuses a position that would not survive the round trip, the same way
* {@link encodeSeqCursor} does.
*
* @param position the resume position, see {@link KeyCursorPosition}
* @throws RangeError if `created_at` is not an RFC 3339 timestamp or `id` is empty
*/
function encodeKeyCursor(position) {
	return encodeCursor({
		kind: "key",
		created_at: position.created_at,
		id: position.id
	});
}
/**
* Validate a position and write it out: the one place a canonical cursor spelling comes from.
*
* The payload is rebuilt from the parsed fields rather than stringified from the argument, so
* the same position always encodes to the same string — whatever object it came in as, and
* whatever order its keys were in.
*/
function encodeCursor(cursor) {
	const parsed = PageCursorSchema.safeParse(cursor);
	if (!parsed.success) throw new RangeError(`not a valid page cursor: ${JSON.stringify(cursor)}`);
	const payload = parsed.data.kind === "seq" ? JSON.stringify({
		kind: "seq",
		seq: parsed.data.seq
	}) : JSON.stringify({
		kind: "key",
		created_at: parsed.data.created_at,
		id: parsed.data.id
	});
	return PAGE_CURSOR_PREFIX + base64UrlEncode(payload);
}
/**
* Decode a cursor received as the `page` query parameter, or `null` if it is not one this
* package produced.
*
* Only the canonical form round-trips: the string must be exactly what
* {@link encodeSeqCursor} or {@link encodeKeyCursor} emits for the position it carries, so
* two different spellings can never mean the same page.
*/
function tryDecodePageCursor(cursor) {
	if (!isPageCursor(cursor)) return null;
	const payload = base64UrlDecode(cursor.slice(5));
	if (payload === null) return null;
	try {
		const decoded = JSON.parse(payload);
		const parsed = PageCursorSchema.safeParse(decoded);
		if (!parsed.success) return null;
		return encodeCursor(parsed.data) === cursor ? parsed.data : null;
	} catch {
		return null;
	}
}
/**
* Decode a cursor received as the `page` query parameter.
*
* @throws RangeError if `cursor` is not a cursor produced by {@link encodeSeqCursor} or
*   {@link encodeKeyCursor}
*/
function decodePageCursor(cursor) {
	const decoded = tryDecodePageCursor(cursor);
	if (decoded === null) throw new RangeError(`not a page cursor: ${JSON.stringify(cursor)}`);
	return decoded;
}
const textEncoder = new TextEncoder();
/** Strict, so bytes that are not UTF-8 fail the decode instead of becoming U+FFFD. */
const textDecoder = new TextDecoder("utf-8", { fatal: true });
/**
* Base64url without padding, over the UTF-8 bytes of `text`.
*
* Built on `TextEncoder` and `btoa` rather than `Buffer`: this package is imported by the web
* app, where `Buffer` does not exist. Both are Web APIs, so they are in Node 24 as well.
*/
function base64UrlEncode(text) {
	let binary = "";
	for (const byte of textEncoder.encode(text)) binary += String.fromCharCode(byte);
	return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}
/**
* {@link base64UrlEncode} in reverse, or `null` if `value` is not unpadded base64url of
* well-formed UTF-8. Every failure — a stray character, a bad length, an invalid byte — is
* the same answer to the caller.
*/
function base64UrlDecode(value) {
	const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
	try {
		const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
		return textDecoder.decode(bytes);
	} catch {
		return null;
	}
}
//#endregion
//#region src/events/stream.ts
/**
* The chunk events: `event_start` and `event_delta`.
*
* A connection opts in per event type with the `event_deltas[]` query parameter on
* `GET /v1/sessions/{session_id}/events/stream`. Since D9 (issue #46) a streamed reply is
* stored chunk by chunk: these are ordinary stored events — `id`, `seq`, `processed_at` — so a
* reply in flight is part of the log, resumable by `seq` like anything else, and the event
* that finishes the reply supersedes the range it spanned.
*
* There is one form, and it is the stored one. The pre-D9 stream-only preview — the same
* event names with no envelope, published to live connections and never stored — was removed
* in phase P4, when there were no writers left that produced it; the stored schemas below are
* what `StoredEventSchema` carries, and `seq` is still the field that tells a log event apart
* from anything a pre-D9 server might still send (see `isStoredEvent()` in `union.ts`).
*
* The chunk announces the id of the event it previews, and the identifiers always line up:
* `event_start.event.id`, every `event_delta.event_id` and the finished event's `id` are the
* same `sevt_` value.
*
* The wire format is deliberately *not* the Messages API streaming format — the delta type is
* `content_delta`, not `content_block_delta`, and there are no per-block start/stop events.
*/
/**
* The event types whose chunks a connection may ask to receive (`event_deltas[]`).
*
* Anthropic also accepts `agent.thinking`, which it emits as `event_start` only;
* openharness has no `agent.thinking` event, so `agent.message` is the whole v1 set.
*/
const DeltaTypeSchema = z.enum([EVENT_TYPES.agentMessage]);
/**
* Incremental content for a previewed event.
*
* `index` is the index of the content block being extended, so a multi-block message
* accumulates into one buffer per `(event_id, index)` pair. Concatenating a reply's deltas in
* arrival order yields a prefix of `content[index].text` in the stored event — a prefix rather
* than the whole text, because deltas may be shed under load.
*
* Anthropic marks `index` optional and its own accumulator reads a missing index as `0`, so
* this schema defaults it: `{ delta: { type: 'content_delta', content: … } }` parses to
* `index: 0` rather than failing. Parsed deltas always carry the field.
*/
const ContentDeltaSchema = z.object({
	type: z.literal("content_delta"),
	index: z.number().int().nonnegative().default(0),
	content: TextBlockSchema
});
/**
* A reply started streaming: the first chunk of the range the finished event supersedes.
*
* Stored since D9 (issue #46): the event carries the usual envelope and is appended by the
* brain as the reply starts. The message it previews is stored later under `event.id`, and
* either that message — or the `span.model_request_end` that closes a request which stored
* none — carries a `supersedes` range over this event and the deltas that follow it.
*/
const StoredEventStartSchema = z.object({
	type: z.literal(EVENT_TYPES.eventStart),
	id: EventIdSchema,
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	event: z.object({
		type: DeltaTypeSchema,
		id: EventIdSchema
	})
});
/**
* One streamed fragment of a reply, as a log entry: an `event_delta` with the stored envelope.
*
* `event_id` names the reply being written; the deltas of one reply are the consecutive
* `seq`s after its `event_start`, and the finished event's `supersedes` range covers them.
*/
const StoredEventDeltaSchema = z.object({
	type: z.literal(EVENT_TYPES.eventDelta),
	id: EventIdSchema,
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	event_id: EventIdSchema,
	delta: ContentDeltaSchema
});
//#endregion
//#region src/resources/user.ts
/**
* The `user` resource and the endpoints around it:
*
* - `GET /v1/me`
* - `GET /v1/me/preferences`
* - `PUT /v1/me/preferences`
*
* // extension: Anthropic's Managed Agents API has no user resource. Authentication itself is
* also not part of this protocol: sign-in and the session/bearer credentials that prove who
* is calling are Better Auth's own `/api/auth/*` surface (the authentication epic, #65). What
* the API needs from all of that is an identity to hang ownership on, and this is it.
*/
/**
* A user id.
*
* // extension: opaquely minted by Better Auth, not an openharness ULID — there is no
* `user_` prefix and no time ordering to read out of it. It is what an `Agent`'s and a
* `Session`'s `owner_id` holds.
*/
const UserIdSchema = z.string().min(1);
/**
* The signed-in user, as `GET /v1/me` returns them.
*
* `name` and `image` are the profile fields the identity providers supply; both are absent
* when the provider did not give one. `email` is the identity — a user *is* a verified email
* address (epic #65, A3) — so it is always present and always verified by the provider that
* proved it.
*/
const UserSchema = z.object({
	id: UserIdSchema,
	email: z.email(),
	name: z.string().optional(),
	image: z.string().optional(),
	created_at: TimestampSchema
});
/**
* Response of `GET /v1/me`: the signed-in user, unwrapped.
*
* Not a list envelope and not a resource wrapper — the route answers with the `user` object
* itself. There is exactly one of it per caller, so there is nothing to paginate.
*/
const GetMeResponseSchema = UserSchema;
/**
* // extension: the shape of a `default_model`: a model id, `provider/model`
* (epic #116, U1).
*
* The first path segment names the provider; the rest is the model, so a provider's own id
* may itself contain a slash. Whitespace and empty segments are refused. The id does not
* have to be in the caller's catalog — a free-text id for a model the provider list has not
* caught up with is allowed, exactly like an agent's `model.id`.
*/
const DEFAULT_MODEL_PATTERN = /^[^\s/]+\/[^\s/]+(?:\/[^\s/]+)*$/;
/**
* // extension: the theme a user has chosen for the web app (epic #201, X3).
*
* `system` follows the operating system and is the default, so a caller that never saved a
* theme gets the browser's own preference. `light`, `dim` and `dark` are explicit choices —
* `dim` a soft dark palette, darker than light and easier on the eye than near-black.
*
* The preference is a *choice*, not a resolved value: a `system` user is not rewritten to
* `light` or `dark` when their OS changes. There is no TUI equivalent: the terminal's own
* theme is the TUI's theme (epic #201, X4).
*/
const UserThemeSchema = z.enum([
	"system",
	"light",
	"dim",
	"dark"
]);
/** The theme a user who has never chosen one gets, as {@link UserPreferencesSchema} holds it. */
const DEFAULT_USER_THEME = "system";
/**
* // extension: "let the chat's own model write the summary" (epic #277, K3; C3, #282).
*
* A `summary_model` is this sentinel or a `provider/model` id, exactly as a mode's model is
* `my-default-model` or an id (#253, M6). The sentinel is the default: compaction summarizes
* with the chat model unless the reader names another one, and the engine reads it as "no
* summary model" (`null` in `ContextCompactionConfig`).
*/
const SUMMARY_MODEL_SAME_AS_CHAT = "same-as-chat";
/**
* // extension: which model writes a summary (epic #277, K3; C3, #282).
*
* {@link SUMMARY_MODEL_SAME_AS_CHAT}, or a model id of the same `provider/model` shape a
* `default_model` has — free text, validated for shape only, because whether the model exists
* is the catalog's to answer.
*/
const SummaryModelSchema = z.union([z.literal(SUMMARY_MODEL_SAME_AS_CHAT), z.string().regex(DEFAULT_MODEL_PATTERN, { error: "summary_model must be a provider/model id" })]);
/**
* // extension: the share of the chat model's budget a user may trigger compaction at
* (epic #277, K2; C3, #282): 30% to 95%.
*
* Below the floor a chat would summarize constantly and forget what it was just told; at or
* above the ceiling there is no room left for the trigger to fire before a provider refuses
* the request. The engine's own default share (70%) is the server's setting, not a constant
* here, so a user who has not chosen one follows it.
*/
const COMPACTION_THRESHOLD_MIN = .3;
/** The top of the compaction share's range (epic #277, K2; C3, #282). */
const COMPACTION_THRESHOLD_MAX = .95;
/**
* // extension: how many passes a summary model may take before the chat model takes over
* (epic #277, K5; C3, #282): 1 to 10.
*
* One is the floor — a limit of zero would refuse every summary — and ten is where the
* control stops meaning anything: at the engine's half-the-budget slice, ten passes fold far
* more history than any model's window holds, so a larger limit is "unlimited" with a longer
* worst case. The default is the engine's own (3), reported in `defaults` when unset.
*/
const SUMMARY_MAX_PASSES_MIN = 1;
/** The top of the summary pass limit's range (epic #277, K5; C3, #282). */
const SUMMARY_MAX_PASSES_MAX = 10;
/**
* // extension: a user's stored preferences (epic #116, U1; theme: epic #201, X3; compaction:
* epic #277, C3, #282).
*
* `default_model` is the `provider/model` a new chat starts with — the free-text model id
* described above, validated for shape only — or `null` when the user has not set one (and
* none was chosen automatically from their provider keys). `theme` is the web app's colour
* scheme, stored beside it so the choice follows the user across browsers; it is written by
* the settings screen, and the web app also caches it in `localStorage` so the first paint is
* in the right theme.
*
* The three compaction fields are the epic #277 controls. `compaction_threshold` is the share
* of the chat model's budget at which older history is summarized, within
* {@link COMPACTION_THRESHOLD_MIN}..{@link COMPACTION_THRESHOLD_MAX}, or `null` for the
* server's own setting. `summary_model` is the model that writes summaries
* ({@link SummaryModelSchema}), defaulting to {@link SUMMARY_MODEL_SAME_AS_CHAT}. And
* `summary_max_passes` bounds how many passes that model may take before the chat model takes
* over, or `null` for the engine's default. Anthropic has no equivalent: its API is
* account-scoped by the caller's key, with no per-user settings.
*/
const UserPreferencesSchema = z.object({
	default_model: z.string().regex(DEFAULT_MODEL_PATTERN, { error: "default_model must be a provider/model id" }).nullable(),
	theme: UserThemeSchema,
	compaction_threshold: z.number().min(COMPACTION_THRESHOLD_MIN).max(COMPACTION_THRESHOLD_MAX).nullable(),
	summary_model: SummaryModelSchema,
	summary_max_passes: z.number().int().min(1).max(10).nullable()
});
/**
* The values a preference of `null` falls back to, so a client can show the effective setting
* (epic #277, C3; #282).
*
* `compaction_threshold` is the server's own share (`OPENHARNESS_COMPACTION_THRESHOLD`), which
* a deployment may set differently, so it has to travel with the response rather than be a
* constant a client could know. `summary_max_passes` is the engine's default, reported here for
* the same reason: the client shows "3 (default)" without hard-coding the number.
*/
const PreferencesDefaultsSchema = z.object({
	compaction_threshold: z.number(),
	summary_max_passes: z.number().int()
});
/**
* Response of `GET /v1/me/preferences`: the caller's preferences, unwrapped, plus the defaults
* their `null`s mean.
*
* Owner-only, like `GET /v1/me`: the route answers for the authenticated caller and nobody
* else. A caller who has never saved any preferences gets the defaults and no choices — the
* absence of a choice, not a 404. `PUT` answers the same shape, so a client that has just
* written a preference reads the effective defaults back in one round trip.
*/
const GetPreferencesResponseSchema = UserPreferencesSchema.extend({ defaults: PreferencesDefaultsSchema });
/**
* Body of `PUT /v1/me/preferences`: the fields to change, all of them optional. Response:
* {@link GetPreferencesResponseSchema}.
*
* A write **merges**: a field the body carries is stored, a field it leaves out keeps its
* stored value, and `default_model: null` clears the stored default. That is what keeps the
* settings from clearing each other — `{ default_model: 'openai/gpt-5-mini' }` from `oh` leaves
* the theme alone, and `{ theme: 'dim' }` from the settings screen leaves the default model
* alone — without an older client or a half-typed form having to read first. An empty body is a
* no-op that answers what is stored.
*
* `compaction_threshold: null` and `summary_max_passes: null` mean "follow the default" the
* response's `defaults` reports, the way `default_model: null` means "no choice".
*
* The model ids and the numbers are shape-checked (`provider/model`, the threshold's range, the
* pass limit's range); whether a model exists is not — the catalog answers that.
*/
const PutPreferencesRequestSchema = z.object({
	default_model: z.string().regex(DEFAULT_MODEL_PATTERN, { error: "default_model must be a provider/model router id" }).nullable().optional(),
	theme: UserThemeSchema.optional(),
	compaction_threshold: z.number().min(COMPACTION_THRESHOLD_MIN).max(COMPACTION_THRESHOLD_MAX).nullable().optional(),
	summary_model: SummaryModelSchema.optional(),
	summary_max_passes: z.number().int().min(1).max(10).nullable().optional()
});
//#endregion
//#region src/resources/agent.ts
/**
* The `agent` resource and the endpoints that manage it:
*
* - `POST   /v1/agents`
* - `GET    /v1/agents`
* - `GET    /v1/agents/{agent_id}`
* - `POST   /v1/agents/{agent_id}` (update)
*
* An agent is a reusable configuration. Sessions snapshot the fields they need at creation
* time (see `SessionAgentSchema`), so editing an agent never rewrites history.
*/
/** Longest agent name Anthropic accepts. */
const AGENT_NAME_MAX_LENGTH = 256;
/** Longest agent description Anthropic accepts. */
const AGENT_DESCRIPTION_MAX_LENGTH = 2048;
/**
* The model an agent runs, e.g. `{ "id": "anthropic/claude-sonnet-5" }`.
*
* `id` is a **model id**, `provider/model` — not a bare Anthropic model id.
* Anthropic's object also carries `effort`, `inference_geo` and `speed`; this is the subset
* v1 stores, and unknown fields are dropped rather than rejected so that a real Anthropic
* response still parses.
*/
const ModelConfigSchema = z.object({ id: z.string().min(1) });
/** The `agent` resource as Anthropic defines it, limited to the fields v1 stores. */
const AgentSchema = z.object({
	id: AgentIdSchema,
	type: z.literal("agent"),
	/**
	* // extension: the user this agent belongs to (epic #65, A4).
	*
	* Read-only, set by the server from the authenticated caller: no request ever carries it —
	* `POST /v1/agents` takes the owner from the session that made the call — and it never
	* changes afterwards. Together with a `404` for anything another user owns, it is the whole
	* of v1 isolation: nothing is shared.
	*
	* Required since #61: the server sets it on every agent it creates, so an agent without one
	* cannot exist — and a response that omits it fails this schema rather than passing as
	* unowned.
	*/
	owner_id: UserIdSchema,
	name: z.string().min(1).max(256),
	description: z.string().max(AGENT_DESCRIPTION_MAX_LENGTH).nullable(),
	model: ModelConfigSchema,
	system: z.string().nullable(),
	created_at: TimestampSchema,
	/** When the agent was last changed. Set on update; equal to `created_at` at creation. */
	updated_at: TimestampSchema
});
/** Body of `POST /v1/agents`. Response: {@link AgentSchema}. */
const CreateAgentRequestSchema = z.object({
	name: z.string().min(1).max(256),
	description: z.string().max(AGENT_DESCRIPTION_MAX_LENGTH).nullable().optional(),
	model: ModelConfigSchema,
	system: z.string().nullable().optional()
});
/**
* Body of `POST /v1/agents/{agent_id}`. Every field is optional; omitted fields keep their
* stored value, and `null` clears a nullable one. Response: {@link AgentSchema}.
*/
const UpdateAgentRequestSchema = z.object({
	name: z.string().min(1).max(256).optional(),
	description: z.string().max(AGENT_DESCRIPTION_MAX_LENGTH).nullable().optional(),
	model: ModelConfigSchema.optional(),
	system: z.string().nullable().optional()
});
/**
* Query parameters of `GET /v1/agents`.
*
* The list is ordered by `(created_at, id)`; `page` resumes at a keyset position in it.
*/
const ListAgentsQuerySchema = z.object({
	/** Maximum results per page. Defaults to `DEFAULT_PAGE_LIMIT`, capped at `MAX_PAGE_LIMIT`. */
	limit: PageLimitSchema.optional(),
	/** Cursor from a previous response's `next_page`: the `(created_at, id)` of its last agent. */
	page: PageCursorStringSchema.optional()
});
/** Response of `GET /v1/agents`: the Anthropic list envelope. */
const ListAgentsResponseSchema = z.object({
	data: z.array(AgentSchema),
	next_page: NextPageSchema
});
//#endregion
//#region src/reasoning.ts
/**
* How much thinking a model is asked to do before it answers (epic #245, A4a; epic decision M5).
*
* // extension: Anthropic's Managed Agents API carries an `effort` on the model config of a
* session's agent. openharness makes the effort a capability of the **request** instead, because
* every side of its boundary has one: it is what a session asks for, what the brain maps onto
* each provider's own knob, and what the request's span records. That is what lets the same
* three levels mean the same thing across eleven providers whose APIs spell them differently.
*
* Three levels, deliberately: the ones the providers' own knobs have in common. A provider that
* has more (`minimal`, `xhigh`, `max`) is asked for the closest level it takes, and a model that
* takes none runs the provider's default — which is exactly what an absent effort means
* everywhere. Nothing here is a promise that a model reasons: that is the model's business, and
* the span records what actually happened (see {@link ReasoningEffortRunSchema}).
*/
const ReasoningEffortSchema = z.enum([
	"low",
	"medium",
	"high"
]);
/** The three levels, ascending: what a picker offers and the order it offers them in. */
const REASONING_EFFORTS = ReasoningEffortSchema.options;
/**
* What a model request was asked for and what it ran with, recorded on its span.
*
* Two facts rather than one, because they differ: a model that takes no effort runs the
* provider's default, and the log has to be able to say "an effort was asked for, none was
* applied" — which a single field could not distinguish from "nothing was asked for". A reader
* that wants what a request cost wants `applied`; one that wants what the session asked for
* wants `requested`. `applied` equals `requested` in every other case: the levels are the same
* three on both sides, so a provider that takes a level takes it unchanged.
*
* The record is written only when an effort was asked for, so a session that never set one
* keeps the log it always had.
*/
const ReasoningEffortRunSchema = z.object({
	/** What the request was asked to run with. */
	requested: ReasoningEffortSchema,
	/**
	* What the request actually ran with, or `null` when the model takes no effort and the
	* provider's own default was used.
	*/
	applied: ReasoningEffortSchema.nullable()
});
//#endregion
//#region src/resources/tool-settings.ts
/**
* The per-user tool settings, and the endpoints that manage them:
*
* - `GET /v1/me/tools`
* - `PUT /v1/me/tools`
*
* // extension: Anthropic's Managed Agents API has no user resource and no per-user settings —
* its API is account-scoped by the key that calls it, and an agent carries a fixed `tools`
* list. openharness has real users (epic #65) and lets each of them decide which of the
* build's tools their chats may use, and under which permission (epic #303, X4; issue #307).
*
* The settings are **stored beside the log**, not on a session: a tool's on/off and its
* permission are a user's, and a change applies from the next request on — except for the
* parts a **mode** overrides (epic #245, M6): a mode may force built-in tools on or off for
* every chat that follows it, and it may not touch a permission. See
* {@link ModeToolOverrideSchema} in `./mode`.
*/
/**
* The longest tool name this API stores.
*
* Long enough for a built-in tool's own name (`web_search`) and for the model-facing name an
* MCP tool takes (`<server>__<tool>`, at most `MCP_TOOL_NAME_MAX_LENGTH` = 64, #312), and short
* enough that the stored key of a settings map cannot be a paragraph.
*/
const TOOL_NAME_MAX_LENGTH = 128;
/**
* A tool's name as the settings speak of it: the name the model calls it by and the registry
* registers it under.
*
* Free text rather than an enum: the tools a build registers are the host's (`web_fetch` and
* friends arrive with #305, and an MCP server's arrive with #312), and a settings row may name
* a tool this process does not register — a `web_search` whose key is gone, say — which the
* read reports as unavailable rather than hiding.
*/
const ToolNameSchema = z.string().min(1).max(128);
/**
* // extension: one built-in tool's setting (epic #303, X4; #307).
*
* `enabled` is whether the tool is offered to the model at all: a tool that is off is not in
* the request's offer and cannot be called. `policy` is the permission a call to it is
* evaluated under ({@link ToolPermissionSchema}): `allow` runs the call, `deny` refuses it, and
* `ask` is the pause of [#309](https://github.com/amirtuval/openharness/issues/309) — accepted
* and stored now, and until that lands treated as a refusal with a message that says so.
*
* The two are deliberately separate. A tool with `policy: 'deny'` is still offered (the model
* may call it and be told no), while an `enabled: false` one is not; and #309's "always allow"
* is remembered per tool, which is why a permission is a per-tool value rather than one
* switch.
*/
const BuiltinToolSettingSchema = z.object({
	enabled: z.boolean(),
	policy: ToolPermissionSchema
});
/**
* // extension: a user's stored tool settings (epic #303, X4; #307; the MCP half is #312).
*
* Two maps, one per source of tool. `builtin` is keyed by a built-in tool's name and carries
* the two things a user chooses about one (on/off and a permission); `mcp` is keyed by a
* remote tool's model-facing offered name and carries a **permission only**, because whether a
* whole server is in play is a resource of its own (#311 carries `enabled`, and a mode overrides
* it through {@link ModeToolOverrideSchema}'s `mcp_servers`) — a mode never carries a
* permission, and a per-MCP-tool on/off does not exist (X6).
*
* A tool absent from the map follows **its own declared default** — the permission its
* `ToolDefinition` carries, which is `allow` for every built-in tool and
* `DEFAULT_MCP_TOOL_PERMISSION` (`ask`) for every MCP tool — so a user who has never opened the
* settings screen gets the build's defaults rather than a frozen copy of them.
*
* Both maps are records of **choices**, so a user who has saved none reads
* `DEFAULT_USER_TOOL_SETTINGS` and every tool follows its declaration.
*/
const UserToolSettingsSchema = z.object({
	/** The built-in tools a user has chosen for, keyed by tool name. */
	builtin: z.record(ToolNameSchema, BuiltinToolSettingSchema),
	/**
	* // extension: the remote MCP tools a user has chosen a policy for (epic #303, X10; #312),
	* keyed by the model-facing offered name (`<server>__<tool>`, see `mcpToolOfferedName`).
	*
	* A **permission and nothing else**: a remote tool has no on/off of its own — whether a whole
	* server is in play is the server resource's `enabled` and a mode's override (X6) — so the
	* only thing a user chooses per tool is what a call to it means. A tool absent from the map
	* follows `DEFAULT_MCP_TOOL_PERMISSION` (`ask`).
	*/
	mcp: z.record(ToolNameSchema, ToolPermissionSchema)
});
/**
* What a user who has never saved any tool settings reads: no choices at all, so every tool
* follows its own declaration. The absence of a choice, not a 404 and not a stored default.
*/
const DEFAULT_USER_TOOL_SETTINGS = {
	builtin: {},
	mcp: {}
};
/**
* // extension: one tool, as `GET /v1/me/tools` reports it (epic #303, X4; #307).
*
* The **effective** state a chat would get, and the facts a settings screen needs to show it:
*
* - `enabled` is the user's choice, overridden by the mode's when the read named one — what
*   decides whether the tool is offered at all.
* - `policy` is the permission a call is evaluated under: the user's choice, or the tool's own
*   declaration when they made none. A mode never changes it.
* - `default_policy` is that declaration, so a client can show "default" beside a choice that
*   happens to equal it — and `null` for a tool this process does not register, which has no
*   declaration to report.
* - `available` is whether this server registers the tool. `false` is a stored setting for a
*   tool that is not here (a built-in whose key this deployment lacks, or a remote tool of a
*   server that is gone), listed rather than hidden so a user can see why it is not working; a
*   tool that is not here is never offered, whatever `enabled` says.
* - `source` is where the tool comes from: `builtin` for this build's own, `mcp` for a remote
*   server's (#312).
* - `mcp_server` is the MCP server a remote tool belongs to, by name — absent for a built-in
*   tool. It is what a settings screen groups remote tools by, and it is the same server name
*   the log records on a call.
*
* For a remote tool `enabled` is the **effective** on/off of the server it belongs to (the
* server's own `enabled`, with the mode's override applied), not a per-tool choice — there is
* none (X6) — and `default_policy` is `'ask'`.
*/
const ToolSettingEntrySchema = z.object({
	name: ToolNameSchema,
	source: ToolSourceSchema,
	enabled: z.boolean(),
	policy: ToolPermissionSchema,
	default_policy: ToolPermissionSchema.nullable(),
	available: z.boolean(),
	/**
	* // extension: the remote MCP server this tool belongs to, by name (#312). Absent for a
	* built-in tool, and for a stored setting that names no tool this or any server offers.
	*/
	mcp_server: z.string().min(1).optional(),
	/**
	* // extension: why this tool is `available: false`, as a sentence a reader is shown (#335).
	*
	* `available: false` has more than one cause, and a settings screen owes the reader the one
	* that applies: a tool this deployment does not register at all, and `web_search` on a model
	* whose provider has no native search tool, are different sentences. Absent when the tool is
	* available, and absent when nothing can be said about it — a stored setting for a tool
	* nothing offers has no cause to name.
	*/
	reason: z.string().min(1).optional()
});
/**
* Response of `GET /v1/me/tools`: the effective settings, one entry per tool.
*
* No pagination envelope, the shape the mode and credential lists use: the list is bounded by
* the tools a build registers plus the settings a user has stored, so a settings screen can
* show all of it at once. Registered tools come in the registry's order — the order a request
* offers them in — then the tools of the user's in-force remote MCP servers (#312), then any
* stored setting for a tool that is neither, by name.
*/
const ListToolSettingsResponseSchema = z.object({ data: z.array(ToolSettingEntrySchema) });
/**
* Query of `GET /v1/me/tools`.
*
* `mode_id` answers the read as a chat on that mode would see it: the mode's override applied
* over the user's own choices (a mode may force built-in tools on or off, never a permission).
* It is how a composer shows "the tools this chat would get" for a chat that follows a mode
* without the client having to merge the two itself. A `mode_` id the caller does not own is
* the 404 every other mode read gives (A4).
*/
const ListToolSettingsQuerySchema = z.object({
	mode_id: ModeIdSchema.optional(),
	/**
	* // extension: the model the chat runs, so the read can say whether a tool the provider has
	* to supply is available *on it* (#335).
	*
	* A model id (`provider/model`), the same spelling the session carries. Only `web_search`
	* uses it: that tool is the chat model's own provider's, so whether it can be offered at all
	* depends on the model, and a settings screen that did not say so would show a switch that
	* cannot do anything. A read that names no model answers as it always did — nothing is
	* claimed about a model nobody named.
	*/
	model_id: z.string().min(1).optional()
});
/**
* Body of `PUT /v1/me/tools`. Response: {@link ListToolSettingsResponseSchema}.
*
* Named `builtin` and `mcp` like the stored maps, and every field optional: the write **merges**
* — a tool the body names replaces that tool's whole setting, a tool it leaves out keeps what is
* stored — so a settings screen can flip one switch without reading and rewriting the rest,
* exactly as `PUT /v1/me/preferences` merges its fields. There is no way to delete an entry,
* because there is nothing a deletion would say that a value does not: following a tool's own
* declaration is `{ enabled: true, policy: <its default> }` for a built-in and its default
* policy (`ask`) for a remote one, and a user may set that deliberately.
*/
const PutToolSettingsRequestSchema = z.object({
	builtin: z.record(ToolNameSchema, BuiltinToolSettingSchema).optional(),
	/** The policy a remote MCP tool's calls are evaluated under, keyed by offered name (#312). */
	mcp: z.record(ToolNameSchema, ToolPermissionSchema).optional()
});
//#endregion
//#region src/resources/mode.ts
/**
* The `mode` resource and the endpoints that manage it:
*
* - `POST   /v1/me/modes`
* - `GET    /v1/me/modes`
* - `GET    /v1/me/modes/{mode_id}`
* - `POST   /v1/me/modes/{mode_id}` (update)
* - `DELETE /v1/me/modes/{mode_id}`
*
* // extension: Anthropic's Managed Agents API has no per-user presets — a session there runs
* one agent, fixed at creation. A mode is a named preset a user saves once (`smart`, `fast`,
* `deep`) and picks instead of a raw `provider/model`: it bundles a model, a reasoning effort
* and a system-prompt addition behind a stable name. A chat that starts from a mode follows
* it live — the next request uses the mode as it is now — which is what lets a user retune a
* mode without touching every chat that runs it. This is the first step of the modes design
* (epic #245, decision M6). Since #307 a mode may also override which tools a chat has —
* the built-in ones by name, and the user's remote MCP servers by id (#311) — see
* {@link ModeToolOverrideSchema}. Since #342 a mode may also say what it is **good for**, and
* a mode that says so is a **subagent type** a parent model may start (epic #340, S2) — see
* {@link ModeSubagentDescriptionSchema}.
*
* Modes are per user and owner-scoped like every other resource (epic #65, A4): a mode is
* listed only to its owner and answers 404 to anyone else.
*/
/** Longest mode name the API accepts. */
const MODE_NAME_MAX_LENGTH = 64;
/**
* Longest subagent description a mode may carry (epic #340, S2; issue #342).
*
* The description is read by a **parent model** choosing a helper to start, so it is a
* sentence or two rather than a paragraph — the whole list of types goes into one tool
* description, and a page of prose per mode would be the model's context, not its choice.
*/
const MODE_SUBAGENT_DESCRIPTION_MAX_LENGTH = 500;
/** How many modes one user may have. */
const MAX_MODES_PER_USER = 20;
/**
* The value a mode's `model` takes when it should follow the user's default model.
*
* It is deliberately not `provider/model`-shaped (`DEFAULT_MODEL_PATTERN` requires a slash),
* so a real model id can never collide with it. A mode on it resolves to the user's
* `default_model` at request time, so it follows a changed default; with no default set it is
* an unavailable model and a chat on the mode is refused.
*/
const MODE_DEFAULT_MODEL = "my-default-model";
/** The model a mode runs: a `provider/model` id, or {@link MODE_DEFAULT_MODEL}. */
const ModeModelSchema = z.union([z.literal(MODE_DEFAULT_MODEL), z.string().regex(DEFAULT_MODEL_PATTERN, { error: "model must be a `provider/model` id" })]);
/**
* A mode as a request that ran under it records it: the id it is referenced by, and the name
* it had when the request ran (#245, M6). Recorded on `span.model_request_start` beside the
* resolved model and effort, so a log stays accurate after the mode is renamed or edited.
*/
const ModeReferenceSchema = z.object({
	id: ModeIdSchema,
	name: z.string().min(1).max(64)
});
/**
* // extension: a mode's override of which built-in tools are on (epic #303, X4; issue #307).
*
* A mode's job is to make a chat behave a certain way — the same one that bundles a model and
* an effort bundles a tool set — so `deep` may want `web_search` on and `todo_write` off, and
* every chat that follows it gets that from its next request on, live, exactly as it gets the
* mode's model.
*
* Three things about it are deliberate:
*
* - **A per-tool patch, not a set.** A tool the mode does not name follows the user's own
*   setting, so a mode expresses "this one differently" rather than restating the whole tool
*   list — and a tool added to the build later is not silently turned off by a mode written
*   before it existed.
* - **On and off, never a permission.** A mode decides *which* tools a chat has, not what a
*   call to one may do: a permission is the user's (E6), and "always allow" (#309) is
*   remembered per tool.
* - **Built-in tools by name, MCP servers and skills by id.** `builtin` is keyed by tool name,
*   `mcp_servers` by a server's `mcps_` id and `skills` by a skill's `skl_` id — a mode turns a
*   whole server or a whole skill on or off, never one MCP tool (an MCP tool's permission and
*   its server's presence are different things, and only the second is a mode's; #311/#312).
*   Separate keys rather than one, so none can be mistaken for another: a tool name is free
*   text and an id is not.
*/
/**
* What an item a mode's map does not name does (epic #349, B8).
*
* - `follow` — the user's own setting. The default when the field is absent, and the value
*   every existing mode (and every mode created from a form that does not ask) reads as.
* - `off` — an **allowlist**: only the items the map turns on are on; anything added later
*   stays off in this mode.
* - `on` — every item is on except those the map turns off, even items the user turned off
*   globally.
*
* An explicit map entry always wins, and a hard rule that removes an item from a context
* (`ask_user` in a non-interactive child, the subagent tools a depth does not offer,
* `disable-model-invocation` skills out of the model's listing) cannot be undone by any of
* these.
*/
const ModeItemDefaultSchema = z.enum([
	"follow",
	"off",
	"on"
]);
/** The value an absent {@link ModeItemDefault} field means. */
const MODE_ITEM_DEFAULT = "follow";
const ModeToolOverrideSchema = z.object({
	/**
	* Per-tool on/off. An entry always wins over {@link builtin_default}; a tool with no entry
	* follows the section default, which is the user's own setting when that is `'follow'`.
	*
	* Optional, and absent from a mode that says nothing about built-in tools — as it is from
	* one that says something about servers or skills only (a shape written before #307, and
	* the shape a patch that does not touch this half produces now).
	*/
	builtin: z.record(ToolNameSchema, z.boolean()).optional(),
	/**
	* // extension: per-**server** on/off for the user's remote MCP servers (epic #303, X10;
	* #311). A server the map does not name follows the user's own `enabled` on the resource —
	* which is the user's default — and a mode that names one turns it on or off for every chat
	* that follows the mode, live, exactly as it decides a built-in tool.
	*
	* **Never per MCP tool, and never a permission.** A mode says whether a server is in play;
	* which of its tools a request offers and under which permission is the server's own
	* listing and the user's settings (#312), not a mode's.
	*
	* **A server the user no longer has is simply ignored.** A mode is stored by id, and the
	* server may be deleted afterwards — or the id may name nothing at all, because the mode
	* routes do not check it — so a map entry is an instruction about a server that is still
	* there and has no effect on one that is not.
	*
	* Optional, and absent from every mode stored before #311, so "this mode says nothing about
	* MCP servers" and "an empty map" are the same thing.
	*/
	mcp_servers: z.record(McpServerIdSchema, z.boolean()).optional(),
	/**
	* // extension: per-**skill** on/off for the user's imported skills (epic #349, K8). A
	* sibling of `mcp_servers`, keyed by a skill's `skl_` id, and read exactly the same way: a
	* skill the map does not name follows the user's own `enabled` on the resource — the user's
	* default — and a mode that names one turns it on or off for every chat that follows the
	* mode, live, from the next request on. A subagent type is a mode (epic #340, S2), so this is
	* what gives one its own set of skills.
	*
	* **A skill the user no longer has is simply ignored**, like a deleted MCP server: the mode
	* routes store the map as written and do not check the ids, so deleting a skill leaves the
	* modes that named it untroubled — the entry just matches nothing.
	*
	* Optional, and absent from every mode stored before #349, so "this mode says nothing about
	* skills" and "an empty map" are the same thing.
	*/
	skills: z.record(SkillIdSchema, z.boolean()).optional(),
	/**
	* // extension: what a built-in tool with **no `builtin` entry** does (epic #349, B8).
	*
	* `'follow'` — the absence of the field, and the value every mode stored before this field
	* existed reads as — is today's behaviour: the tool follows the user's own setting in
	* `/v1/me/tools`. `'off'` makes the map an **allowlist**: every tool the map does not turn on
	* is off, so a tool the build adds later stays off in this mode. `'on'` makes every tool on
	* except the ones the map turns off, **even tools the user turned off globally**.
	*
	* An explicit entry always wins. It is still **never a permission**: the default decides
	* which tools a chat has, not what a call may do, and a hard rule that removes a tool from a
	* context (`ask_user` in a non-interactive child, the subagent tools a session's depth does
	* not offer) cannot be undone by `'on'`.
	*/
	builtin_default: ModeItemDefaultSchema.optional(),
	/**
	* // extension: what a remote MCP server with **no `mcp_servers` entry** does (epic #349,
	* B8) — `'follow'` (the user's `enabled`), `'off'` (only the servers the map turns on are in
	* force) or `'on'` (every server except the ones the map turns off). An explicit entry
	* always wins, and a deleted server named by the map is still ignored.
	*/
	mcp_servers_default: ModeItemDefaultSchema.optional(),
	/**
	* // extension: what an imported skill with **no `skills` entry** does (epic #349, B8) —
	* `'follow'` (the skill's own `enabled`), `'off'` (only the skills the map turns on are in
	* force, so one imported later stays off in this mode) or `'on'` (every skill except the
	* ones the map turns off). An explicit entry always wins; `disable-model-invocation` still
	* keeps a skill out of the model's listing whatever this says.
	*/
	skills_default: ModeItemDefaultSchema.optional()
});
/**
* The one resolution rule a mode's default and map entry follow, for one item.
*
* **An explicit entry wins; otherwise the section default decides; `follow` falls through to
* the user's own setting.** This is the function every consumer of a mode's override uses —
* the built-in tool listing and the brain's offer, the in-force MCP servers, the in-force
* skills and both frontends' `/` menus — so server, brain, child sessions, web and `oh`
* cannot drift about what a mode turns on.
*
* @param entry the mode's map entry for the item, or `undefined` when it does not name it
* @param follows what the item does when the mode says nothing (the user's own setting)
* @param fallback the section default, or `undefined` for `follow`
*/
function modeItemInForce(entry, follows, fallback) {
	if (entry !== void 0) return entry;
	switch (fallback ?? "follow") {
		case "on": return true;
		case "off": return false;
		default: return follows;
	}
}
/**
* Whether one built-in tool is in force for a chat on `override` (epic #303, X4; #307; the
* default: epic #349, B8).
*
* @param name the tool's name as the settings speak of it
* @param follows whether the user's own setting has it on
* @param override the resolved mode's `tools`, or `null` for no mode
*/
function builtinToolInForce(name, follows, override) {
	return modeItemInForce(override?.builtin?.[name], follows, override?.builtin_default);
}
/**
* Whether one remote MCP server is in force for a chat on `override` (epic #303, X10; #311;
* the default: epic #349, B8).
*
* @param serverId the server's `mcps_` id
* @param follows the server resource's own `enabled`
* @param override the resolved mode's `tools`, or `null` for no mode
*/
function mcpServerInForce(serverId, follows, override) {
	return modeItemInForce(override?.mcp_servers?.[serverId], follows, override?.mcp_servers_default);
}
/**
* // extension: the description that makes a mode a **subagent type** (epic #340, S2; #342).
*
* A parent model starting a helper picks a *kind* of helper, and a kind is a mode: the mode
* already says how a chat on it runs — a model, an effort, a prompt addition and a tool set —
* and this field is the one thing it did not have, what the mode is **good for**. A mode with
* one is offered to the parent as a type it may start; a mode without is not, because
* `null` is the honest spelling of "this mode is not meant to be one".
*
* It is a description for a **model**, not for a settings screen: short sentences saying what
* the type is for, phrased so the parent can tell it from its neighbours ("Reviews a diff for
* correctness bugs", "Searches the web and reports sources"), and bounded to
* {@link MODE_SUBAGENT_DESCRIPTION_MAX_LENGTH} because every one of them is part of the one
* description of the `agent` tool.
*
* The type a mode becomes resolves **exactly as a chat on that mode does** — its model
* ("my default model" included), its effort, its prompt addition and its tool and MCP-server
* override — so a user retunes what their helpers do by editing the mode they already have.
* Policies are not a mode's (E6): what a call may do stays the user's setting.
*
* Stripped like every unknown field when the API did not put it there; the empty string is
* refused rather than read as `null`, because "no description" has one spelling.
*/
const ModeSubagentDescriptionSchema = z.string().min(1).max(500);
/** The `mode` resource. */
const ModeSchema = z.object({
	id: ModeIdSchema,
	type: z.literal("mode"),
	/**
	* // extension: the user this mode belongs to (epic #65, A4). Read-only: the server sets it
	* from the caller, no request carries it, and another user's mode is answered as missing.
	*/
	owner_id: UserIdSchema,
	/** The mode's name, unique among its owner's modes. */
	name: z.string().min(1).max(64),
	/** The model a chat on this mode runs, or {@link MODE_DEFAULT_MODEL} to follow the default. */
	model: ModeModelSchema,
	/**
	* The reasoning effort a chat on this mode runs at (#252), or `null` for the model's
	* provider default. A `user.message` carrying an explicit `reasoning_effort` overrides it.
	*/
	reasoning_effort: ReasoningEffortSchema.nullable(),
	/**
	* Appended to the session's own system prompt for every request the mode runs, or `null`
	* for no addition. Appended after it, never in place of it.
	*/
	system_prompt_addition: z.string().nullable(),
	/**
	* What this mode is good for, when it is offered to a parent model as a subagent type
	* (epic #340, S2; #342), or `null` for a mode that is not one. See
	* {@link ModeSubagentDescriptionSchema}.
	*/
	subagent_description: ModeSubagentDescriptionSchema.nullable(),
	/**
	* Which tools, remote MCP servers and skills a chat on this mode has on or off, overriding
	* the user's own settings (#307; the MCP half: #311; the skills half: epic #349, K8) — or
	* `null` for no override at all, a mode that says nothing and lets every chat follow its
	* owner. Never a permission: see {@link ModeToolOverrideSchema}.
	*/
	tools: ModeToolOverrideSchema.nullable(),
	created_at: TimestampSchema,
	/** When the mode was last changed. Set on update; equal to `created_at` at creation. */
	updated_at: TimestampSchema
});
/**
* Body of `POST /v1/me/modes`. Response: {@link ModeSchema}.
*
* A mode whose model the user cannot use yet is still valid to store — the key may come
* later, and "my default model" may be unset today — so creation never checks availability;
* a *chat* on an unavailable mode is refused instead.
*/
const CreateModeRequestSchema = z.object({
	name: z.string().min(1).max(64),
	model: ModeModelSchema,
	reasoning_effort: ReasoningEffortSchema.nullable().optional(),
	system_prompt_addition: z.string().nullable().optional(),
	subagent_description: ModeSubagentDescriptionSchema.nullable().optional(),
	tools: ModeToolOverrideSchema.nullable().optional()
});
/**
* Body of `POST /v1/me/modes/{mode_id}`. Every field is optional; omitted fields keep their
* stored value, and `null` clears a nullable one. Response: {@link ModeSchema}.
*/
const UpdateModeRequestSchema = z.object({
	name: z.string().min(1).max(64).optional(),
	model: ModeModelSchema.optional(),
	reasoning_effort: ReasoningEffortSchema.nullable().optional(),
	system_prompt_addition: z.string().nullable().optional(),
	subagent_description: ModeSubagentDescriptionSchema.nullable().optional(),
	tools: ModeToolOverrideSchema.nullable().optional()
});
/**
* Response of `GET /v1/me/modes`.
*
* No pagination envelope, the shape the provider-credential list uses: a user's modes are
* capped at {@link MAX_MODES_PER_USER}, so the response is bounded and a picker can show them
* all at once.
*/
const ListModesResponseSchema = z.object({ data: z.array(ModeSchema) });
//#endregion
//#region src/resources/skill.ts
/**
* The `skill` resource and the endpoints that manage it:
*
* - `GET    /v1/me/skills`
* - `GET    /v1/me/skills/{skill_id}`                (with the `SKILL.md` file's content)
* - `GET    /v1/me/skills/{skill_id}/files/{path}`   (one file's content)
* - `POST   /v1/me/skills/{skill_id}`                (update `enabled`)
* - `DELETE /v1/me/skills/{skill_id}`
*
* // extension: Anthropic's Managed Agents API has `skills` on an agent as a per-run reference
* to a skill Anthropic holds. openharness does not: a **user** imports skills they have
* reviewed (epic #349, K3), each is a snapshot of a folder of files stored under their own id
* (K4), and a skill is a per-user resource like a mode or a remote MCP server. Import and
* refresh — the two routes that write a snapshot — are #351; this module is the resource those
* routes answer with, the vocabulary the rest of the epic reads, and the limits they enforce.
*
* ## The format (K1)
*
* A skill is a folder with a `SKILL.md` at its root: YAML frontmatter carrying a `name` and a
* `description`, then a Markdown body — plus any other files the folder holds. The format is
* the open [Agent Skills](https://agentskills.io) one, used unchanged, so a skill written for
* another harness imports as it is. `name` is lowercase letters, digits and interior hyphens,
* at most {@link SKILL_NAME_MAX_LENGTH} characters, and it matches the folder's name;
* `description` is at most {@link SKILL_DESCRIPTION_MAX_LENGTH} characters.
*
* Three optional frontmatter fields are **honoured** (a later sub-issue reads them; this one
* stores them with the rest): `disable-model-invocation: true`, a command only the user runs;
* `user-invocable: false`, one only the model uses; and `argument-hint`, shown in the `/` menu.
* Every other field — `license`, `metadata`, `compatibility`, `allowed-tools`, … — is stored
* and shown and otherwise ignored.
*/
/** Longest skill name the API accepts (K1): 64 characters. */
const SKILL_NAME_MAX_LENGTH = 64;
/**
* The shape of a skill name (K1): lowercase letters, digits and interior hyphens.
*
* Narrower than the open format's prose on two points, both of which the format's own
* examples satisfy: no leading or trailing hyphen, and no uppercase. It is the same pattern a
* remote MCP server's name takes ({@link isMcpServerName}), restated here because the two are
* free to diverge — a skill name is a folder's name and a slash command, not a tool prefix.
*/
const SKILL_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** Whether `value` has the shape of a skill name — {@link SKILL_NAME_PATTERN}, and a length inside the cap. */
function isSkillName(value) {
	return value.length > 0 && value.length <= 64 && SKILL_NAME_PATTERN.test(value);
}
/**
* The names a skill may not take: every built-in slash command (epic #349, K7).
*
* A built-in command is a **client action** the user types — `/compact` today, and `/new`,
* `/model`, `/mode`, `/agents` and `/skills` as their features land — and it is not a skill.
* A skill that took one of these names would be unreachable in the `/` menu and ambiguous in a
* transcript, so the list is reserved: {@link SkillNameSchema} refuses a skill named for one.
*
* It is spelled **once**, here, because three sides have to agree on it and none owns it: the
* import's validation (#351), the resource this schema describes, and the `/` menu, which
* reads the built-ins from this constant beside the user's own skills (#352, #353, #354).
*/
const RESERVED_SKILL_NAMES = [
	"compact",
	"new",
	"model",
	"mode",
	"agents",
	"skills"
];
/** Whether `value` is a built-in slash command's name, which no skill may take. */
function isReservedSkillName(value) {
	return RESERVED_SKILL_NAMES.includes(value);
}
/**
* A skill name: the rule of K1 shape-checked by {@link isSkillName} and reserved names refused.
*
* Both checks are here rather than at a caller because this schema is the one place a name is
* validated — a name that reaches a store has already passed it.
*/
const SkillNameSchema = z.string().refine(isSkillName, { error: `a skill name is 1–64 lowercase letters, digits and interior dashes, e.g. \`release-notes\` — and it matches the skill folder’s name` }).refine((name) => !isReservedSkillName(name), { error: `a skill may not take a built-in command name (${RESERVED_SKILL_NAMES.join(", ")})` });
/** Longest skill description the format allows (K1): 1024 characters. */
const SKILL_DESCRIPTION_MAX_LENGTH = 1024;
/**
* The file every skill's folder carries (K1): the frontmatter that names and describes it,
* then the instructions a model or a user reads.
*
* It is the one path with a bound of its own ({@link SKILL_MD_MAX_BYTES}) and the one every
* skill must hold: a snapshot without it is not a skill, and a store refuses one.
*/
const SKILL_MD_FILENAME = "SKILL.md";
/**
* The most characters a slash invocation's arguments may carry (K7).
*
* The arguments ride the `user.message` as typed and are substituted into the body the model is
* given, so they are part of a request's size like anything else; the cap keeps a paste from
* becoming a message the protocol stores without bound. It is generous — a skill invocation is a
* sentence, not a document.
*/
const SKILL_ARGUMENTS_MAX_LENGTH = 1e4;
/**
* The three honoured fields of a skill's snapshot (K1), read one way by every consumer.
*
* A field is honoured only in the shape the format documents — a real `true` for
* `disable-model-invocation`, a real `false` for `user-invocable`, a string for `argument-hint`
* (or the flow-sequence array the format's own `[version]` spelling parses to, rendered back as
* written) — because the frontmatter is open and untyped ({@link SkillFrontmatterSchema}): a
* value a hand-written file got wrong is an ignored field rather than a skill that behaves
* differently from what it says. The lookup is case-sensitive to the field names the format uses.
*/
function skillSettingsOf(skill) {
	const frontmatter = skill.frontmatter;
	return {
		disableModelInvocation: frontmatter["disable-model-invocation"] === true,
		userInvocable: frontmatter["user-invocable"] !== false,
		argumentHint: argumentHintOf(frontmatter["argument-hint"])
	};
}
/**
* Whether one skill is **on** for a request, given the mode's override (epic #349, K8).
*
* The rule is the one every reader must apply: **a mode that names the skill wins; a skill it
* does not name follows {@link ModeToolOverride.skills_default} — `follow` (the user's
* `enabled`), `off` (an allowlist, so a skill imported later stays off in this mode) or `on`
* (every skill except the ones the mode turns off)**. A map entry naming a skill the user no
* longer has never matches anything, so a mode that named a deleted skill is simply ignored.
* It is exported from the protocol — rather than written once in the server and once in each
* frontend — because the server's `assertInvocableSkills` and the `/` menu have to answer with
* the same effective set: a menu row the server refuses, or a missing row the server would
* accept, is a bug that only one shared rule can prevent.
*
* A mode default cannot bring back a skill a **hard rule** removes: `disable-model-invocation`
* still keeps the skill out of the model's listing, and `resolveSkills` applies that beside this
* function.
*
* @param skill the skill as the store lists it (its stored `enabled`, never an effective one)
* @param override the resolved mode's `tools` patch, or `null` for no override
*/
function skillInForce(skill, override) {
	return modeItemInForce(override?.skills?.[skill.id], skill.enabled, override?.skills_default);
}
/**
* The effective skills of a list, in its own order (K8) — {@link skillInForce} applied to
* every one of them.
*
* The order is deliberately the input's (the store lists a user's skills oldest first), so a
* frontend that shows the effective set and one that shows the stored list agree on
* everything but the on/off answer.
*/
function skillsInForce(skills, override) {
	return skills.filter((skill) => skillInForce(skill, override));
}
/**
* `argument-hint` as the text a `/` menu shows, or `null` for a value that is not one.
*
* A string is the hint itself, trimmed; an empty one names nothing. An **array** is the
* format's own bracket spelling read as YAML — `argument-hint: [version]` is a flow sequence,
* not a string — and it is still the hint the file meant, so it is rendered back the way the
* line wrote it (`[version]`, `[a, b]`). A snapshot imported since the import keeps the raw
* line (see `apps/server`'s discovery), so this half is for one stored before that: the shape
* the file has never changes the hint a reader sees.
*/
function argumentHintOf(value) {
	if (typeof value === "string") {
		const hint = value.trim();
		return hint.length === 0 ? null : hint;
	}
	if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
		const items = value.map((item) => item.trim()).filter((item) => item !== "");
		return items.length === 0 ? null : `[${items.join(", ")}]`;
	}
	return null;
}
/**
* A `SKILL.md` file's **body**: everything after its YAML frontmatter (K1, K6).
*
* The one reading of where the frontmatter ends, so the body a model is handed is the same in
* the tool that serves it, the context builder that renders a slash invocation and any client
* that shows a skill. The rules are the format's own:
*
* - the file opens with a line that is exactly `---` (a leading byte-order mark is tolerated);
* - the frontmatter runs to the next line that is exactly `---` or `...`;
* - everything after it is the body, trimmed of the blank lines that separate the two.
*
* A file that does not open a frontmatter block has no frontmatter to strip and is its own body,
* and so is one whose block never closes — the markers are what make a block, and a file without
* them is not one. Nothing here parses YAML: the parsed frontmatter is the resource's own field
* ({@link Skill.frontmatter}), and this helper only knows where the text it came from begins.
*/
function skillBody(content) {
	const text = content.startsWith(BOM) ? content.slice(BOM.length) : content;
	const lines = text.split("\n");
	if (!isFrontmatterFence(lines[0])) return text.trim();
	for (let index = 1; index < lines.length; index += 1) if (isClosingFence(lines[index])) return lines.slice(index + 1).join("\n").trim();
	return text.trim();
}
/** The byte-order mark a file saved by a Windows editor may open with. */
const BOM = "﻿";
/** Whether a line opens a `SKILL.md`'s frontmatter block. */
function isFrontmatterFence(line) {
	return line?.trim() === "---";
}
/** Whether a line closes one: the format's ending marker, or YAML's own document end. */
function isClosingFence(line) {
	const trimmed = line?.trim();
	return trimmed === "---" || trimmed === "...";
}
/**
* The size limits of K4, in bytes.
*
* They bound what one import may store, and they are enforced by the store's callers — the
* import (#351) refuses a skill that breaks one with the reason, which is why they are
* constants here rather than checks inside a store. Sizes are counts of **bytes**, not
* characters ({@link skillFileSize}), so a limit means the same thing whatever the files hold.
*/
/** Largest `SKILL.md` a skill may carry: 20 KB. */
const SKILL_MD_MAX_BYTES = 20480;
/** Largest of any one file of a skill: 256 KB. */
const SKILL_FILE_MAX_BYTES = 262144;
/** Largest whole skill, its files together: 2 MB. */
const SKILL_MAX_BYTES = 2097152;
/** Most files one skill may hold: 100. */
const MAX_SKILL_FILES = 100;
/**
* Most skills one user may hold: 50.
*
* A store rule as well as a caller's — the store counts and inserts under a per-owner lock, so
* two concurrent creates cannot both take the last slot, exactly as modes and remote MCP
* servers do.
*/
const MAX_SKILLS_PER_USER = 50;
/**
* Largest clone an import may download: 20 MB (K4).
*
* A constant only, for now: the shallow clone it bounds is #351's, and it is here because the
* limit belongs with the others rather than in the module that happens to consume it first.
*/
const SKILL_CLONE_MAX_BYTES = 20971520;
/**
* Largest total of **unpacked** blob bytes one clone may read: 100 MB.
*
* The download cap above bounds the compressed pack, and a pack of highly compressible content
* can inflate to orders of magnitude more than its download size — `SKILL_CLONE_MAX_BYTES` of
* zeros is gigabytes once read. The clone therefore also caps the sum of the blobs it really
* inflates, and refuses past it with `over_limit`.
*
* The number is the most a legitimate import could need: fifty skills (§
* {@link MAX_SKILLS_PER_USER}) each at the whole-skill limit (§ {@link SKILL_MAX_BYTES}). A
* caller that reads only a folder's own blobs — the import passes its `path` to the clone —
* therefore cannot reach it with skills that pass K4, while a repository whose content unpacks
* past it is refused rather than left to exhaust the process.
*/
const SKILL_INFLATE_MAX_BYTES = SKILL_MAX_BYTES * 50;
/**
* The size of a file's content in bytes — what {@link SkillFile.size} counts.
*
* Bytes rather than characters, because a limit of "20 KB" has to mean the same thing for a
* file of ASCII and a file of CJK or emoji: `TextEncoder` counts what would really be stored,
* and it is a Web API both a browser and Node 24 have, so this package stays browser-safe.
*
* A base64 file's `size` is the number of bytes it decodes to, not the length of the base64
* text: the limits of K4 count the file itself, and a base64 string is a third larger than the
* bytes it carries. The arithmetic below is exact for the canonical base64 the import writes
* (including a final group padded with `=` or `==`), and requires no `atob` — which keeps this
* function usable in `@openharness/protocol`'s browser-safe program.
*/
function skillFileSize(content, encoding = "utf8") {
	if (encoding === "base64") {
		const padding = content.endsWith("==") ? 2 : content.endsWith("=") ? 1 : 0;
		return Math.floor(content.length * 3 / 4) - padding;
	}
	return new TextEncoder().encode(content).length;
}
/**
* How a file's content is spelled in storage (K4).
*
* A skill's files are not all text: a folder may ship an icon, a font or a `.docx`, and the
* import stores those losslessly rather than decoding them into mojibake. `utf8` is the
* ordinary case — `content` is the file's text; `base64` is a non-text file — `content` is the
* file's bytes, base64-encoded — and `size` is the original byte length either way.
*
* The encoding is what Postgres needs: its `text` type rejects a NUL byte outright, so a
* binary file cannot be stored as text at all (`invalid byte sequence for encoding "UTF8":
* 0x00`), and a file that is not valid UTF-8 cannot be kept faithfully as a string. Base64 is
* the lossless spelling every store accepts.
*/
const SkillFileEncodingSchema = z.enum(["utf8", "base64"]);
/**
* // extension: the frontmatter fields besides `name` and `description`, as parsed.
*
* Open and JSON-valued on purpose: the format allows any field, its own examples carry nested
* maps and lists (`metadata`, `allowed-tools`), and a deployment must not have to know a field
* to store or show it. The honoured keys of K1 (`disable-model-invocation`, `user-invocable`,
* `argument-hint`) ride here beside the ignored ones, so a reader sees exactly what the file
* said — what the import (#351) writes is the parsed frontmatter with the two fields the
* resource carries as its own ({@link Skill.name}, {@link Skill.description}) left out.
*/
const SkillFrontmatterSchema = z.record(z.string(), z.unknown());
/**
* One file of a skill, as a **listing** reports it: where it is and how big it is, and never
* its content.
*
* `path` is relative to the skill folder and spelled with forward slashes (`SKILL.md`,
* `reference/notes.md`); the file's content has a read of its own
* (`GET /v1/me/skills/{skill_id}/files/{path}`). Keeping the content out of the resource is
* what makes a listing of fifty skills one small answer whatever the snapshots hold — and what
* keeps the store free to move contents to object storage later (K4) without a resource
* changing shape.
*/
const SkillFileSchema = z.object({
	/** The file's path inside the skill folder, relative and forward-slashed. */
	path: z.string().min(1),
	/** The content's size in bytes (see {@link skillFileSize}), not its character count. */
	size: z.number().int().min(0)
});
/**
* Where a skill's snapshot came from (K3, K4): the public git URL it was imported from, the
* ref and path it was taken at, and the commit it was cloned.
*
* Recorded so a user can see what they imported and re-run the same clone by hand, and so a
* refresh knows what to compare against (K5): the diff is against `commit_sha`. The fields are
* the import's own input: `url` is the `https://` git URL, `ref` the branch, tag or commit the
* user asked for, `path` the folder inside the repository, and `commit_sha` the commit the
* clone really landed on. `ref` and `path` are `null` when the user named none, and so is
* `commit_sha` for a snapshot nothing cloned — a skill a test wrote directly.
*/
const SkillSourceSchema = z.object({
	/** The public `https://` git URL the skill was imported from. */
	url: z.string(),
	/** The branch, tag or commit the clone was asked for, or `null` for the repository's default. */
	ref: z.string().nullable(),
	/** The folder inside the repository the skill was taken from, or `null` for its root. */
	path: z.string().nullable(),
	/** The commit the clone landed on, or `null` for a snapshot nothing cloned. */
	commit_sha: z.string().nullable()
});
/**
* A user's imported skill, as the API returns it.
*
* `files` is a **listing** — each file's path and size ({@link SkillFileSchema}) — and never
* its content: reading one file is its own request, and the `SKILL.md` the resource is
* described by comes with the single-skill read
* ({@link GetSkillResponseSchema}).
*/
const SkillSchema = z.object({
	id: SkillIdSchema,
	/**
	* // extension: the discriminant, so the resource can be told from an agent or a session in
	* a bag of mixed resources. Anthropic has no per-user skill resource.
	*/
	type: z.literal("skill"),
	/**
	* // extension: the user this skill belongs to (epic #65, A4). Read-only: the server sets it
	* from the caller, no request carries it, and another user's skill is answered as missing.
	*/
	owner_id: UserIdSchema,
	/** The skill's name (K1), unique among its owner's skills and the command it is run as. */
	name: SkillNameSchema,
	/** The one-line description the model is told about the skill (K1). */
	description: z.string().max(SKILL_DESCRIPTION_MAX_LENGTH),
	/** The frontmatter's other fields, as parsed — see {@link SkillFrontmatterSchema}. */
	frontmatter: SkillFrontmatterSchema,
	/** Where the snapshot came from: the URL, the ref, the path and the commit (K3, K4). */
	source: SkillSourceSchema,
	/** The snapshot's files, each with its path and size — never its content. */
	files: z.array(SkillFileSchema),
	/**
	* Whether the skill is on for this user's chats (K8).
	*
	* A skill is on when it is imported, and the user turns it off; a mode may override which
	* skills are on (K8) through its own `skills` map, which is a **patch** over this field and
	* never a change to it — this is the stored default, not the effective set.
	*/
	enabled: z.boolean(),
	/** When the snapshot was first stored. */
	imported_at: TimestampSchema,
	/** When the snapshot was last replaced by a refresh (K5), or {@link Skill.imported_at} if never. */
	refreshed_at: TimestampSchema
});
/**
* One file of a skill, with its content: the answer of
* `GET /v1/me/skills/{skill_id}/files/{path}`.
*
* The path and size the listing carries are repeated beside the content so a reader does not
* have to pair this answer with a listing to know what it is holding. The content is the file
* **exactly as stored** — the snapshot is the skill, and a refresh that changed the file is a
* new snapshot rather than an edit to this one.
*/
const SkillFileContentSchema = z.object({
	/** The file's path inside the skill folder, the one that was asked for. */
	path: z.string().min(1),
	/** The content's size in bytes (see {@link skillFileSize}) — the original file's, in bytes. */
	size: z.number().int().min(0),
	/**
	* How `content` is spelled: the file's text (`utf8`), or its bytes base64-encoded
	* (`base64`). Required, because a reader that assumed text would show mojibake for a
	* binary file — and a model that read one as base64 would be told nothing useful.
	*/
	encoding: SkillFileEncodingSchema,
	/** The file's content, as the snapshot holds it — see `encoding`. */
	content: z.string()
});
/**
* Response of `GET /v1/me/skills/{skill_id}`: the resource, plus `SKILL.md`'s content.
*
* The one read that carries a file's content without a second request, because the `SKILL.md`
* is what a settings screen (and a user) reads a skill by: its frontmatter is already split
* across {@link Skill.name}, {@link Skill.description} and {@link Skill.frontmatter}, and what
* is left is the instructions. The content is the **whole file** — frontmatter and body — as
* the snapshot stored it, so what a reader sees is what the model would be handed.
*/
const GetSkillResponseSchema = SkillSchema.extend({ 
/** `SKILL.md` as stored: the frontmatter, then the Markdown body. */
content: z.string() });
/**
* Response of `GET /v1/me/skills`.
*
* No pagination envelope, the shape the mode, credential and MCP-server lists use: a user
* holds at most {@link MAX_SKILLS_PER_USER}, so the response is bounded and a settings screen
* shows them all at once.
*/
const ListSkillsResponseSchema = z.object({ data: z.array(SkillSchema) });
/**
* Body of `POST /v1/me/skills/{skill_id}`. Response: {@link SkillSchema}.
*
* `enabled` and nothing else: what a skill is, is its snapshot, and the only field of one a
* user changes without a refresh (K5) is whether it is on (K8) — a new snapshot comes from
* re-importing, which is #351's. Required rather than optional: a body that names no field
* says nothing, so there is no patch to apply.
*/
const UpdateSkillRequestSchema = z.object({ 
/** Whether the skill is on for this user's chats (K8). */
enabled: z.boolean() });
/**
* The distinct ways an import can fail (K3), as stable codes.
*
* Every one of them is a **request the server understood** and could not carry out — the URL
* is fine as a URL, but it is not a repository, or not this one's branch, or the deployment
* refuses to dial the address it names. They are deliberately apart from one another rather
* than one generic refusal, because a user who pasted a URL is owed the difference between
* *their* mistake (a path that is not there, a branch that does not exist), *the
* repository's* state (empty, or not a repository at all), and *this deployment's* policy (a
* private address, no credentials to send); only the last two are the server's to explain,
* and all three are the caller's to act on differently.
*
* The code does not travel on its own — the error envelope has room for a type and a message
* and nothing else — so {@link SKILL_IMPORT_MESSAGES} is the one spelling of each sentence,
* shared by the server that raises it and the client that shows it.
*/
const SKILL_IMPORT_FAILURES = [
	"unreachable",
	"not_a_repository",
	"authentication_required",
	"ref_not_found",
	"path_not_found",
	"no_skills_found",
	"skill_not_importable",
	"over_limit",
	"refused_address"
];
/**
* What each import failure says to the user, as a sentence.
*
* One spelling per code, so the server that raises the error, the tests that pin it and a
* client that shows it cannot drift apart. A failure whose message needs a detail — which
* ref, which path — takes the sentences below and appends it, which is what the server's
* import does; the detail is always the caller's own input, never anything the server knows.
*/
const SKILL_IMPORT_MESSAGES = {
	unreachable: "The repository could not be reached.",
	not_a_repository: "That URL is not a public git repository.",
	authentication_required: "That repository is private or requires credentials, and openharness imports public repositories only.",
	ref_not_found: "That branch, tag or commit is not in the repository.",
	path_not_found: "That folder is not in the repository.",
	no_skills_found: "That folder holds no SKILL.md, so there is no skill to import.",
	skill_not_importable: "That skill cannot be imported as it stands.",
	over_limit: "That repository is larger than the import limit.",
	refused_address: "That address is not a public one, so this deployment will not fetch it."
};
/**
* One thing wrong with a candidate skill an import found (K1, K4).
*
* A **candidate** is a folder holding a `SKILL.md`; a problem is a reason it cannot be
* imported as it stands, and a candidate carries every problem it has rather than the first,
* so a preview can show a user the whole story of a folder at once. Nothing here fails the
* preview: a repository holding one good skill and one broken one offers the good one, and the
* broken one with its reasons.
*
* `code` is the stable half a client branches on, `message` the sentence it shows.
*/
const SKILL_PROBLEM_CODES = [
	"invalid-frontmatter",
	"name-mismatch",
	"reserved-name",
	"duplicate-name",
	"over-limit"
];
/** One reason a candidate cannot be imported as it stands. */
const SkillProblemSchema = z.object({
	/** The stable code; see {@link SKILL_PROBLEM_CODES}. */
	code: z.enum(SKILL_PROBLEM_CODES),
	/** The sentence to show, naming the field or the limit it is about. */
	message: z.string()
});
/**
* One folder holding a `SKILL.md` that an import found, as a **preview** reports it (K3).
*
* Nothing has been stored: this is what the server would import, so the user can read the
* `SKILL.md` and the file list and decide. `name` is the **folder's** name — the identity a
* candidate is picked by, and the name the frontmatter has to agree with (K1) — while
* `description` is the frontmatter's, or `null` when the frontmatter did not parse (`name`
* then carries the folder's name alone, and what the frontmatter said is in its problems).
*
* `skill_md` is the **whole** `SKILL.md` — frontmatter and body — because that is what a
* reader reviews before approving an import, and `files` is a listing (paths and sizes,
* never contents) exactly as a stored skill's is.
*/
const SkillImportCandidateSchema = z.object({
	/** The folder's name (K1) — the key an import picks this candidate by. */
	name: z.string(),
	/**
	* The folder's path in the repository, from the repository's root, forward-slashed.
	*
	* `""` is the repository's own root: a `SKILL.md` sitting there is a candidate like any other,
	* named (its `name`) for the repository rather than for a folder that does not exist.
	*/
	folder: z.string(),
	/** The frontmatter's `description` (K1), or `null` when the frontmatter did not parse. */
	description: z.string().nullable(),
	/** The folder's files, relative to it, each with its size — never its content. */
	files: z.array(SkillFileSchema),
	/** `SKILL.md` as it is in the repository: the frontmatter, then the Markdown body. */
	skill_md: z.string(),
	/** Everything wrong with this candidate ({@link SkillProblemSchema}); empty when it is importable. */
	problems: z.array(SkillProblemSchema),
	/**
	* Whether the caller already has a skill by this name.
	*
	* A preview carries it — rather than leaving the client to diff two reads — because it is
	* the one reason a candidate a user picked will be refused on the import that follows, and
	* the store's rule is per user (K4). It is a separate field and not a {@link SkillProblem}:
	* nothing is wrong with the skill, and the answer a user wants is "you already have one of
	* these", which is not a shape a code could carry without naming the other skill.
	*/
	name_taken: z.boolean()
});
/**
* Where an import is to read from: the repository, and the optional ref and path inside it.
*
* The three fields a user types, and the three `SkillSource` records. `ref` and `path` are
* optional rather than nullable because a body names only what the user said — a browse URL
* (`…/tree/<ref>/<path>`) fills them in server-side, and a repository's default branch is what
* an absent `ref` means (K3).
*/
const SkillImportSourceSchema = z.object({
	/** A public `https://` git URL, or a GitHub/GitLab browse URL (`…/tree/<ref>/<path>`). */
	url: z.string(),
	/** The branch, tag or commit to read, or absent for the repository's default branch. */
	ref: z.string().optional(),
	/** The folder inside the repository to look in, or absent for its root. */
	path: z.string().optional()
});
/**
* Body of `POST /v1/me/skills/import/preview`. Response: {@link PreviewSkillImportResponseSchema}.
*
* The first of the import's two steps (K3): nothing is stored, and the answer is what a
* repository holds under the path. The same body is the second step's, plus the commit the
* preview resolved and the names the user picked — see {@link SkillImportRequestSchema}.
*/
const PreviewSkillImportRequestSchema = SkillImportSourceSchema;
/**
* Response of `POST /v1/me/skills/import/preview`: the commit the clone landed on and every
* candidate it found (K3).
*
* `url`, `ref` and `path` are the server's reading of the request — a browse URL arrives as
* one string and leaves split in three — and `commit_sha` is the commit that reading landed
* on. The three are echoed because they are what a following
* {@link SkillImportRequestSchema} has to name, together with that commit, for the import to
* be the one that was previewed.
*/
const PreviewSkillImportResponseSchema = z.object({
	/** The `https://` git URL the clone was made from, with a browse URL split apart. */
	url: z.string(),
	/** The ref the clone was made at, or `null` for the repository's default branch. */
	ref: z.string().nullable(),
	/** The folder that was looked in, or `null` for the repository's root. */
	path: z.string().nullable(),
	/** The commit the clone landed on; an import naming this commit is the one that was previewed. */
	commit_sha: z.string(),
	/** Every `SKILL.md` under the path, each with its problems; empty only when there are none. */
	candidates: z.array(SkillImportCandidateSchema)
});
/**
* Body of `POST /v1/me/skills/import`. Response: {@link ImportSkillsResponseSchema}.
*
* The second step (K3): the same source, the commit the preview resolved, and the **names** of
* the candidates the user picked. The server clones again — it holds nothing between the two
* calls — and refuses the import when the ref no longer resolves to `commit_sha`, so what is
* stored is what was reviewed rather than whatever the branch has become. `names` is
* deliberately the candidates' names and not their folders: a name is what a skill is
* identified by among its owner's skills (K4), and a candidate whose name is `null` could not
* be stored under one anyway.
*/
const SkillImportRequestSchema = SkillImportSourceSchema.extend({
	/** The commit the preview resolved; the clone must land on it again or the import is refused. */
	commit_sha: z.string(),
	/** The names of the candidates to import, at least one, as the preview spelled them. */
	names: z.array(z.string()).min(1)
});
/**
* Response of `POST /v1/me/skills/import`: the skills that were stored, in the order asked for.
*
* The same envelope {@link ListSkillsResponseSchema} uses — a `data` array, no pagination —
* because the answer is the resources themselves, so a client can draw the new rows without a
* second read.
*
* The whole batch is stored or nothing is (the same rule every other write here follows): a
* picked name that is not a candidate is the 400 `invalid_request_error`, a picked candidate
* that still has a problem is the 422 `skill_import_error` carrying that candidate's own
* problems, and a name the caller already has is the 409 `conflict_error` the store raises —
* each of them before the first skill is written.
*/
const ImportSkillsResponseSchema = z.object({ data: z.array(SkillSchema) });
/**
* Body of `POST /v1/me/skills/{skill_id}/refresh`. Response: {@link SkillSchema}.
*
* A refresh is by hand and always previewed first (K5): the preview clones the skill's stored
* source again and answers what changed, and this confirms it by naming the commit the preview
* resolved. As with an import, a ref that has moved since the preview is refused, so what a
* user confirmed is what gets stored.
*/
const RefreshSkillRequestSchema = z.object({ 
/** The commit the refresh preview resolved. */
commit_sha: z.string() });
/**
* Response of `POST /v1/me/skills/{skill_id}/refresh/preview`: what a re-clone found (K5).
*
* The clone is of the skill's **stored** source — the same URL, ref and path it was imported
* from — and the comparison is against the stored snapshot, so nothing here depends on the
* caller remembering anything. `changed` is the whole answer in one boolean (a client that
* only wants to offer a button reads it and skips the rest); the three lists are the file
* story, and `skill_md_diff` is the unified diff of the one file a skill is really read by.
*
* The lists hold **paths** (relative to the skill folder) rather than listings, because what a
* reader wants from a diff is which files moved; the sizes are on the snapshot the skill
* already carries.
*/
const RefreshSkillPreviewResponseSchema = z.object({
	/** The commit the re-clone landed on. */
	commit_sha: z.string(),
	/**
	* Whether anything at all is different from the stored snapshot — the three lists empty and
	* no diff. `false` is the "already up to date" answer, and it is still a commit: a refresh
	* preview of an unchanged skill names the commit it is unchanged at, so a confirm is
	* still possible (and is a no-op on the snapshot but moves `refreshed_at`).
	*/
	changed: z.boolean(),
	/** Files the new snapshot has and the stored one does not. */
	files_added: z.array(z.string()),
	/** Files the stored snapshot has and the new one does not. */
	files_removed: z.array(z.string()),
	/** Files both hold with different content. */
	files_changed: z.array(z.string()),
	/** The unified diff of `SKILL.md`, or `null` when that file is unchanged. */
	skill_md_diff: z.string().nullable(),
	/**
	* Everything wrong with the new snapshot (K1, K4).
	*
	* A refresh is not refused by a preview: a repository that has since grown a `SKILL.md` over
	* the limit is shown as such, and whether to keep the snapshot the user has is the user's
	* answer to give. A confirm of a snapshot with a problem is the one thing the route refuses
	* — replacing a working skill with a broken one is not a refresh.
	*/
	problems: z.array(SkillProblemSchema)
});
//#endregion
//#region src/events/user.ts
/**
* // extension: the skill a message invokes as a slash command (epic #349, K7).
*
* `/name arguments` is how a user runs a skill: the composer sends an ordinary `user.message`
* whose {@link UserMessageEventSchema.content} is **what was typed** — the transcript shows the
* command a reader wrote, and a client that knows nothing of skills renders it as the text it is
* — carrying this field to say which skill it named and with what. The body itself is *not*
* here: the model is given it, and what the model was given is the `session.skill_loaded` event
* the brain writes when it first handles the message (K7), so an edit or a refresh of the skill
* never rewrites a chat that already ran it.
*
* A built-in command is a **client action**, not a skill: `/compact`, `/new`, `/model` and the
* rest are not invocable this way, which is what {@link RESERVED_SKILL_NAMES} keeps a skill's
* name from colliding with.
*/
const MessageSkillSchema = z.object({
	/** The skill's name, as the user typed it after the `/`. */
	name: SkillNameSchema,
	/**
	* What the user typed after the command, or absent when they typed nothing.
	*
	* Substituted into the body's `$ARGUMENTS` placeholders, or appended after it when the body
	* has none; see the brain's substitution rule. Absent and `''` mean the same thing to a skill
	* with placeholders, and only the absent spelling is normalised away.
	*/
	arguments: z.string().max(SKILL_ARGUMENTS_MAX_LENGTH).optional()
});
/**
* Events the user sends to a session.
*
* These are the events a client appends on its own behalf, and the ones a session queues for
* the brain: `POST /v1/sessions/{session_id}/events` accepts them, and one instruction more —
* a `session.rewind` (#238), whose event the server writes. A user event is written to the
* log the moment it is
* accepted and is never modified after that (D9, issue #46): it is stored as written — with
* `processed_at: null` — and the claim the brain takes on it is a fact recorded beside it,
* so a read derives the `processed_at` it reports from the claim that took the event. `null`
* (no claim yet) is what tells a client that the agent has not seen the message.
*/
/** A message from the user to the agent. v1 carries text blocks only. */
const UserMessageEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.userMessage),
	seq: EventSeqSchema,
	processed_at: QueuedProcessedAtSchema,
	content: ContentBlocksSchema,
	/**
	* // extension: switch the session's model for this message and the ones after it (#111).
	*
	* When a message carries a `model`, appending it also sets the session's current model —
	* `Session.model`, the projection — to it, in the same transaction as the append, and the
	* session keeps running that model until another message changes it. The brain makes each
	* model request with the session's current model, so a switch sent while a turn is running
	* applies from the next request. The log stays the source of truth: this field is the
	* record of the switch, and each `span.model_request_start` records the model that ran.
	*
	* Optional: a message without one leaves the session's model alone, which is every message
	* an agent-based client sends. Anthropic has no equivalent — there the model is the
	* agent's, fixed when the session is created.
	*/
	model: ModelConfigSchema.optional(),
	/**
	* // extension: switch the session's mode for this message and the ones after it (#245, M6).
	*
	* A `mode` is the id of one of the user's modes: appending it sets the session's mode to it
	* in the same append, exactly as a `model` switches the model (#111), and every request from
	* then on resolves the mode as it is now — its model, effort and prompt addition. `null`
	* detaches the session from any mode, and a message that carries a `model` but no `mode`
	* detaches too: a chat follows either a mode or a plain model, never both. A message that
	* carries neither leaves the session's mode alone.
	*
	* The server resolves and validates the mode on the append path, so a mode the user does
	* not own — or one whose model cannot be used — is refused before anything is stored.
	*/
	mode: ModeIdSchema.nullable().optional(),
	/**
	* // extension: run this message's turn at a reasoning effort, and the turns after it (#252).
	*
	* The effort a request runs with is the newest one the log carries: appending a message with
	* one sets it for the turn the message starts and for every turn after it, exactly as a
	* `model` switches the model (#111). `null` asks for the provider's default again, and a
	* message that leaves the field out changes nothing — so a log that never carried an effort
	* replays exactly as it always did.
	*
	* Whether a request *runs* at the effort is the model's business: a model that takes none
	* runs the provider's default, and the request's `span.model_request_start` records what was
	* asked for beside what was applied (see `ReasoningEffortRunSchema`).
	*/
	reasoning_effort: ReasoningEffortSchema.nullable().optional(),
	/**
	* // extension: the skill this message invoked as a slash command (epic #349, K7).
	*
	* Stored on the event because the invocation *is* what the user did — a client reading the log
	* back says "/release-notes v1.2" rather than showing text that happens to start with a slash
	* — and because the brain uses it to find the `session.skill_loaded` event it wrote for this
	* message (K7): the two name each other by name and by `seq`. See {@link MessageSkillSchema}.
	*
	* The server refuses a message naming a skill its owner cannot run — unknown, turned off, or
	* `user-invocable: false` — before anything is stored, so an event carrying this field always
	* named a skill that was in force when it landed.
	*/
	skill: MessageSkillSchema.optional()
});
/**
* Stop the agent mid-execution.
*
* The response in flight is cut short: whatever text it had already produced is stored as an
* `agent.message`, its `span.model_request_end` carries an `interrupted` error, and the turn
* ends with `session.status_idle { stop_reason: { type: "end_turn" } }`. There is no stop
* reason specific to interruption.
*/
const UserInterruptEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.userInterrupt),
	seq: EventSeqSchema,
	processed_at: QueuedProcessedAtSchema
});
/** Any stored user event. */
const UserEventSchema = z.discriminatedUnion("type", [UserMessageEventSchema, UserInterruptEventSchema]);
/**
* A user event as a client sends it: the same shapes without the fields the server assigns
* (`id`, `seq`) or fills in later (`processed_at`).
*
* Anthropic calls these `...EventParams`; they are the members of the `events` array on
* `POST /v1/sessions/{session_id}/events` and of `initial_events` on session creation.
*/
const UserMessageEventInputSchema = z.object({
	type: z.literal(EVENT_TYPES.userMessage),
	content: ContentBlocksSchema,
	/** Switch the session's model for this message and the ones after it (#111). */
	model: ModelConfigSchema.optional(),
	/**
	* Switch the session's mode from this message on, or detach it with `null` (#245, M6). A
	* `model` without a `mode` detaches; see {@link UserMessageEventSchema}.
	*/
	mode: ModeIdSchema.nullable().optional(),
	/** Run at a reasoning effort from this message on; `null` returns to the provider's default (#252). */
	reasoning_effort: ReasoningEffortSchema.nullable().optional(),
	/**
	* Invoke one of the user's skills as a slash command (epic #349, K7): the skill's name and the
	* arguments typed after it, while `content` stays whatever the user typed. Refused with a 400
	* when the skill is unknown, off, or not user-invocable.
	*/
	skill: MessageSkillSchema.optional()
});
/** A `user.interrupt` as a client sends it: only the type. */
const UserInterruptEventInputSchema = z.object({ type: z.literal(EVENT_TYPES.userInterrupt) });
/** Any user event a client may send. */
const UserEventInputSchema = z.discriminatedUnion("type", [UserMessageEventInputSchema, UserInterruptEventInputSchema]);
//#endregion
//#region src/events/span.ts
/**
* Span events: observability markers that wrap a model request.
*
* Every model request is bracketed by a `span.model_request_start` and a
* `span.model_request_end` carrying its token usage, so a client can time a request and add
* up a turn's cost from the log alone. The end event is always written, even when the request
* was aborted by an interrupt or its span was closed by a recovering brain.
*/
/**
* Token counts for one model request. Matches Anthropic's `BetaManagedAgentsSpanModelUsage`
* without its optional `speed` field.
*
* The four counters are **disjoint** — no token is counted twice — which is what lets
* `usageCost` price each at its own rate. In particular `input_tokens` is the **uncached** input,
* the way Anthropic's own `input_tokens` reads: a provider whose API reports a cache-inclusive
* input (OpenAI's `prompt_tokens`, a Gemini `promptTokenCount`) has already been normalised by
* `toModelUsage` in `@openharness/brain` before it reaches here. The real prompt size a request
* was made with is therefore the three input-side counters summed — the primitive the compaction
* trigger measures against (epic #277, K2).
*/
const ModelUsageSchema = z.object({
	/** Prompt-cache write tokens, priced separately from the uncached input (epic #277, K2). */
	cache_creation_input_tokens: z.number().int().nonnegative(),
	/** Prompt-cache read tokens, priced separately from the uncached input (epic #277, K2). */
	cache_read_input_tokens: z.number().int().nonnegative(),
	/** Uncached input tokens consumed by this request — see the note above. */
	input_tokens: z.number().int().nonnegative(),
	/** Output tokens generated by this request. */
	output_tokens: z.number().int().nonnegative()
});
/**
* // extension: why a model request ended without a normal reply.
*
* Anthropic's `span.model_request_end` only has `is_error`; openharness adds the reason, which
* the client and the session store need in order to tell three different situations apart:
*
* - `interrupted` — a `user.interrupt` cut the request short. The partial text is stored as
*   an `agent.message` and the turn still ends with `end_turn`.
* - `brain_lost` — the brain instance died mid-request. A recovering brain closes the orphaned
*   span with this, then re-runs the turn.
* - `model_error` — the model request failed (the `session.error` that follows carries the
*   detail).
*/
const SpanErrorTypeSchema = z.enum([
	"interrupted",
	"brain_lost",
	"model_error"
]);
/** The `error` of a `span.model_request_end` event. */
const SpanErrorSchema = z.object({
	type: SpanErrorTypeSchema,
	message: z.string().optional()
});
/**
* // extension: one tool result a request had to cap (epic #303, X9; #306).
*
* A tool result is capped before it enters a request — a head and a tail with an omission marker
* between them — to the smaller of the cap its tool declares and the share of the chat model's
* budget no single result may take. The stored `agent.tool_result` is never modified: this is
* what the *request* carried, and what the result cost before and after.
*/
const ToolResultTruncationSchema = z.object({
	/** The `seq` of the `agent.tool_result` whose text was shortened. */
	seq: EventSeqSchema,
	/** The tool the result came from — what the model called, not what it was asked. */
	tool: z.string().min(1),
	/** What the result cost before it was cut, in tokens. */
	tokens_before: z.number().int().nonnegative(),
	/** What the truncated result costs, in tokens. */
	tokens_after: z.number().int().nonnegative()
});
/**
* // extension: what a request had to cut to fit (epic #277, K6; epic #303, X9).
*
* `tokens_before` is what the truncated item cost, `tokens_after` what the truncated form costs,
* and the difference is what the omission marker stands for. Both are the characters-per-token
* estimate — the same measure the budget is checked with — not a provider's report. `seq` names
* the newest item the request had to shorten: the newest message when it had to be capped (K6),
* and otherwise the newest tool result it capped (X9; `results` lists every one of them).
*/
const TruncationSchema = z.object({
	/** The `seq` of the event whose text was shortened — the newest item of the request. */
	seq: EventSeqSchema,
	/** What the item cost before it was cut, in tokens. */
	tokens_before: z.number().int().nonnegative(),
	/** What the truncated item costs, in tokens. */
	tokens_after: z.number().int().nonnegative(),
	/**
	* // extension: the tool results this request capped (epic #303, X9; #306).
	*
	* Absent when no tool result was over its cap, which is every request that called no tool and
	* every log stored before #306 — so a reader that does not know the field sees exactly the
	* record it always did. `seq`/`tokens_before`/`tokens_after` above describe the newest of
	* these when no message had to be capped.
	*/
	results: z.array(ToolResultTruncationSchema).optional()
});
/**
* // extension: the old tool results a request cleared, and what they cost (epic #303, X9; #306).
*
* A tool result older than the verbatim tail of the history — the recent quarter of the chat
* model's budget the compaction engine keeps in full (K4) — is not worth its tokens to a request
* that is running out of room, so its body is replaced by a placeholder before the history is
* summarized. The stored `agent.tool_result` keeps its body: replay, the transcript and the
* summarizer all still see what the tool said.
*/
const ClearedResultsSchema = z.object({
	/** How many tool results had their body replaced by a placeholder. */
	results: z.number().int().nonnegative(),
	/** What those results cost before they were cleared, in tokens. */
	tokens: z.number().int().nonnegative()
});
/**
* // extension: a reminder a request carried besides the history (#331).
*
* The context strategy may put a short block in a request that is not part of the conversation:
* the session's **current todo list** (so the plan survives a summary, which keeps the list only
* as prose), the **stuck reminder** a streak of identical calls earns, or the optional nudge to
* write a list. None of it is appended to the log as a message — it is rebuilt from the log on
* every request — so the span is the only record that the model was told, and `tokens` is what
* that block cost the request (the size accounting counts it, K2).
*/
const RequestReminderSchema = z.object({
	/**
	* Which reminder the request carried: the todo block, or the stuck one a repeating model
	* earns.
	*/
	kind: z.enum(["todo", "stuck"]),
	/** What the block cost the request, in tokens — the characters-per-token estimate (K2). */
	tokens: z.number().int().nonnegative()
});
/**
* // extension: why a request was made, when it is not the chat itself (epic #277, C2).
*
* `summary` is the one value today: a request the compaction engine made to summarize history.
* It exists so the log can tell such a request apart from the chat's own — the size accounting's
* baseline rule reads it (K2) — and it is a union rather than a boolean so a later kind of
* internal request is a new member, not a second field.
*/
const ModelRequestPurposeSchema = z.enum(["summary"]);
/**
* A model request started, and the user events it claims.
*
* Since D9 (issue #46) this event is also the *claim*: the request it opens answers exactly
* the user events its `consumes` lists, so "which user message is still waiting for an answer"
* is a question a reader answers from the log itself, with no mutable `processed_at` column
* behind it. A claim is ordinary event data: an event that is already consumed by one span
* cannot be consumed again, and the store enforces that when the span is appended — atomic and
* fenced like every other write (phase P2a).
*/
const ModelRequestStartEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.modelRequestStart),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/**
	* // extension: the user events this request claims — the `sevt_` ids of the
	* `user.message` / `user.interrupt` events it folds into the request.
	*
	* Optional only so a log stored before D9 (issue #46) keeps validating: those events carry
	* no list, and a reader falls back to the pre-D9 reading (everything queued when the request
	* started was picked up). Every request the brain appends from phase P3 on carries one — and
	* since P4 an interrupt that ends a request is claimed here on the request's
	* `span.model_request_end` instead of by a request of its own. An empty array claims nothing.
	*/
	consumes: z.array(EventIdSchema).optional(),
	/**
	* // extension: the model that served this request, a model id (`provider/model`) —
	* the same spelling as `model.id` on the agent.
	*
	* It is recorded per request rather than read off the agent so that a session which changes
	* models mid-conversation keeps, for every request, the model that actually ran — per-model
	* context budgets and the usage the span end reports are then both attributable. Optional
	* for the D9 transition only, like `consumes` above.
	*/
	model: z.string().min(1).optional(),
	/**
	* // extension: the reasoning effort this request was asked for and ran with (#252).
	*
	* Written when the session's log asks for one: `requested` is the effort the newest
	* effort-carrying `user.message` named, and `applied` is what the request actually ran with —
	* the same level, or `null` when the model takes no effort and the provider's default was
	* used. It is recorded per request, like `model`, so a session that changes its effort
	* mid-conversation keeps, for every request, what that request ran with.
	*
	* Absent when nothing was asked for, which is every request of a session that never set an
	* effort — a reader without the field reads the provider's default, exactly as before.
	*/
	reasoning_effort: ReasoningEffortRunSchema.optional(),
	/**
	* // extension: the mode this request ran under, and the name it had then (#245, M6).
	*
	* Written when the session's mode was applied to the request: `id` is the mode the log
	* points at and `name` is what it was called when the request ran, recorded per request
	* like `model` so that a later rename or edit does not rewrite history. The resolved model
	* the mode produced is in `model` and its effort in `reasoning_effort`, so the two fields
	* beside this one say what the mode resolved to.
	*
	* Absent for a request that ran without a mode — a plain model chat, or one whose mode was
	* gone by the time the request ran.
	*/
	mode: ModeReferenceSchema.optional(),
	/**
	* // extension: the newest message was too big for the model and was shortened (epic #277, K6).
	*
	* Summarizing cannot help when the newest item alone is over the chat model's budget — the
	* newest message has to stay verbatim — so the context strategy caps it to a head and a tail
	* with an omission marker, and records what it cut here so a client can show the user that
	* their message was shortened rather than silently dropped. `seq` is the `seq` of the event
	* that carried the text, and the two token counts are what the item cost before and after.
	*
	* Absent when nothing had to be cut, which is every request whose newest message fits — so a
	* session that never sent an oversized message keeps the span shape it always had.
	*/
	truncated: TruncationSchema.optional(),
	/**
	* // extension: the old tool results this request cleared (epic #303, X9; #306).
	*
	* Before older history is summarized, the bodies of the tool results older than the verbatim
	* tail are replaced by a placeholder — "result cleared, N tokens" — so a context that clearing
	* alone brings back under the threshold needs no summary at all. The results are still in the
	* log, in full; only the request carried the placeholder. Absent when the request cleared
	* none, which is every request without an old tool result.
	*/
	cleared: ClearedResultsSchema.optional(),
	/**
	* // extension: the tools this request offered the model (epic #303, X1).
	*
	* A name and where it comes from, per tool, recorded per request — so the log says what the
	* model *could* have called, not merely what it did. A request built by a deployment with no
	* tool registry offered nothing, and so does one on a model that cannot call tools; both
	* write no `tools` at all, which is every request stored before #304. A request that offered
	* tools and had the model call none still carries the list: the offer is what the span
	* records, whatever came of it.
	*/
	tools: z.array(ToolReferenceSchema).optional(),
	/**
	* // extension: the reminders this request carried besides the history (#331).
	*
	* The session's current todo list, and the stuck reminder a streak of identical calls earns,
	* are rebuilt from the log per request and never stored as messages — this is the only place
	* the log says the model was told. Absent for a request that carried none, which is every
	* request of a session with no list and no streak.
	*/
	reminders: z.array(RequestReminderSchema).optional(),
	/**
	* // extension: a request the compaction engine made to write a summary (epic #277, C2).
	*
	* Marked on the span because a summary request is not part of the chat's own context: its
	* `model_usage` measures the summarizer's prompt, not the chat's, so the size accounting
	* refuses it as a baseline when the next request asks how full the context is (K2). Absent for
	* every ordinary request, and — like `consumes` — optional only so a log stored before the
	* field existed keeps validating.
	*/
	purpose: ModelRequestPurposeSchema.optional()
});
/**
* A model request finished — and, since P4, the claim on any user events the request itself
* answers that its span start did not already list.
*
* `model_request_start_id` points at the matching `span.model_request_start`, which is what
* lets a brain recovering from a crash find and close the span it inherited.
*/
const ModelRequestEndEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.modelRequestEnd),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The `span.model_request_start` this closes. */
	model_request_start_id: EventIdSchema,
	/** Token usage for this request. */
	model_usage: ModelUsageSchema,
	/** Whether the model request resulted in an error. */
	is_error: z.boolean().nullable(),
	/**
	* // extension: why the request failed or was aborted.
	*
	* Written together with `is_error: true`; a request that completed normally has neither.
	* The schema does not enforce that pairing — a consumer that wants the reason should read
	* `error`, and one that only wants a yes/no should read `is_error` — but the server always
	* writes the two consistently.
	*/
	error: SpanErrorSchema.optional(),
	/**
	* // extension: the chunk range this span end replaces (D9, issue #46).
	*
	* A request whose streamed chunks will have no `agent.message` of their own — it was
	* interrupted before storing one, it streamed no text at all, or a recovering brain closes
	* it with `brain_lost` after a crash — supersedes the orphaned chunks here instead. See
	* {@link SupersedesSchema} for the range's meaning and who reads it.
	*/
	supersedes: SupersedesSchema.optional(),
	/**
	* // extension: the user events this span end claims (P4).
	*
	* A `user.interrupt` that cut an open model request short is answered by the request's end,
	* not by a request of its own — no model was called for it — so the interrupt's ids are
	* claimed here, with the same rules and the same atomic, insert-only claim as
	* {@link ModelRequestStartEventSchema}'s `consumes`. The brain writes the list on an
	* `interrupted` end; for every other outcome it is absent. Optional so a log stored before
	* P4 keeps validating.
	*/
	consumes: z.array(EventIdSchema).optional(),
	/**
	* // extension: how many web searches the provider ran inside this request (epic #303, X14;
	* issue #335).
	*
	* A provider-executed search is not one of our calls, so there is no `agent.tool_use` to
	* count: the provider reports the number itself (Anthropic counts them under its usage's
	* `server_tool_use.web_search_requests`, and the others are read from the steps' own
	* reports), and it is written here — per request, on the span that spent it, the same place
	* the tokens are.
	*
	* Absent when the request ran no provider search, which is every request of a log stored
	* before #335 and every request of a chat that cannot search. A `0` is never written: a count
	* of nothing is the absence of the field, the rule the rest of the protocol follows.
	*/
	searches: z.number().int().positive().optional(),
	/**
	* // extension: the request had to be made again without its native search tool (epic #303,
	* X14; issue #335).
	*
	* A provider that does not know the tool it was handed refuses the whole request, and the
	* model's support for it varies by version. The loop drops the tool and retries once — the
	* same request, minus the thing that was refused — and records that here, on the span of the
	* request that was **kept**, so the log says a search was offered and then withdrawn rather
	* than staying silent about it. Absent for every request that was not retried this way.
	*/
	search_rejected: z.literal(true).optional()
});
/** Any stored span event. */
const SpanEventSchema = z.discriminatedUnion("type", [ModelRequestStartEventSchema, ModelRequestEndEventSchema]);
//#endregion
//#region src/tasks.ts
/**
* Background tasks: the vocabulary a session's log, its brain and the tool that starts one all
* agree on (epic #340 decision S4; issue #343).
*
* A background task is work a session starts and does not wait for: starting one returns at
* once, the conversation goes on while it runs, and what it produces reaches the parent model
* as a **notification** rather than as a tool result. The mechanism is deliberately generic —
* a subagent is the first kind of task, and a sandboxed `bash` will be the second — so nothing
* here knows what a task *is*: the kinds are a closed list a later issue widens, and the whole
* of a task's life in the parent's log is three events
* (`session.task_started`, `session.task_status`, `session.task_notification`).
*
* The pieces that are not wire shapes live here too, because three packages have to agree on
* them and none owns them:
*
* - {@link TASK_WAIT_TOOL_NAME} — the one tool whose call ends a turn in a **durable wait**
*   (S5) rather than in a model request, spelled once for the brain (which recognises the call)
*   and for the deployment that registers the tool.
* - {@link TaskWaitInputSchema} and {@link taskWaitTimeoutMs} — what a wait asks for and how
*   long it may last, so the brain and the tool that offers it agree on the bounds.
* - the names and inputs of the subagent tools (S5; #344) — {@link AGENT_TOOL_NAME},
*   {@link AGENT_MESSAGE_TOOL_NAME}, {@link AGENT_STOP_TOOL_NAME}, {@link MESSAGE_PARENT_TOOL_NAME}
*   and their schemas — and the two nesting limits of S6 ({@link DEFAULT_MAX_AGENT_DEPTH},
*   {@link DEFAULT_MAX_LIVE_TASKS}), because the deployment that offers the tools, the harness
*   that answers them and the frontends that render a task's card all have to agree on them.
*/
/** The kinds of background task this build knows (S4). */
const TASK_KINDS = ["subagent"];
/** The kind a `session.task_started` names. */
const TaskKindSchema = z.enum(TASK_KINDS);
/**
* What a task is doing, as the parent's log records it (S4).
*
* - `running` — the task is working. Written when it starts, and again if it is resumed.
* - `idle` — the task's turn ended: what it produced reached the parent as a notification, and
*   nothing is running for it until something asks it to continue.
* - `failed` — the task ended in an error. The notification beside it says so.
* - `stopped` — the task was stopped (a delete, an interrupt of the child, a parent going
*   away). It will not run again.
*
* A status change is written to the **parent's** log as the child changes state, so a client
* draws the card from the parent's log alone; live activity is read from the child's own
* stream, and the child never reports progress.
*/
const TASK_STATUSES = [
	"running",
	"idle",
	"failed",
	"stopped"
];
/** The status a `session.task_status` records. */
const TaskStatusSchema = z.enum(TASK_STATUSES);
/**
* What a notification says happened (S4).
*
* - `result` — the task finished a turn and this is what it produced. The text is the child's
*   own output.
* - `message` — something the task's session said to its parent that is not a finished turn
*   (a question, a note).
* - `failed` — the task's turn ended in an error; the text says what happened.
*/
const TASK_NOTIFICATION_KINDS = [
	"result",
	"message",
	"failed"
];
/** The kind a `session.task_notification` carries. */
const TaskNotificationKindSchema = z.enum(TASK_NOTIFICATION_KINDS);
/**
* The longest `description` a `session.task_started` may carry.
*
* Bounded like every other free-text field a caller sets (`SESSION_TITLE_MAX_LENGTH`,
* `COMPACT_INSTRUCTIONS_MAX_LENGTH`): enough to say what the task is for, short enough that it
* cannot smuggle a second prompt into a card or bloat the log. The notification's `text` is
* deliberately **not** bounded — it is a task's output, as unbounded as a tool result.
*/
const TASK_DESCRIPTION_MAX_LENGTH = 500;
/**
* The longest `source_key` a notification may carry.
*
* The key is what makes delivery exactly once (S4): the parent's append is idempotent on it, so
* it is an identifier written by the task's own side — `<child_session_id>:<idle seq>` for a
* subagent — and not free text. Bounded so a log row cannot be made large by it.
*/
const TASK_SOURCE_KEY_MAX_LENGTH = 200;
/**
* The name of the tool whose call is a **durable wait** (S5): `agent_wait { task_ids?,
* timeout_s? }`.
*
* It is spelled once because two sides have to agree on it and neither owns it: the brain
* recognises a call of this name and ends the turn `waiting_on_tasks` instead of running
* anything, and the deployment registers the tool the model is offered. Its `run` is never
* called — a wait is not something a tool executes, exactly as `ask_user`'s answers are not.
*/
const TASK_WAIT_TOOL_NAME = "agent_wait";
/** How long a wait lasts when the call does not ask for a time (S5): ten minutes. */
const TASK_WAIT_DEFAULT_TIMEOUT_MS = 6e5;
/** The longest a wait may last (S5): one hour, whatever the call asks for. */
const TASK_WAIT_MAX_TIMEOUT_MS = 36e5;
/**
* What an `agent_wait` call carries (S5): the tasks to wait for, and how long to wait.
*
* Both are optional and both absences mean something. No `task_ids` waits for **any** task's
* notification — "tell me when anything I started finishes" — and no `timeout_s` takes
* {@link TASK_WAIT_DEFAULT_TIMEOUT_MS}. The bounds are the brain's to apply
* ({@link taskWaitTimeoutMs}), so a call asking for a week waits an hour rather than holding a
* session for one.
*/
const TaskWaitInputSchema = z.object({
	/** The tasks this wait is for; absent or empty means any task of this session. */
	task_ids: z.array(TaskIdSchema).optional(),
	/** How long to wait, in seconds; absent means the default. */
	timeout_s: z.number().optional()
});
/**
* What is wrong with an `agent_wait` call's arguments, or `[]` when they are a wait.
*
* The check the brain answers a malformed call with, so a wait on arguments nobody can honour
* is a result the model learns from rather than a turn that pauses on it — the rule
* `ask_user`'s questions follow (`askUserInputProblems`). Written against the call's raw
* arguments, which is what the log holds.
*/
function taskWaitInputProblems(value) {
	if (typeof value !== "object" || value === null || Array.isArray(value)) return ["the arguments must be an object: an optional `task_ids` list and an optional `timeout_s`"];
	const problems = [];
	const { task_ids: taskIds, timeout_s: timeout } = value;
	if (taskIds !== void 0) {
		if (!Array.isArray(taskIds) || !taskIds.every((id) => TaskIdSchema.safeParse(id).success)) problems.push("`task_ids` must be a list of `task_` ids");
	}
	if (timeout !== void 0 && (typeof timeout !== "number" || !Number.isFinite(timeout))) problems.push("`timeout_s` must be a number of seconds");
	return problems;
}
/**
* Parse an `agent_wait` call's arguments, or `null` when they are not a wait.
*
* The one reading the brain and the tool's own input schema share, so "what does this call ask
* for?" has one answer. A call {@link taskWaitInputProblems} refuses is not a wait: the model is
* told what was wrong with it and asks again, rather than the turn pausing on something nobody
* can honour (the rule `ask_user`'s questions follow).
*/
function parseTaskWaitInput(value) {
	if (taskWaitInputProblems(value).length > 0) return null;
	const parsed = TaskWaitInputSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}
/**
* How long a wait lasts: what the call asked for, capped (S5).
*
* A call that asked for nothing waits {@link TASK_WAIT_DEFAULT_TIMEOUT_MS}, a call that asked
* for more than {@link TASK_WAIT_MAX_TIMEOUT_MS} waits that, and a call that asked for
* something that is not a positive number waits the default — a wait of zero would be a pause
* that resolves as soon as it is taken, which is not what any model means by asking for one.
*/
function taskWaitTimeoutMs(input) {
	const asked = input?.timeout_s;
	if (typeof asked !== "number" || !Number.isFinite(asked) || asked <= 0) return TASK_WAIT_DEFAULT_TIMEOUT_MS;
	return Math.min(Math.round(asked * 1e3), TASK_WAIT_MAX_TIMEOUT_MS);
}
/**
* The name of the tool that starts a helper (S5): `agent { type, description, prompt }`.
*
* It is the one tool that starts a task, and its result is the handle — so it is spelled once
* for the deployment that offers it, the brain that answers it, and the frontends that render
* the card it produces. A call to it is not run by `@openharness/hands`: the harness itself
* creates the child, which is why the name is here rather than in a tool's own module.
*/
const AGENT_TOOL_NAME = "agent";
/** The name of the tool that sends a running or finished helper a message (S5): `agent_message`. */
const AGENT_MESSAGE_TOOL_NAME = "agent_message";
/** The name of the tool that stops a helper (S5): `agent_stop`. */
const AGENT_STOP_TOOL_NAME = "agent_stop";
/**
* The name of the tool a **child** sends its parent a note with (S5): `message_parent`.
*
* The one tool only a child is offered — the other four belong to the parent side — and it does
* not block: it writes a `message` notification and returns.
*/
const MESSAGE_PARENT_TOOL_NAME = "message_parent";
/**
* What an `agent` call carries (S5): which type to start, what for, and the prompt.
*
* `type` is a name the server's type resolver knows (`general-purpose`, or one of the owner's
* modes with a description, S2) and is deliberately **not** checked against a closed list here:
* the list is the owner's own and moves with their modes, so "is this a type" is the resolver's
* answer (`{ ok: false, message }`), which the parent model reads and acts on.
*
* `description` is what the task is for — it is the `description` of the `session.task_started`
* the call writes and what a card shows — and is bounded like every other free text a caller
* sets (`TASK_DESCRIPTION_MAX_LENGTH`). `prompt` is not: it becomes the child's first
* `user.message`, as unbounded as any message.
*/
const AgentToolInputSchema = z.object({
	/** The type of helper to start: a mode's name, or `general-purpose`. */
	type: z.string().min(1),
	/** What the helper is for, in a sentence; the task's description. */
	description: z.string().min(1).max(500),
	/** What the helper is asked to do — its first `user.message`, and its whole context. */
	prompt: z.string().min(1)
});
/**
* What an `agent_message` call carries (S5): the task to steer, and what to say.
*
* The text is a `user.message` in the child — it steers a running child, and starts a new turn
* on one that has already reported, with its context intact — so it is unbounded like any other
* message.
*/
const AgentMessageToolInputSchema = z.object({
	/** The task to send the message to; its `task_` id, as the `agent` call answered it. */
	task_id: TaskIdSchema,
	/** What to tell the child. */
	text: z.string().min(1)
});
/** What an `agent_stop` call carries (S5): the task to interrupt. */
const AgentStopToolInputSchema = z.object({ 
/** The task to stop; its `task_` id. */
task_id: TaskIdSchema });
/**
* What a child's `message_parent` call carries (S5): a note to the parent.
*
* One field, because there is nothing else to say about it: the parent is the session's own
* (`Session.parent_session_id`) and the task is its own `parent_task_id`, so a child cannot
* address anybody else. The text is delivered as a `message` notification, framed the way a
* result is.
*/
const MessageParentToolInputSchema = z.object({ 
/** What to tell the parent. */
text: z.string().min(1) });
/**
* How deep a tree of helpers may get when the deployment says nothing (S6): a top-level chat
* (depth 0), its children (1) and their children (2) are offered the tools that start one; a
* session at depth 3 is not.
*/
const DEFAULT_MAX_AGENT_DEPTH = 3;
/**
* How many descendants of one root chat may be live at once when the deployment says nothing
* (S6): ten. Past it an `agent` call is answered with an `is_error` the model can act on rather
* than starting anything.
*/
const DEFAULT_MAX_LIVE_TASKS = 10;
//#endregion
//#region src/events/task.ts
/**
* The background tasks a session started: what it started (epic #340 decision S4; issue #343),
* what each one is doing, and what reached the parent model from it.
*
* A background task is work a session does not wait for. Starting one returns at once — the
* tool's result is a handle — and the conversation goes on while it runs, so the parent model
* learns what happened through the log rather than through a call that blocked. Three events
* carry the whole of a task's life in the **parent's** log:
*
* ```
*   session.task_started    { task_id, kind, tool_use_id?, child_session_id?, task_type?,
*                             description }
*   session.task_status     { task_id, status }        running | idle | failed | stopped
*   session.task_notification { task_id, kind, text, source_key }   queued, claimed
* ```
*
* The split is the epic's: a client draws a task's card from the parent's log alone
* (`task_started` names it, `task_status` says what it is doing), live activity is read from
* the **child's** own stream, and the only thing that reaches the parent model is a
* notification.
*
* A notification is the one **queued** event of the three — its `processed_at` is `null` until
* a turn claims it, exactly as a `user.*` event's is — because it is addressed to the model
* rather than being a fact about the session: it is delivered by the parent's brain (folded
* into the next model request, or starting a turn on an idle session, or resolving a wait),
* and never placed between a tool call and its result. What it carries is another session's
* output, so it reaches the model framed as data, never as an instruction — see
* [`docs/threat-model.md`](../../../docs/threat-model.md).
*
* Anthropic has no equivalent: its API has no background task, no way for a child session to
* reach its parent, and no event a client draws a running task from.
*/
/**
* // extension: a session started a background task and did not wait for it (S4).
*
* `kind` is what kind of task it is ({@link TASK_KINDS} — `subagent` today, and a sandboxed
* `bash` later), and `child_session_id` is the session the task runs in, when it has one. A
* task that is not a session of its own — a subprocess, a scheduled job — carries none, which
* is why the field is optional rather than the task being required to have one.
*
* `task_type` is the kind's own sub-kind when it has one (a subagent's type, #342), and
* `description` is what the task is for, written for a reader rather than for the model. Both
* are the starter's to say; the brain writes neither — a task is started by the tool that
* starts it (S4's "the tool's result is the handle"), through the deployment's own append.
*
* `tool_use_id` is the **call** that started the task: the id of the `agent.tool_use` whose
* result was the handle (the event's id *is* the call's id, X1), so a reader places a task's
* card at the exact call it belongs to instead of guessing by `description` (#345). It is
* optional because not every task kind is started by a tool call of this log — a future
* background kind may be started by something else — and because a log stored before the field
* existed carries none; a reader that finds none falls back to matching by `description`.
*
* The epic spells the sub-kind `type` (S4); it is `task_type` here because a stored event's
* `type` is its own discriminant, and two fields of one object cannot share a name.
*/
const SessionTaskStartedEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal("session.task_started"),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The task's id, `task_`-prefixed: what its statuses and notifications name. */
	task_id: TaskIdSchema,
	/** What kind of background task it is. */
	kind: TaskKindSchema,
	/**
	* The tool call that started it — the id of the `agent.tool_use` whose result was the handle
	* — when a tool call started it. Absent for a kind nothing of this log called, and for a log
	* stored before the field existed.
	*/
	tool_use_id: EventIdSchema.optional(),
	/**
	* The session the task runs in, when it has one. Written so a client can follow the task's
	* live activity: the child's own stream is where its progress is, and the parent's log only
	* says what it is doing.
	*/
	child_session_id: SessionIdSchema.optional(),
	/** The kind's own sub-kind (a subagent's type, #342), when the starter named one. */
	task_type: z.string().min(1).optional(),
	/** What the task is for, in the starter's words: a card's line, never a prompt. */
	description: z.string().min(1).max(500)
});
/**
* // extension: the task's state changed (S4).
*
* Written to the **parent's** log as the task changes state — `running` when it starts and
* whenever it is resumed, `idle` when its turn ends (its notification follows), `failed` when
* it ends in an error, `stopped` when it is stopped — so a client draws a task's card, and
* tells a finished task from a running one, from the parent's log alone. The task itself does
* not report progress: what it is doing *now* is on its own stream.
*
* The newest status of a task is its state, so replay answers "what is this task doing?" with
* a fold rather than with a flag. A log that holds a `task_started` and no status yet reads as
* a task that has just started.
*/
const SessionTaskStatusEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal("session.task_status"),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The task this is a status of. */
	task_id: TaskIdSchema,
	/** What the task is doing now. */
	status: TaskStatusSchema
});
/**
* // extension: something reached the parent model from a task (S4).
*
* The one way anything gets from a task to the model it belongs to. It is a **queued** event:
* stored with `processed_at: null` and claimed by the parent's brain the way a `user.message`
* is — by the `consumes` list of the event that answers it — so "has the model been told?"
* is answered by the log rather than by a flag, and a crash between the append and the claim
* loses nothing. It is never placed between a tool call and its result: the brain claims it
* where it claims steering, and where a wait is resolved it *becomes* the wait's result.
*
* **Delivery is exactly once** (S4). `source_key` is the task side's own key for this
* notification — `<child_session_id>:<idle seq>` for a subagent, so the same finished turn
* cannot be announced twice however many times a recovery path runs — and the append is
* idempotent on it: a second append naming a key this session's log already holds is a no-op
* that changes nothing. That is what makes a crash plus a re-delivery neither a loss nor a
* duplicate.
*
* To the model it is a clearly framed user-role message naming the task, and its `text` is
* **data, not instructions**: a task's output can carry what that session read from the web or
* from an MCP server, so the frame says so (X11).
*/
const SessionTaskNotificationEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal("session.task_notification"),
	seq: EventSeqSchema,
	processed_at: QueuedProcessedAtSchema,
	/** The task this came from; it names a `session.task_started` in the same log. */
	task_id: TaskIdSchema,
	/** What happened: a finished turn's output, a message, or a failure. */
	kind: TaskNotificationKindSchema,
	/**
	* The text itself — a task's output, framed as data when the model is told it. Unbounded on
	* purpose: it is what a child produced, and the caps that keep a *request* small
	* (epic #303, X9) are the request's business, not the log's.
	*/
	text: z.string(),
	/** The task side's key for this notification; a second append with the same one is a no-op. */
	source_key: z.string().min(1).max(200)
});
/** Any stored background-task event. */
const TaskEventSchema = z.discriminatedUnion("type", [
	SessionTaskStartedEventSchema,
	SessionTaskStatusEventSchema,
	SessionTaskNotificationEventSchema
]);
//#endregion
//#region src/events/session.ts
/**
* Events the session itself emits: status transitions, errors, the running usage of the turn
* (epic #245, A2), the rewind that restarts the conversation from an earlier message (#238) and
* the summary that replaces older history for the model (epic #277, K1; #278).
*
* They bracket a turn — `session.status_running` opens it, `session.status_idle` closes it —
* so replaying just these events gives the session's state at any point in the log. A
* `session.rewind` is the one member a client asks for rather than the brain writing it, and
* the one that changes what the log *means* rather than what the session is doing: it is a
* statement about the transcript, not about a turn. A `session.context_summary` is the other
* kind of statement: it changes nothing about the log or the transcript, and only says what the
* *model* is told about the history it no longer sees. `session.context_summary_progress` is the
* same statement while it is being made — one event per pass of the compaction engine — and says
* nothing beyond which pass is starting. A `session.compact` (#283) is the second member a client
* asks for rather than the brain writing it — `/compact [instructions]` — and the
* `session.compaction` that follows it is the brain's stored answer, whether or not a summary
* came of it.
*/
/**
* Why the agent stopped.
*
* `end_turn` is what a turn that finishes on its own *and* a turn cut short by a
* `user.interrupt` both report: there is no stop reason specific to interruption. It is also
* what a turn that ran out of tool steps reports — that one says so in the `session.error`
* beside it, the one event that carries a sentence (X1/X2).
*
* `requires_action` is the pause (epic #303, X6; #309): the turn ended because a call is
* waiting on the user, and `event_ids` names the calls — the ids of the `agent.tool_use`
* events, which are the calls' own ids. The session is idle, so a client shows the question
* or the approval prompt; when the user answers, one `user.tool_confirmation` starts the turn
* that carries on. Anthropic's union also has `retries_exhausted` and `budget_reached`, which
* openharness does not emit (see `AGENTS.md`).
*
* `waiting_on_tasks` is the **task wait** (epic #340, S5; #343): the turn ended because the
* model asked to wait for background tasks, and `task_ids` names them. It is the same kind of
* pause as `requires_action` — written down, holding no lease and no slot, resumed by whatever
* answers it — but what it waits for is not the user: it resumes on a
* `session.task_notification` for one of the tasks, on its `deadline`, or on a `user.message`
* (which resolves it exactly as a message resolves a user pause). `task_ids` is empty for a
* wait that did not name any task — "tell me when anything I started finishes".
*/
const StopReasonSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("end_turn") }),
	z.object({
		type: z.literal("requires_action"),
		/**
		* The `agent.tool_use` events the turn is waiting on, in the order they were made —
		* the calls' own ids, which a `user.tool_confirmation` names to answer one.
		*/
		event_ids: z.array(EventIdSchema)
	}),
	z.object({
		type: z.literal("waiting_on_tasks"),
		/**
		* The tasks the turn waits for, or an empty list for a wait that named none — anything
		* this session started may resolve it.
		*/
		task_ids: z.array(TaskIdSchema),
		/**
		* When the wait gives up, as a timestamp: a wait always has one (S5 — ten minutes by
		* default, an hour at most), because it is what the recovery scan and the scheduler's
		* timer read to resume a session nothing else will reach.
		*/
		deadline: TimestampSchema
	})
]);
/** The agent is actively working. Emitted at the start of every turn. */
const SessionStatusRunningEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionStatusRunning),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema
});
/** The agent finished its turn and is waiting for input. Closes every turn. */
const SessionStatusIdleEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionStatusIdle),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	stop_reason: StopReasonSchema,
	/**
	* // extension: the user events this turn end claims (P4).
	*
	* A `user.interrupt` that arrived with no model request running — no brain had started a
	* turn, or the interrupt landed between requests — is answered by the turn ending: the
	* `session.status_idle` that closes the turn claims its ids, so it reads processed and is
	* not reached again. The claim rules are {@link ModelRequestStartEventSchema}'s `consumes`
	* (atomic, insert-only, refused whole on a conflict). The brain writes the list when it
	* ended the turn on an interrupt; a turn that ends on its own carries none. Optional so a
	* log stored before P4 keeps validating.
	*/
	consumes: z.array(EventIdSchema).optional()
});
/**
* A transient error occurred and the session is retrying automatically.
*
* Always preceded by a `session.error` and followed by a `session.status_running`.
*/
const SessionStatusRescheduledEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionStatusRescheduled),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema
});
/**
* What a client should do about a `session.error`, from Anthropic's
* `BetaManagedAgentsRetryStatus*` union.
*/
const RetryStatusTypeSchema = z.enum([
	"retrying",
	"exhausted",
	"terminal"
]);
/** `retry_status`: an object with a `type`, exactly as Anthropic shapes it. */
const RetryStatusSchema = z.object({ type: RetryStatusTypeSchema });
/** The error kinds a `session.error` can carry, from Anthropic's union. */
const SessionErrorTypeSchema = z.enum([
	"unknown_error",
	"model_overloaded_error",
	"model_rate_limited_error",
	"model_request_failed_error",
	"mcp_connection_failed_error",
	"mcp_authentication_failed_error",
	"billing_error",
	"credential_host_unreachable_error",
	"missing_provider_credential",
	"tool_steps_exhausted_error",
	"tool_loop_stuck_error",
	"recovery_exhausted_error"
]);
/** The `error` object of a `session.error` event. */
const SessionErrorSchema = z.object({
	type: SessionErrorTypeSchema,
	message: z.string(),
	retry_status: RetryStatusSchema
}).refine((error) => error.type !== "missing_provider_credential" || error.retry_status.type === "exhausted", { error: "missing_provider_credential is never retried: retry_status must be `exhausted` (epic #65, A5)" }).refine((error) => error.type !== "recovery_exhausted_error" || error.retry_status.type === "terminal", { error: "recovery_exhausted_error is terminal: recovery was already attempted, so retry_status must be `terminal` (#378, P4)" });
/**
* Something went wrong during the turn.
*
* A retryable error is followed by `session.status_rescheduled` and then a fresh
* `session.status_running`. When the retries run out, or the error was never retryable, the
* turn ends with `session.status_idle { stop_reason: { type: "end_turn" } }`.
*/
const SessionErrorEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionError),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	error: SessionErrorSchema
});
/**
* // extension: the session this stream was following was deleted (#111, epic #116 U5).
*
* **Stream-only.** A `DELETE /v1/sessions/{session_id}` removes the session and its whole
* log, so there is nowhere to store this event: it names the session that is gone and is
* never in it. A server sends it as the **last** event on every open stream for the session
* and closes the stream after it, so a subscriber learns the session was deleted — an end
* state — instead of reconnecting to a session that no longer exists. It carries no `seq`
* and no envelope: it is not a position in a log.
*
* Anthropic has no equivalent: session deletion is an openharness extension. Note it is the
* one event type in {@link EVENT_TYPES} that is not in {@link STORED_EVENT_TYPES}.
*/
const SessionDeletedEventSchema = z.object({
	type: z.literal(EVENT_TYPES.sessionDeleted),
	/** The deleted session's id — the one the stream was following. */
	session_id: SessionIdSchema
});
/**
* // extension: the session restarts from an earlier `user.message` (#238).
*
* Editing a message never changes what is stored: this event supersedes the tail of the log
* from the message the reader is editing, and the edited text follows it as an ordinary
* `user.message`. `supersedes.from_seq` is that message's `seq` and `supersedes.to_seq` the
* last event before this one, so the range runs to the end of the log as it stood — see
* {@link SupersedesSchema} for what a range means and who reads it. Replay and compaction
* treat the whole range as gone: a reader that loads the session later never sees the
* messages it replaced, and a client that was already rendering them drops them when it
* receives this event (it is never itself superseded, so it is always delivered).
*
* The event is the server's, not the brain's, and like the status events it takes effect in
* the append that writes it: it is stored with its `processed_at` set and is not claimed by
* any span. A session accepts one only while it is idle — a turn in flight would be appending
* into the range it just lost — which the server enforces (a 409).
*
* Anthropic has no equivalent: editing a sent message is an openharness extension.
*/
const SessionRewindEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionRewind),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/**
	* The range this rewind replaces: from the edited `user.message` to the last event of the
	* log as it stood. Both ends are `seq`s of this session, and `to_seq` is always the `seq`
	* this event's own `seq` follows — a rewind restarts from a message through the end of what
	* has been written, never a window in the middle.
	*/
	supersedes: SupersedesSchema
});
/**
* Why the older history was summarized (epic #277, K1; #278).
*
* - `threshold` — the context reached the share of the chat model's budget the settings allow,
*   so the compaction engine summarized before the provider would have refused the request (K2).
* - `overflow` — the provider refused a request as too long, so the engine summarized with
*   tighter caps and retried once (K2).
* - `manual` — the user asked for it (`/compact`, K8).
*/
const ContextSummaryReasonSchema = z.enum([
	"threshold",
	"overflow",
	"manual"
]);
/**
* The part of the log a summary replaces **for the model** (epic #277, K1; #278).
*
* `to_seq` is the last event the summary covers, inclusive: the context strategy hands the model
* the summary and then every event **after** it. Nothing here is a supersession — the log, the
* transcript and replay keep the history whole; the range says only where the model is told to
* start reading. `seq`-shaped rather than an event id, because it is a position in the log.
*/
const ContextSummaryCoversSchema = z.object({ 
/** The `seq` of the last event the summary replaces for the model, inclusive. */
to_seq: EventSeqSchema });
/**
* // extension: a summary of the older history, written by the brain (epic #277, K1; #278).
*
* When a chat's context fills, the brain summarizes the older messages and continues from the
* summary plus the recent messages verbatim, instead of dropping the oldest messages (which is
* what `trimToBudget` in `@openharness/brain` did before this). The summary is an **event**, and
* it supersedes **nothing**: the log stays append-only, the transcript and replay still show the
* full history, and the only reader of this event is the context strategy — what the model sees
* is the system prompt, then the latest non-superseded summary, then every event after
* `covers.to_seq`.
*
* A summary is written by the compaction engine (`packages/brain`, epic #277 C2), never by a
* client: like `session.usage` it is the brain's own bookkeeping, and it is not queued and never
* claimed. A later summary replaces an earlier one simply by being newer; a `session.rewind`
* that reaches back before this event supersedes it along with the rest of the tail it replaced,
* so it disappears from the strategy's reading the way everything the edit took back does.
*
* Anthropic has no equivalent: its API has no server-side brain to summarize with, and no
* compaction event that leaves the transcript intact.
*/
const ContextSummaryEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionContextSummary),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The summary text: what the model is told instead of the events `covers` names. */
	summary: z.string(),
	/** The last event the summary replaces for the model, inclusive. */
	covers: ContextSummaryCoversSchema,
	/** Why the summary was made (K2/K8), recorded so a reader can tell them apart. */
	reason: ContextSummaryReasonSchema,
	/**
	* The context size when the summary was made, in tokens, measured on the chat model before the
	* summary replaced anything. Recorded so the log says how full the context was when it happened
	* (K10's meter reads it back).
	*/
	tokens_before: z.number().int().nonnegative(),
	/** The `provider/model` that wrote the summary — the summary model, or the chat model. */
	summary_model: z.string().min(1),
	/** The version of the summary prompt that produced the text (K7), so a later change is visible. */
	prompt_version: z.string().min(1),
	/** How many passes the summary took (K5; the pass limit is a user preference). */
	passes: z.number().int().positive(),
	/**
	* Why the chat model summarized instead of the summary model the user chose (K3/K5).
	*
	* Written only when a fallback happened — no credential for the chosen summary model, or the
	* chosen model would have needed more passes than the limit allows — and absent when the chosen
	* model did the work.
	*/
	fallback_reason: z.string().optional()
});
/**
* // extension: the compaction engine started another pass (epic #277, C2; #279).
*
* A summary of a big history takes more than one model call — the engine folds the older
* history in slices, each pass updating the running summary — and this event says which pass is
* starting, of how many the plan holds. It is **stored**, not stream-only, because of D9: every
* fact a client is shown lives in the log, so a client that reconnects mid-compaction (or reads
* the session later) sees the same progress the live stream carried. The one exception in the
* codebase stays `session.deleted`.
*
* It is the brain's bookkeeping, like `session.usage` and `session.context_summary`: not queued,
* never claimed, and read by no part of the brain — the context strategy ignores it and the
* transcript does not show it. A reader that wants it (a progress bar) reads the newest one
* between a summary request's spans. Deleting the stale ones is follow-up work (#285).
*
* Anthropic has no equivalent: it has no server-side brain, and so nothing to report progress
* about.
*/
const ContextSummaryProgressEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionContextSummaryProgress),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** Which pass is starting, counting from 1. */
	pass: z.number().int().positive(),
	/**
	* How many passes the plan holds for the model doing the work. A plan ahead of the pass count
	* is normal — the engine computes the slices before it calls the model — and the two are equal
	* on the last pass.
	*/
	passes: z.number().int().positive()
});
/**
* The longest `instructions` string a manual compaction may carry (epic #277, K8; #283).
*
* Bounded like every free-text field a client may set (`SESSION_TITLE_MAX_LENGTH`,
* `METADATA_MAX_VALUE_LENGTH`): enough for the guidance the epic names — "keep the API decisions
* in detail" — with room for a paragraph or two of specifics, and short enough that it cannot be
* used to smuggle a second context into the summarizer's prompt or bloat the log. The route
* refuses anything longer with the protocol's 400, so the bound is checked once, before an event
* is written.
*/
const COMPACT_INSTRUCTIONS_MAX_LENGTH = 2e3;
/**
* // extension: the user asked for a manual compaction (`/compact [instructions]`; epic #277, K8; #283).
*
* The `instructions` are the reader's guidance for the summary — "keep the API decisions in
* detail" — or `null` when they asked for a plain compaction. The brain adds them to the
* summarizer's prompt as the user's own instruction and echoes them on the
* {@link SessionCompactionEvent} it answers with, so the log says what was asked and whether
* guidance was used. A request already waiting for an answer is not appended again: a second
* `POST …/compact` before the first is handled is the same request.
*
* Anthropic has no equivalent: it has no server-side brain and no user-triggered compaction.
*/
const SessionCompactEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionCompact),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The user's guidance for the summary, or absent for none. */
	instructions: z.string().min(1).max(COMPACT_INSTRUCTIONS_MAX_LENGTH).optional()
});
/**
* A `session.compact` as a client sends it: just the optional instructions.
*
* The stored event's `id`, `seq` and `processed_at` are the server's, so the input carries only
* what the caller owns — the same split `session.rewind`'s input makes (#238).
*/
const SessionCompactEventInputSchema = z.object({
	type: z.literal(EVENT_TYPES.sessionCompact),
	instructions: z.string().min(1).max(COMPACT_INSTRUCTIONS_MAX_LENGTH).optional()
});
/**
* What came of a manual compaction (epic #277, K8; #283).
*
* - `summarized` — a `session.context_summary` with reason `manual` was written; the chat
*   continues from the summary plus the recent history.
* - `nothing_to_summarize` — there was no older history the engine could cut away (a short
*   chat, or a history with nothing before the newest turn). A clear outcome rather than a
*   silent no-op.
* - `failed` — the summarizer failed (a model error, an interrupt, an empty answer). The chat
*   carries on with the strategy's trimming, exactly as K11 says.
*/
const SessionCompactionOutcomeSchema = z.enum([
	"summarized",
	"nothing_to_summarize",
	"failed"
]);
/**
* // extension: the answer to a manual compaction request (epic #277, K8; #283).
*
* Written by the brain once it has handled the newest pending `session.compact`, whether or not
* a summary came of it. It is the "clear, stored outcome" the clients show, and — by being the
* event that follows the request — it is what makes the request no longer pending, so the log
* alone answers "is a manual compaction waiting?". `instructions` echoes the request's guidance
* when it had any, which is how the log records that the user's guidance was used; `summary_seq`
* points at the `session.context_summary` a `summarized` outcome wrote; `message` explains a
* `nothing_to_summarize` or `failed` outcome in the reader's terms.
*
* It supersedes nothing and is not queued: like `session.usage` it is the brain's own
* bookkeeping, and no part of the brain reads it back except the pending check above.
*/
const SessionCompactionEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionCompaction),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** What came of the compaction. */
	outcome: SessionCompactionOutcomeSchema,
	/** The request's guidance, echoed when it carried any, so the log records it was used. */
	instructions: z.string().min(1).max(COMPACT_INSTRUCTIONS_MAX_LENGTH).optional(),
	/** For `summarized`, the `seq` of the `session.context_summary` that was written. */
	summary_seq: EventSeqSchema.optional(),
	/** For `nothing_to_summarize` or `failed`, a sentence a client can show. */
	message: z.string().min(1).optional()
});
/**
* One model's running total for a session (epic #245, A2; issue #247).
*
* The tokens of every request that ran on this model, summed over the session so far. It carries
* the model id because a session's totals are only priceable per model — a session that switched
* providers mid-conversation ran some requests at one set of rates and some at another.
*/
const SessionModelUsageSchema = z.object({
	/** The `provider/model` these requests ran on. */
	model: z.string().min(1),
	/** What they reported, summed. */
	usage: ModelUsageSchema,
	/**
	* How many requests ran on this model so far — at least one, since an entry exists because a
	* request named it.
	*
	* The count is what lets a reader of the running totals price them per model the way the usage
	* routes do: a model nobody publishes a price for contributes `requests` unpriced requests, and
	* a priced one contributes none (#247). It is a fact about the log, not about money — no cost
	* is stored — so it stays true whatever the catalog's prices turn out to be.
	*/
	requests: z.number().int().positive()
});
/**
* // extension: the session's running totals, written after a model request (epic #245, A2).
*
* Anthropic has the event — a `session.usage` snapshot of the session's cumulative usage and its
* tracked list cost — and openharness keeps its name and its placement in the log. Three things
* differ, all of them deliberate:
*
* - **It is written after every model request, not once per idle.** Anthropic emits one
*   immediately before the session goes idle, whatever the stop reason. openharness's client
*   shows the session's cost as the turn runs, and a request that finishes mid-turn is exactly
*   the moment the number moved, so the event is written there. Fewer events would be cheaper
*   and a client would have to derive the same totals from the spans it already reads.
* - **It carries no cost.** Anthropic stamps `list_cost` (platform-computed and stored) onto the
*   snapshot; here cost is computed when it is read, from the tokens below and the model
*   catalog's prices, and is never written into the log (epic #245). The tokens are what is
*   stored, and they are the part that cannot be recovered from anywhere else.
* - **It breaks the totals down by model.** Anthropic's snapshot is flat — a session there runs
*   one model — while an openharness session may switch models mid-conversation (epic #116, U3),
*   and its tokens can only be priced one model at a time.
*
* The totals are **cumulative over the whole session**, not per request: a reader that wants
* what the last request cost subtracts the previous event's totals, and one that wants the
* session's cost reads the newest event alone. `models` breaks the same totals down, and the
* four counters beside it are their sum — the schema refuses a snapshot where the two disagree.
*
* A session stored before this event existed has none, and its usage is derived on read from the
* `span.model_request_end` events it does have: the running total an event carries is exactly
* what a fold over those spans produces, so a log replay answers the same numbers a live stream
* does, whichever of the two a client is reading.
*/
const SessionUsageEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionUsage),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** Input tokens every request of this session reported, summed. */
	input_tokens: z.number().int().nonnegative(),
	/** Output tokens every request of this session reported, summed. */
	output_tokens: z.number().int().nonnegative(),
	/** Prompt-cache write tokens, summed. */
	cache_creation_input_tokens: z.number().int().nonnegative(),
	/** Prompt-cache read tokens, summed. */
	cache_read_input_tokens: z.number().int().nonnegative(),
	/** The same totals per model, in `model` order. */
	models: z.array(SessionModelUsageSchema)
}).refine((event) => event.models.reduce((sum, entry) => sum + entry.usage.input_tokens, 0) === event.input_tokens && event.models.reduce((sum, entry) => sum + entry.usage.output_tokens, 0) === event.output_tokens && event.models.reduce((sum, entry) => sum + entry.usage.cache_creation_input_tokens, 0) === event.cache_creation_input_tokens && event.models.reduce((sum, entry) => sum + entry.usage.cache_read_input_tokens, 0) === event.cache_read_input_tokens, {
	error: "the totals must be the sum of `models`: the breakdown is the same tokens",
	path: ["models"]
});
/**
* // extension: the instructions a slash invocation loaded (epic #349, K7).
*
* `/name arguments` sends a `user.message` that names a skill; when the brain first handles that
* message it writes this event, and from then on this event — never the current snapshot — is
* what the chat ran on. `content` is the skill's **body** with the arguments substituted, so the
* log holds exactly what the model was given, and `commit_sha` names the snapshot it came from
* (K5: a refresh stores a new commit under the same id, and the chats that already ran say which
* one they ran). `message_seq` is the `user.message` this belongs to, which is how the context
* builder knows to render that message as instructions rather than as the text the user typed.
*
* The brain writes it, like `session.usage` and `session.context_summary`: it is a fact about a
* request the brain made, not something a client may append. It is written **once** per invoked
* message — the "first handles" in K7 — so a resume, a replay or a second brain appends nothing
* and the body a chat was built on cannot change under it.
*
* The event carries no `name`-to-snapshot pointer a reader could re-resolve: `skill_id` is here
* so a client can link back to the skill, and the body is here so nothing has to.
*/
const SessionSkillLoadedEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.sessionSkillLoaded),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	/** The `seq` of the `user.message` that invoked the skill. */
	message_seq: EventSeqSchema,
	/** The skill's name, as the message named it. */
	name: SkillNameSchema,
	/** The `skl_` id of the skill whose snapshot was read. */
	skill_id: SkillIdSchema,
	/** The commit the snapshot was cloned at, or `null` for a snapshot nothing cloned (K4). */
	commit_sha: z.string().nullable(),
	/** The body the model was given: the skill's instructions, arguments substituted in. */
	content: z.string()
});
/**
* A `session.rewind` as a client sends it: the message the session should restart from.
*
* The input names `from_seq` alone. How far the restart reaches is not the caller's to say:
* a rewind always covers through the end of the log, and the store records that end — the
* `seq` the rewind event itself follows — in the `supersedes` range of the stored event, in
* the same append, so there is no window in which the caller's idea of the log's end and the
* store's could differ.
*/
const SessionRewindEventInputSchema = z.object({
	type: z.literal(EVENT_TYPES.sessionRewind),
	/** The `seq` of the `user.message` the session restarts from, inclusive. */
	from_seq: EventSeqSchema
});
/** Any stored session event. */
const SessionEventSchema = z.discriminatedUnion("type", [
	SessionStatusRunningEventSchema,
	SessionStatusIdleEventSchema,
	SessionStatusRescheduledEventSchema,
	SessionErrorEventSchema,
	SessionRewindEventSchema,
	SessionUsageEventSchema,
	ContextSummaryEventSchema,
	ContextSummaryProgressEventSchema,
	SessionCompactEventSchema,
	SessionCompactionEventSchema,
	SessionSkillLoadedEventSchema,
	SessionTaskStartedEventSchema,
	SessionTaskStatusEventSchema,
	SessionTaskNotificationEventSchema
]);
//#endregion
//#region src/events/tool-confirmation.ts
/**
* `user.tool_confirmation`: the one event that answers a pause (epic #303, X6; #309).
*
* Every pause a session is in is answered by **one** client event, whatever is being waited
* for: an approval the settings demanded (`result: allow | deny`), or the answers to an
* `ask_user` call (`result: allow` with `answers`). Nothing else — a client never writes a
* tool result, and the brain is the one that turns this event into the
* `agent.tool_result` the call is owed.
*
* It is a `user.*` event because that is whose act it is, and Anthropic's name for it — but
* unlike `user.message` and `user.interrupt` it is **not queued**: the server writes it, with
* a real `processed_at`, only after checking that the call it names is really waiting. So it
* is not a member of {@link UserEventSchema} and nothing ever claims it: the event itself is
* the record, which is exactly what makes an approval a fact of the log — a reader (and the
* brain) answers "was this call allowed?" by finding the confirmation that named it, and a
* `session.rewind` past one removes it along with the rest of the branch it covered.
*
* `@openharness/brain`'s `./pausing` owns what a waiting call is and what its confirmation
* means; this module owns the wire shape and the two extensions.
*/
/** What the user decided about a call that was waiting on them. */
const ToolConfirmationResultSchema = z.enum(["allow", "deny"]);
/**
* // extension: how long an approval is remembered (epic #303, X6; #309).
*
* - `once` — this call only.
* - `session` — every later call to the same tool in this chat is allowed, for as long as the
*   confirmation is part of the log: the event **is** the record, so the answer survives
*   compaction and replay, and an edit that rewinds past it takes it back with the branch.
* - `always` — the same, and the user's stored setting for that tool becomes `allow`, which
*   is what makes the next chat inherit it (that write is the server's, on the append). A call
*   to a remote MCP tool writes the *policy* the user's settings hold for that tool (#312): a
*   remote tool has no on/off of its own, only the permission a call is evaluated under.
*
* Absent means `once`: the field is an extension, and every confirmation stored before it
* existed means what it always meant.
*/
const ToolConfirmationRememberSchema = z.enum([
	"once",
	"session",
	"always"
]);
/** The fields both the stored and the client-sent confirmation carry. */
const confirmationBody = {
	/**
	* The call this answers — the event id of the `agent.tool_use` or `agent.mcp_tool_use` it
	* names, which is the call's own id.
	*/
	tool_use_id: EventIdSchema,
	/** What the user decided: `allow` runs the call (or carries its answers), `deny` refuses it. */
	result: ToolConfirmationResultSchema,
	/** Why the call was refused, when the user said so. Only a `deny` carries one. */
	deny_message: z.string().min(1).optional(),
	/** How long the approval is remembered; see {@link ToolConfirmationRememberSchema}. */
	remember: ToolConfirmationRememberSchema.optional(),
	/** The answers to an `ask_user` call; see `AskUserAnswersSchema`. Only an `allow` carries them. */
	answers: AskUserAnswersSchema.optional()
};
/**
* The rules the fields hold between them: a denial explains itself and remembers nothing, and
* an approval that carries `answers` is answering a question rather than authorizing a run.
*/
function confirmationRules(value, ctx) {
	if (value.deny_message !== void 0 && value.result !== "deny") ctx.addIssue({
		code: "custom",
		path: ["deny_message"],
		message: "only a denial carries a `deny_message`: an approval has nothing to excuse"
	});
	if (value.remember !== void 0 && value.result !== "allow") ctx.addIssue({
		code: "custom",
		path: ["remember"],
		message: "only an approval is remembered: a denial cannot allow the next call"
	});
	if (value.answers !== void 0 && value.result !== "allow") ctx.addIssue({
		code: "custom",
		path: ["answers"],
		message: "only an approval carries `answers`: a denial says nothing about the questions"
	});
}
/**
* A stored `user.tool_confirmation`.
*
* `processed_at` is never null: the server writes it once the call it names has been checked,
* so the event is a fact of the log the moment it exists rather than work waiting to be picked
* up. An event naming a call that is not waiting is refused before it is stored (the 400 the
* route answers), never stored and ignored.
*/
const UserToolConfirmationEventSchema = z.object({
	id: EventIdSchema,
	type: z.literal(EVENT_TYPES.userToolConfirmation),
	seq: EventSeqSchema,
	processed_at: ProcessedAtSchema,
	...confirmationBody
}).superRefine(confirmationRules);
/**
* A `user.tool_confirmation` as a client sends it: the same shape without the fields the
* server assigns (`id`, `seq`, `processed_at`).
*
* It travels on `POST /v1/sessions/{session_id}/events`, like a `user.message`, and the
* server answers it as the protocol's 400 when the call it names is not waiting, when the
* answers do not fit the questions the call asked, or when the fields contradict each other
* (`remember` on a denial, say).
*/
const UserToolConfirmationEventInputSchema = z.object({
	type: z.literal(EVENT_TYPES.userToolConfirmation),
	...confirmationBody
}).superRefine(confirmationRules);
//#endregion
//#region src/events/union.ts
/**
* The event unions a consumer should code against.
*
* Pick the narrowest one that fits: {@link StoredEventSchema} for anything read out of the
* log, {@link StreamEventSchema} for anything read off a stream, and {@link EventInputSchema}
* for anything a client sends — the user's own events, and the one rewind instruction whose
* event the server writes (#238).
*
* Since D9 (issue #46) a streamed reply is stored chunk by chunk, so a stream carries the
* log's events; the one exception is `session.deleted` (#111), which is stream-only because
* the log it would belong to has just been deleted. The pre-D9 stream-only previews —
* `event_start` / `event_delta` with no envelope, never stored — were removed in phase P4,
* when nothing wrote them any more.
*
* Every event type is deep-readonly: the log is immutable, and the types say so — `event.seq
* = …` is a compile error, and the stores hand out deep-frozen events so it would throw at
* runtime too. The `Immutable*` names still exist as deprecated aliases of the plain ones.
*/
/**
* The stored events whose `type` string no stream-only event shares.
*
* `event_start` and `event_delta` join {@link StoredEventSchema} beside this union rather than
* inside it: a zod discriminated union cannot hold two options with one discriminator value
* (it throws when it parses), and their schemas are built from their own bodies.
*/
const StoredEventCoreSchema = z.discriminatedUnion("type", [
	UserMessageEventSchema,
	UserInterruptEventSchema,
	UserToolConfirmationEventSchema,
	AgentMessageEventSchema,
	AgentToolUseEventSchema,
	AgentToolResultEventSchema,
	AgentMcpToolUseEventSchema,
	AgentMcpToolResultEventSchema,
	AgentOpaqueContentEventSchema,
	SessionStatusRunningEventSchema,
	SessionStatusIdleEventSchema,
	SessionStatusRescheduledEventSchema,
	SessionErrorEventSchema,
	ModelRequestStartEventSchema,
	ModelRequestEndEventSchema,
	SessionRewindEventSchema,
	SessionUsageEventSchema,
	ContextSummaryEventSchema,
	ContextSummaryProgressEventSchema,
	SessionCompactEventSchema,
	SessionCompactionEventSchema,
	SessionSkillLoadedEventSchema,
	SessionTaskStartedEventSchema,
	SessionTaskStatusEventSchema,
	SessionTaskNotificationEventSchema
]);
/**
* Every event a session can store, discriminated on `type`.
*
* The core members are one discriminated union; the two stored chunks are members too,
* reached first by `type` and then by shape.
*/
const StoredEventSchema = z.union([
	StoredEventCoreSchema,
	StoredEventStartSchema,
	StoredEventDeltaSchema
]);
/**
* Every event a stream can deliver: the stored events, and one that is not.
*
* Before phase P4 this union also held the stream-only previews of `event_start` /
* `event_delta`; the brain stores its chunks since P3, so there is no second form left. A
* payload from a pre-P4 server that carries a seq-less chunk does not parse and is skipped by
* a reader, the way any event a reader does not know is.
*
* `session.deleted` is the one member that is **not** stored (#111): it announces that the
* session — and its log with it — is gone, so it can never be a `StoredEvent`. A server
* sends it as the last event on every open stream for the session, before the stream closes.
*/
const StreamEventSchema = z.union([StoredEventSchema, SessionDeletedEventSchema]);
/**
* Whether a stream event was persisted, i.e. whether it is a {@link StoredEvent}.
*
* True for everything but `session.deleted`, which names a session whose log no longer
* exists. The `seq` test is what tells them apart — the stored envelope is exactly what
* `session.deleted` does not have — so it is honest both for a value from the schemas and for
* a value that did not come from them (a seq-less `event_start` from a pre-D9 server, say).
*/
function isStoredEvent(event) {
	return "seq" in event;
}
/**
* Every event a client may append to a session's log, as it sends it.
*
* The user's own events — `user.message` and `user.interrupt` — plus two instructions the
* server turns into an event of its own: a `session.rewind` (#238), and a
* `user.tool_confirmation` answering a call that was waiting on the user (epic #303, #309).
* Neither the rewind nor the confirmation is a *queued* user event: the server writes them,
* processed, after checking them against the log — the rewind's `from_seq` has to name a
* message the session still shows, and the confirmation has to name a call that is really
* waiting (a call that is not is the protocol's 400, and nothing is stored).
*
* A rewind is not a user event — the session's status events are no more the user's for being
* asked for — but the client is the one that knows which message the reader edited, so it
* travels on the same `events` array and the same request as the message that follows it,
* which is what makes the two atomic: either the session is rewound and the edited message is
* stored, or neither is. A batch carries **at most one rewind, and it comes first**: see
* `SendEventsRequestSchema` for why anything else would be swallowed by the range the rewind
* records.
*
* This is the member type of `SendEventsRequest.events`; read a log with
* {@link StoredEventSchema} and a stream with {@link StreamEventSchema}.
*/
const EventInputSchema = z.union([
	UserEventInputSchema,
	UserToolConfirmationEventInputSchema,
	SessionRewindEventInputSchema
]);
//#endregion
//#region src/events/api.ts
/**
* The three event endpoints:
*
* - `POST /v1/sessions/{session_id}/events` — append user events
* - `GET  /v1/sessions/{session_id}/events` — read the log
* - `GET  /v1/sessions/{session_id}/events/stream` — follow it live
*
* Array-valued query parameters use the same wire spelling as Anthropic's API: the key is
* repeated with a `[]` suffix, `types[]=user.message&types[]=agent.message`. The schemas
* below model the collected array; turning a URL into it is the server's job.
*/
/** Default `order`: oldest event first, the order the log was written in. */
const DEFAULT_EVENT_ORDER = "asc";
/** An event type that may be passed to the `types[]` filter. */
const StoredEventTypeSchema = z.enum(STORED_EVENT_TYPES);
/**
* Body of `POST /v1/sessions/{session_id}/events`.
*
* The user's own events, and one instruction the server turns into a session event: a
* `session.rewind` (#238). The server owns everything else in the log. A rewind travels with
* the edited message it belongs to, in one array and one append, so the session is never
* rewound but missing the message the reader sent — see {@link EventInputSchema}.
*
* A batch may carry **at most one rewind, and it comes first**. The batch is appended in
* order and a rewind supersedes everything from the message it names to the end of the log as
* it stands, so a message ahead of it — or anything behind a second rewind — would be stored
* and then swallowed by the range the rewind records: accepted by the response and answered by
* no turn. A batch that breaks the rule is a 400 `invalid_request_error` and stores nothing.
* The store enforces the same rule on its append path (`assertRewinds` in
* `@openharness/session`); this is the wire's half of it.
*/
const SendEventsRequestSchema = z.object({ events: z.array(EventInputSchema).min(1) }).superRefine((body, ctx) => {
	const [first, second] = body.events.flatMap((event, index) => event.type === EVENT_TYPES.sessionRewind ? [index] : []);
	if (second !== void 0) {
		ctx.addIssue({
			code: "custom",
			path: ["events", second],
			message: "a batch may carry at most one session.rewind: each one restarts the log, so a second would supersede the first"
		});
		return;
	}
	if (first !== void 0 && first > 0) ctx.addIssue({
		code: "custom",
		path: ["events", first],
		message: "a session.rewind must be the first event in the batch: its range supersedes everything after the message it names, so a message before it would be stored and then swallowed"
	});
});
/**
* Response of `POST /v1/sessions/{session_id}/events`: the stored **user** events, with their
* `id` and `seq` assigned. Their `processed_at` is `null` until the brain reaches them.
*
* A `session.rewind` in the request is not one of them: the server writes it, and a client
* reads it back from the log or the stream like every other session event.
*/
const SendEventsResponseSchema = z.object({ data: z.array(UserEventSchema) });
/** Query parameters of `GET /v1/sessions/{session_id}/events`. */
const ListEventsQuerySchema = z.object({
	/** Maximum results per page. Defaults to `DEFAULT_PAGE_LIMIT`, capped at `MAX_PAGE_LIMIT`. */
	limit: PageLimitSchema.optional(),
	/** `asc` (default, oldest first) or `desc`. */
	order: ListOrderSchema.optional(),
	/** Cursor from a previous response's `next_page`: a `seq` position, the last event returned. */
	page: PageCursorStringSchema.optional(),
	/** The wire key is `types[]`. Only these event types are returned; omit for all of them. */
	types: z.array(StoredEventTypeSchema).optional(),
	/**
	* // extension: return only events with `seq > after_seq`.
	*
	* Cheaper and exact where `page` is only a resume hint: `after_seq=0` reads the session
	* from the start, and `after_seq=<last seen seq>` re-reads nothing.
	*/
	after_seq: AfterSeqSchema.optional()
});
/** Response of `GET /v1/sessions/{session_id}/events`: the Anthropic list envelope. */
const ListEventsResponseSchema = z.object({
	data: z.array(StoredEventSchema),
	next_page: NextPageSchema
});
/** Largest number of `event_deltas[]` values a stream request may carry, per Anthropic. */
const MAX_EVENT_DELTAS = 100;
/** Query parameters of `GET /v1/sessions/{session_id}/events/stream`. */
const StreamEventsQuerySchema = z.object({
	/**
	* Opt in to the chunks of a reply (`event_start` / `event_delta`), per connection. The wire
	* key is `event_deltas[]`; repeating it opts in to several event types. Omit it for a plain
	* stream of stored events.
	*/
	event_deltas: z.array(DeltaTypeSchema).max(100).optional(),
	/**
	* // extension: replay stored events with `seq > after_seq` before streaming live ones.
	*
	* The same parameter as on the list endpoint, so a client that reconnects after
	* `last-event-id` and one that passes `after_seq` explicitly converge on the same position.
	*/
	after_seq: AfterSeqSchema.optional()
});
//#endregion
//#region src/events/tool.ts
/** Whether a stored event is a tool call. */
function isToolCallEvent(event) {
	return event.type === EVENT_TYPES.agentToolUse || event.type === EVENT_TYPES.agentMcpToolUse;
}
/** Whether a stored event answers a tool call. */
function isToolResultEvent(event) {
	return event.type === EVENT_TYPES.agentToolResult || event.type === EVENT_TYPES.agentMcpToolResult;
}
/** Whether a tool call is one a remote MCP server's tool — rather than this build's own. */
function isMcpToolCall(call) {
	return call.type === EVENT_TYPES.agentMcpToolUse;
}
/** The call's id, which is the event's own id — the identity a result names. */
function toolCallId(call) {
	return call.id;
}
/** The arguments the model produced, whichever pair the call belongs to. */
function toolCallInput(call) {
	return call.input;
}
/** What the policy in force said about the call, whichever pair it belongs to. */
function toolCallPermission(call) {
	return call.evaluated_permission;
}
/**
* Where the call's tool comes from.
*
* An MCP call is `mcp` by the event's own type; a built-in one is whatever the call recorded
* (epic #303, X14; issue #335) — `builtin` for a tool this build ran, `provider` for one the
* model's own provider ran inside the request, which is the difference between a call the
* crash rule and the step limit apply to and one they cannot.
*/
function toolCallSource(call) {
	return isMcpToolCall(call) ? "mcp" : call.source ?? "builtin";
}
/** The MCP server a call's tool belongs to, by name — or `undefined` for a built-in tool. */
function toolCallServer(call) {
	return isMcpToolCall(call) ? call.mcp_server_name : void 0;
}
/**
* The name the model called the tool by — what a registry, a result cap and a `remember`
* approval are keyed by.
*
* A built-in's `name` is already that name; an MCP call records the server's own pair, so the
* offered name is recomputed from it ({@link mcpToolOfferedName}). Both are pure readings of
* the event, which is the property that makes every reader able to answer this question from
* the log alone.
*/
function toolCallOfferedName(call) {
	return isMcpToolCall(call) ? mcpToolOfferedName(call.mcp_server_name, call.name) : call.name;
}
/**
* The tool's own name as the log records it: a built-in's name, or an MCP server's name for
* its tool — what a `user.tool_confirmation`'s reason or a summary reads it as.
*/
function toolCallName(call) {
	return call.name;
}
/** The call a result answers, whichever pair the result belongs to. */
function toolResultCallId(result) {
	return result.type === EVENT_TYPES.agentMcpToolResult ? result.mcp_tool_use_id : result.tool_use_id;
}
/** Whether a result reports a failed call. */
function toolResultIsError(result) {
	return result.is_error;
}
//#endregion
//#region src/resources/mcp-server.ts
/**
* The remote-MCP-server resource and the endpoints that manage it:
*
* - `POST   /v1/me/mcp_servers`
* - `GET    /v1/me/mcp_servers`
* - `GET    /v1/me/mcp_servers/{mcp_server_id}`
* - `POST   /v1/me/mcp_servers/{mcp_server_id}` (update)
* - `DELETE /v1/me/mcp_servers/{mcp_server_id}`
* - `POST   /v1/me/mcp_servers/{mcp_server_id}/test`      (connection check)
* - `POST   /v1/me/mcp_servers/{mcp_server_id}/connect`   (start OAuth; answers an authorization URL)
* - `POST   /v1/me/mcp_servers/{mcp_server_id}/disconnect`(drop the stored tokens)
* - `GET    /v1/me/mcp_servers/oauth/callback`            (the provider's redirect back)
*
* // extension: Anthropic's Managed Agents API has `mcp_servers` on an agent or a session as a
* per-run reference (`{ type: 'url', url, name }`) — Anthropic holds the servers and their
* credentials. openharness does not: a **user** registers remote MCP servers they operate
* (epic #303, X10), each with the URL and the authentication it needs, and the servers are a
* per-user resource like a mode or a provider credential. MCP tools reach a chat through this
* resource, never through the model context's own configuration. There is no tool calling in
* this change (#312); what is here is the resource, its sealed secrets and its OAuth 2.1
* client.
*
* ## Streamable HTTP only
*
* A remote server is addressed by an absolute `http`/`https` URL and spoken to over MCP's
* **Streamable HTTP** transport. The other transports (stdio, the deprecated HTTP+SSE) are not
* supported: stdio is a local process this server does not spawn, and Streamable HTTP is what
* the `2026-03` protocol revision settles on. An address that resolves to a private, loopback
* or link-local host is refused unless the deployment's self-host setting is on (the same
* `OPENHARNESS_ALLOW_PRIVATE_PROVIDER_URLS` flag the custom OpenAI-compatible credential
* uses, #249) — an MCP server on an internal network is a real self-hosted case, and an SSRF
* is a real attack.
*
* ## Secrets
*
* A server authenticated with `headers` carries a name/value map the user supplies (an API
* key header, say). The map is **write-only**: it is sealed with `@openharness/vault`, exactly
* like a provider credential, and never returned — the resource lists the header **names**
* only. An `oauth` server carries the tokens its authorization granted, sealed the same way
* and refreshed automatically. Nothing on this path is ever put in an event or in a model
* request's context (epic #303, X11).
*/
/**
* Longest MCP server name the API accepts.
*
* Capped short because the name is a tool-name prefix a model sees once tools land (#312): a
* remote tool is `<server name>__<tool name>`, so a long server name would spend a model's
* tool-name budget before the tool's own name says anything. Same 32 characters a named
* provider credential takes, for the same reason.
*/
const MCP_SERVER_NAME_MAX_LENGTH = 32;
/**
* The shape of a server name: lowercase letters, digits and interior dashes.
*
* A name has to be usable as a tool-name prefix, and tool names are `[a-zA-Z0-9_-]` in
* practice — so the vocabulary is narrowed to what every provider's tool-name rules accept
* without a rewrite: no dots, no underscores (the `__` separator owns those), no uppercase,
* and no leading or trailing dash. The same pattern a named credential's name takes
* (`CREDENTIAL_NAME_PATTERN`), restated here because the two are free to diverge.
*/
const MCP_SERVER_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** Whether `value` can be an MCP server name. */
function isMcpServerName(value) {
	return value.length > 0 && value.length <= 32 && MCP_SERVER_NAME_PATTERN.test(value);
}
/** The name a server gets when the reader does not type one, so the form can be two fields. */
const MCP_SERVER_NAME_FALLBACK = "mcp-server";
/**
* A server name derived from its URL's host, or {@link MCP_SERVER_NAME_FALLBACK}.
*
* A reader should not have to name a server before it works, so `create` accepts a body with no
* `name` and derives one: the host, lowercased, with `www.` dropped and every character a name
* may not carry turned into a dash. The result always satisfies {@link isMcpServerName} — it is
* truncated to {@link MCP_SERVER_NAME_MAX_LENGTH}, trimmed of the dashes the substitution can
* leave at either end, and falls back to `mcp-server` for a host that reduces to nothing (an
* IPv6 literal, say). A derived name is not guaranteed unique: `create` still answers the 409 a
* taken name gets, and the reader renames it.
*
* @param url the server's URL, as the reader typed it
*/
function mcpServerNameFromUrl(url) {
	let host;
	try {
		host = new URL(url).hostname;
	} catch {
		return MCP_SERVER_NAME_FALLBACK;
	}
	const reduced = host.toLowerCase().replace(/^www\./, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32).replace(/-+$/, "");
	return isMcpServerName(reduced) ? reduced : MCP_SERVER_NAME_FALLBACK;
}
/** A server name: the rule {@link isMcpServerName} checks, as a schema. */
const McpServerNameSchema = z.string().refine(isMcpServerName, { error: `a server name is 1–32 lowercase letters, digits and interior dashes, e.g. \`notes\` or \`my-notes\`` });
/**
* How many MCP servers one user may hold.
*
* Lower than the provider-credential count and equal to `MAX_MODES_PER_USER`: every enabled
* server contributes its whole tool list to **every** request once tools land (#312, #307), so
* this is a context budget as much as a row count. Twenty servers' worth of tools is already
* far more than a model takes well; a user past it disables some rather than adding more.
*/
const MAX_MCP_SERVERS_PER_USER = 20;
/** Most header pairs one `headers`-authenticated server may carry. */
const MAX_MCP_HEADERS = 16;
/** Longest header name accepted. HTTP header names are short; this is a bound, not a rule. */
const MCP_HEADER_NAME_MAX_LENGTH = 256;
/** Longest header value accepted. A bearer token or an API key fits well inside it. */
const MCP_HEADER_VALUE_MAX_LENGTH = 4096;
/** Longest `last_error` string a resource reports, so a failure cannot bloat a response. */
const MCP_LAST_ERROR_MAX_LENGTH = 500;
/**
* How an MCP server authenticates a request (epic #303, X10).
*
* - `none` — no credential; the server is open, or trusts the network.
* - `headers` — a name/value map the user supplies, sent on every request. The values are
*   sealed and never returned.
* - `oauth` — OAuth 2.1, with this server as the OAuth **client**: discovery, dynamic client
*   registration, authorization code with PKCE, and tokens sealed and refreshed automatically.
*/
const McpAuthTypeSchema = z.enum([
	"none",
	"headers",
	"oauth"
]);
/**
* The health of an MCP server as far as the server can tell (epic #303, X10).
*
* - `connected` — the last request succeeded (or the server needs no auth and the last check
*   passed).
* - `needs_reconnect` — an `oauth` server with no live tokens: never connected yet, a refresh
*   that failed, or a disconnect. The user starts the flow again.
* - `error` — the last check or request failed for another reason (`last_error` says which).
*
* Set by the connection check on save and on demand, and by the credentials resolver when a
* refresh fails (#312); a failure here never blocks a chat (epic #303).
*/
const McpServerStatusSchema = z.enum([
	"connected",
	"needs_reconnect",
	"error"
]);
/**
* The header pairs a `headers`-authenticated server sends, **write-only**.
*
* Accepted on a create or an update and stored sealed; never echoed back. Keys are HTTP header
* names, values the secrets. An empty map is refused — a `headers` server with no headers is a
* `none` server that says otherwise.
*/
const McpHeadersSchema = z.record(z.string().min(1).max(256), z.string().min(1).max(MCP_HEADER_VALUE_MAX_LENGTH)).refine((headers) => Object.keys(headers).length > 0, { error: "a headers-authenticated server needs at least one header" }).refine((headers) => Object.keys(headers).length <= 16, { error: `at most 16 headers` });
/**
* The URL of a remote MCP server: an absolute `http` or `https` URL, with no fragment.
*
* The **scheme and shape** are all this schema checks — a private or loopback host is a
* *server-side* refusal (the SSRF guard), because whether one is acceptable depends on the
* deployment's self-host setting, not on the syntax. `http` is allowed for the same reason a
* custom OpenAI-compatible credential allows it: a self-hosted server on a private network is
* the case the setting exists for. A fragment is refused because it is never sent in a request
* and could only be a mistake; userinfo is refused because a URL that carries a credential in
* it would put that credential in a log line.
*/
const McpServerUrlSchema = z.string().refine(isMcpServerUrl, { error: "the URL must be an absolute http or https URL with a host, no userinfo and no fragment, e.g. `https://mcp.example.com/mcp`" });
/** Whether `value` is an acceptable MCP server URL — the rule {@link McpServerUrlSchema} checks. */
function isMcpServerUrl(value) {
	let url;
	try {
		url = new URL(value);
	} catch {
		return false;
	}
	if (url.protocol !== "http:" && url.protocol !== "https:") return false;
	if (url.hostname.length === 0 || url.username !== "" || url.password !== "") return false;
	return url.hash === "";
}
/**
* One tool an MCP server offers, as the protocol carries it.
*
* `input_schema` is the tool's JSON Schema exactly as the server sent it — an open object,
* because it is the server's schema and not this protocol's to model. It is what a tool
* definition costs tokens on (see {@link estimateToolDefinitionTokens}) and what a model is
* shown once tools land (#312); this change only lists and measures the tools, it never calls
* them.
*/
const McpToolDefinitionSchema = z.object({
	name: z.string().min(1),
	description: z.string().nullable(),
	input_schema: z.record(z.string(), z.unknown())
});
/**
* One tool as a **resource** reports it: the name, the description, and what its definition
* costs in tokens.
*
* A summary rather than the whole definition: the resource is a listing, and a settings screen
* shows names and a cost, not every tool's JSON Schema. The full definition is read live from
* the server when tools land (#312).
*/
const McpToolSummarySchema = z.object({
	name: z.string().min(1),
	description: z.string().nullable(),
	/** The tool's estimated definition size, in tokens (see {@link estimateToolDefinitionTokens}). */
	definition_tokens: z.number().int().min(0)
});
/**
* How many characters of a definition this package counts as one token.
*
* The estimator is deliberately the cheap, provider-agnostic one — roughly four characters per
* token, the rule of thumb for English and JSON — because the real tokenizer belongs to the
* model that will read the definitions and nothing in this pure package can run it. It is an
* **estimate** and is named as one everywhere it is reported (epic #303, X6): its job is to
* tell a settings screen that one server's tools cost ~2k tokens and another's ~200, not to
* predict a bill.
*/
const TOKENS_PER_CHARACTER_RATIO = 4;
/**
* The estimated token cost of one tool's definition.
*
* Counts the three fields a model is shown — the name, the description and the input schema —
* as compact JSON, so the size tracks what really travels. A definition that cannot be
* serialized (a circular schema, which an MCP server should not send) counts as the name and
* description alone rather than throwing: a listing is not the place to fail over one tool.
*/
function estimateToolDefinitionTokens(tool) {
	const definition = {
		name: tool.name,
		description: tool.description,
		input_schema: tool.input_schema
	};
	let json;
	try {
		json = JSON.stringify(definition) ?? "";
	} catch {
		json = JSON.stringify({
			name: tool.name,
			description: tool.description
		}) ?? "";
	}
	return Math.ceil(json.length / 4);
}
/** The estimated token cost of a whole tool list: the sum of its tools' definitions. */
function estimateToolsTokens(tools) {
	let total = 0;
	for (const tool of tools) total += estimateToolDefinitionTokens(tool);
	return total;
}
/** The {@link McpToolSummary} of one tool definition. */
function mcpToolSummary(tool) {
	return {
		name: tool.name,
		description: tool.description,
		definition_tokens: estimateToolDefinitionTokens(tool)
	};
}
/**
* A user's remote MCP server, as the API returns it.
*
* Never carries a secret. A `headers`-authenticated server reports the header **names** it
* sends (`header_names`) so a settings screen can say what is configured — the values stay
* sealed. An `oauth` server reports nothing about its tokens at all: `status` is the whole
* story a caller gets, and a token is refreshed behind it without the resource changing shape.
*/
const McpServerSchema = z.object({
	id: McpServerIdSchema,
	/**
	* // extension: the discriminant, so the resource can be told from an agent or a session in
	* a bag of mixed resources. Anthropic has no per-user MCP server resource.
	*/
	type: z.literal("mcp_server"),
	/**
	* // extension: the user this server belongs to (epic #65, A4). Read-only: the server sets it
	* from the caller, no request carries it, and another user's server is answered as missing.
	*/
	owner_id: UserIdSchema,
	/** The name, unique among its owner's servers and used as a tool-name prefix. */
	name: McpServerNameSchema,
	/** The server's Streamable HTTP endpoint. */
	url: z.string(),
	/** How requests authenticate: `none`, `headers` or `oauth`. */
	auth: McpAuthTypeSchema,
	/**
	* Whether the server is on by default for this user's chats (epic #303, X6).
	*
	* The user default only. A mode may later override which servers are on, at **server**
	* granularity — there is no per-tool switch — but that override is #307 and is deliberately
	* not modelled here; this field is the default a mode would override, not the effective set.
	*/
	enabled: z.boolean(),
	/** Whether the server is reachable and authenticated right now. */
	status: McpServerStatusSchema,
	/** Why the last check or request failed, or `null`. Never a secret. */
	last_error: z.string().nullable(),
	/**
	* The header names a `headers`-authenticated server sends, in insertion order, and `[]` for
	* every other auth type. The values are sealed and never appear here.
	*/
	header_names: z.array(z.string()),
	/** The tools the last connection check listed, or `[]` before one has run. */
	tools: z.array(McpToolSummarySchema),
	/**
	* The estimated token cost of `tools` — what the server's tool definitions spend of a
	* request's context (epic #303, X6). `0` before a check has listed any.
	*/
	definition_tokens: z.number().int().min(0),
	/** When the tools above were last listed, or `null` before a check has run. */
	last_tested_at: TimestampSchema.nullable(),
	created_at: TimestampSchema,
	/** When the server was last changed, or its tokens last refreshed. Set on update. */
	updated_at: TimestampSchema
});
/**
* The fields every create body carries, whatever the auth type. Split out so each union member
* repeats only its own authentication.
*/
const mcpServerBaseFields = {
	name: McpServerNameSchema,
	url: McpServerUrlSchema,
	/** Whether the server is on by default; `true` when omitted. */
	enabled: z.boolean().optional()
};
/**
* Body of `POST /v1/me/mcp_servers`. Response: {@link McpServerSchema} (201).
*
* **The auth type is detected, not chosen.** A reader types a URL — and, optionally, a name —
* and the **server** probes it: an unauthenticated request the server answers `401` (or whose
* metadata advertises an authorization server, RFC 9728) means OAuth, and a successful
* initialize means `none`. So `auth` is **optional** here and a body that omits it is the
* normal one; a `headers` map is the one thing a caller can still say explicitly, and it
* **implies** `auth: 'headers'`.
*
* `auth` is still accepted, for a caller that knows better than the probe — it is what the
* field meant before detection existed, and the API stays backward compatible with it. When it
* is supplied it wins: `auth: 'headers'` requires a map, and `auth: 'oauth'` stores the server
* on `needs_reconnect` so the reader can run the Connect flow.
*
* `name` is optional too: a body without one is named after its URL's host
* ({@link mcpServerNameFromUrl}). A name — given or derived — is unique per user, and a
* duplicate is the 409 a mode's is.
*/
const CreateMcpServerRequestSchema = z.object({
	...mcpServerBaseFields,
	/** Omitted means "name it after the URL's host" ({@link mcpServerNameFromUrl}). */
	name: McpServerNameSchema.optional(),
	auth: McpAuthTypeSchema.optional(),
	headers: McpHeadersSchema.optional()
}).refine((body) => body.auth === void 0 || body.auth !== "headers" || body.headers !== void 0, {
	error: "a server saved with `auth: 'headers'` needs a `headers` map; omit `auth` to let the server detect the authentication from the URL",
	path: ["headers"]
}).refine((body) => body.headers === void 0 || body.auth === void 0 || body.auth === "headers", {
	error: "a `headers` map implies `auth: 'headers'`; it may not be sent beside another auth type",
	path: ["auth"]
});
/** The `auth` a create body resolves to, given what the caller said and what it carries. */
function requestedMcpAuth(body) {
	if (body.headers !== void 0) return "headers";
	return body.auth ?? null;
}
/**
* Body of `POST /v1/me/mcp_servers/{mcp_server_id}`. Response: {@link McpServerSchema}.
*
* Every field is optional; an omitted field keeps its stored value. `headers` is the one
* write-only field: when present it **replaces** the sealed header map, and when omitted the
* stored one is left alone — so a rename or an enable does not require re-entering a secret.
* Changing `auth` away from `headers` drops the sealed headers, and changing it to `oauth`
* drops any stored headers and requires a fresh `connect`; the route checks those pairings and
* answers 400 for a body that carries `headers` while the resulting auth is not `headers`.
*/
const UpdateMcpServerRequestSchema = z.object({
	name: McpServerNameSchema.optional(),
	url: McpServerUrlSchema.optional(),
	enabled: z.boolean().optional(),
	auth: McpAuthTypeSchema.optional(),
	headers: McpHeadersSchema.optional()
});
/**
* Response of `GET /v1/me/mcp_servers`.
*
* No pagination envelope, like the mode and credential lists: a user holds at most
* {@link MAX_MCP_SERVERS_PER_USER}, so the response is bounded and a settings screen shows
* them all at once.
*/
const ListMcpServersResponseSchema = z.object({ data: z.array(McpServerSchema) });
/**
* Where an OAuth flow was started (epic #303, X10; #311).
*
* The browser that lands on the callback is **not** necessarily signed in to this server: `oh`
* starts the flow by opening the authorization URL in the system browser, and `oh` itself
* authenticates with a bearer token from the device flow, so its browser may never have a
* session here. The callback is therefore authenticated by the `state` alone, and the two
* callers want different endings — so the flow records which one started it. The web app is
* sent back to its settings screen; the CLI is shown a plain page to close.
*
* // extension: Anthropic has no per-user MCP server resource and no OAuth client flow, so
* there is no equivalent of a flow's origin to record.
*/
const McpOAuthClientSchema = z.enum(["web", "cli"]);
/**
* Body of `POST /v1/me/mcp_servers/{mcp_server_id}/connect`.
*
* Every field is optional — the route accepts a request with no body at all — and `client`
* defaults to `web`, which is what the app sends and what every caller meant before #311.
*/
const ConnectMcpServerRequestSchema = z.object({ 
/**
* Where the flow was started, recorded on the pending `state` so the callback knows how to
* answer. `web` when omitted.
*/
client: McpOAuthClientSchema.default("web") });
/**
* Response of `POST /v1/me/mcp_servers/{mcp_server_id}/connect`: where to send the user to
* authorize the server.
*
* The server is the OAuth **client** (epic #303, X10): it discovers the authorization server,
* registers itself dynamically when it can, builds the authorization code + PKCE request and
* answers the URL. The web app and `oh` both open that URL in a browser; the provider then
* redirects back to this server's own callback route, which completes the flow. `state` binds
* the round trip to the user and the server, is single use and short-lived, and is not carried
* here — it is inside `authorization_url`.
*/
const ConnectMcpServerResponseSchema = z.object({ 
/** The authorization URL to open in the user's browser. */
authorization_url: z.string() });
//#endregion
//#region src/resources/model.ts
/**
* The model catalog — `GET /v1/models` (epic #92, wave 1) — and the entries it lists: the
* chat models the caller's own provider credentials can use.
*
* ```
* GET /v1/models   -> { data: model entry[], providers: provider status[] }
* ```
*
* Only providers the caller has a credential for appear (C5). For each of them the server
* calls the provider's own list-models endpoint with that credential, joined with the
* bundled models.dev registry for display names, context windows and output limits (C1, C2),
* and `providers`
* reports how each list was obtained: `ok` for the provider's own answer, `fallback` when the
* call failed or timed out (5 s) — or the provider has no list endpoint — and the registry's
* chat models stood in (C3). The server caches the answer in memory per user and provider for
* an hour (C4); `refresh=true` bypasses the cache.
*
* // extension: the shape is openharness's own, and so is the endpoint's scope. Anthropic's
* `GET /v1/models` lists Anthropic's models; this one lists the models the caller's keys can
* use, merges each provider's own list with the registry, and reports a per-provider status,
* so a registry-only fallback is a visible state rather than a silent one.
*/
/**
* One model's list price: US dollars per **million tokens**, as models.dev publishes it.
*
* `input` and `output` are always there — a model cannot be priced at all without them — and the
* two cache rates are `null` when the registry does not carry them, which is common (models.dev
* has `cache_write` for Anthropic and almost nobody else). A `null` rate is not "free": nothing
* spends cache tokens at a rate nobody published, and
* {@link usageCost} answers "unknown" rather than zero for a request that did.
*
* // extension: Anthropic's model catalog carries no price at all — its usage figures are
* platform-computed and stored. openharness computes cost when it is read, from the tokens the
* log holds and these rates, so the rates are what a client needs to price a reply itself.
*/
const ModelCostSchema = z.object({
	/** USD per million input tokens. */
	input: z.number().nonnegative(),
	/** USD per million output tokens. */
	output: z.number().nonnegative(),
	/** USD per million tokens read from the prompt cache, when the registry has a rate. */
	cache_read: z.number().nonnegative().nullable(),
	/** USD per million tokens written to the prompt cache, when the registry has a rate. */
	cache_write: z.number().nonnegative().nullable()
});
/**
* One chat model the caller can use.
*
* `id` is the model id an agent's `model.id` takes, `provider/model`, and
* `provider` is its prefix. Non-chat models (embeddings, image, TTS, …) are never listed.
*/
const ModelEntrySchema = z.object({
	/** The router id, e.g. `openai/gpt-4.1-mini`. */
	id: z.string().min(1),
	/** The provider that serves it: the prefix of `id`, e.g. `openai`. */
	provider: z.string().min(1),
	/** Display name: the registry's, or the provider's, or `id` when neither has one. */
	name: z.string().min(1),
	/** The context window in tokens, when it is known; `null` when it is not (C6). */
	context_window: z.number().int().nonnegative().nullable(),
	/** The largest output the model accepts, when it is known; `null` when it is not. */
	max_output_tokens: z.number().int().nonnegative().nullable(),
	/**
	* // extension: the model's list price, when the registry has one, and `null` when it does
	* not (epic #245; issue #247).
	*
	* It is what a client prices a reply with — the reply's own tokens times these rates — so a
	* session's cost is readable without a second request and without the server storing it. A
	* model whose price the registry lacks keeps its tokens and reports no cost: `null` here is
	* what turns into "—" in a UI, and never into an estimate.
	*/
	cost: ModelCostSchema.nullable(),
	/**
	* // extension: the history budget the server will trim this model's requests to, in tokens
	* (epic #277, K10; #246, #280).
	*
	* The **one** number a context meter should compare a prompt size against: the budget the
	* brain's own resolver answers for this id (`apps/server/src/catalog/context-budget.ts`),
	* or the brain's fallback when the registry knows the model not at all — which is the case
	* for a custom OpenAI-compatible endpoint and an Azure deployment, where `context_window`
	* may still describe the deployment while the registry has nothing to derive a budget from.
	* Deriving it here rather than in a client is what keeps a meter and the trimming that
	* actually happens from disagreeing.
	*/
	context_budget: z.number().int().positive(),
	/**
	* // extension: whether the model can call tools (epic #303, X2).
	*
	* The registry's `tool_call` — models.dev's own flag — read off the bundled models.dev
	* snapshot, and **true** for a model the registry knows nothing about: an unfamiliar model is
	* offered tools rather than quietly denied them, the same "never hide a usable model"
	* principle the chat filter follows (C2). A model the registry marks as tool-less is the one
	* case that is `false`, and the brain offers it no tools at all.
	*
	* A client uses it to say so before a chat starts (#308); nothing about a session changes
	* with it, and a model that cannot call tools chats exactly as it did before tools existed.
	*/
	tool_call: z.boolean(),
	/**
	* // extension: where this entry's listing came from — `provider` when the provider's own
	* list carried it, `registry` when it came from the registry alone (C3).
	*/
	source: z.enum(["provider", "registry"])
});
/**
* How one provider's catalog was obtained, one entry per provider the caller has a credential
* for (C5).
*
* `ok` — the provider's list endpoint answered within its 5-second budget and its models are
* in `data` with `source: 'provider'`. `fallback` — the call failed, timed out, or the
* provider has no known list endpoint, and its chat models came from the registry instead
* (C3).
*/
const ProviderCatalogStatusSchema = z.object({
	/** The provider id, e.g. `anthropic`, `openai`. */
	provider: z.string().min(1),
	/** `ok` or `fallback`; see the schema's description. */
	status: z.enum(["ok", "fallback"]),
	/** When the provider's list was fetched. `null` on a fallback: nothing was fetched. */
	fetched_at: TimestampSchema.nullable(),
	/** Why it fell back — never any part of a credential (C3); `null` when it did not. */
	message: z.string().nullable()
});
/**
* Response of `GET /v1/models`: the caller's chat models and one status per provider.
*
* No pagination envelope: the response is bounded by the caller's own credentials and each
* provider's model list, and the agent form needs every provider at once. `data` is sorted by
* provider, then by name; both arrays are empty, never absent, for an account with no
* credentials at all.
*/
const ListModelsResponseSchema = z.object({
	/** The chat models, sorted by `provider`, then `name`. */
	data: z.array(ModelEntrySchema),
	/** One status per provider the caller has a credential for. */
	providers: z.array(ProviderCatalogStatusSchema)
});
/**
* Query parameters of `GET /v1/models`.
*/
const ListModelsQuerySchema = z.object({ 
/**
* Bypass the per-user, per-provider cache and re-fetch from the providers (C4).
*
* The wire spelling is `refresh=true`; a query string arrives as text, so the schema accepts
* `'true'` / `'false'` as well as a real boolean. Refreshing is rate-limited to once a
* minute per user: a request inside that window is answered 429 `rate_limit_error` instead
* of refreshing.
*/
refresh: z.union([z.boolean(), z.enum(["true", "false"])]).transform((value) => value === true || value === "true").optional() });
//#endregion
//#region src/resources/provider-credential.ts
/**
* Provider credentials — the per-user model-provider keys (epic #65, A5) — and the endpoints
* that manage them:
*
* - `PUT    /v1/provider-credentials/{name}` (add or replace)
* - `GET    /v1/provider-credentials`
* - `DELETE /v1/provider-credentials/{name}`
*
* // extension: Anthropic's Managed Agents API has no notion of a user's own credentials;
* Anthropic holds the model-provider keys. openharness does not: every user brings their own,
* they are stored encrypted (the `@openharness/vault` package), and the API is **write-only**
* — a secret goes up and only metadata ever comes back down. Nothing in this module carries a
* secret in a response, and the plaintext must never reach a log, an event or an error.
*
* A credential is keyed by its **name**, which is the `provider` half of the model ids it
* serves: `PUT /v1/provider-credentials/anthropic` stores the Anthropic key, and
* `PUT /v1/provider-credentials/azure-eu` stores a named Azure OpenAI credential that answers
* `azure-eu/<deployment>`. For the eleven fixed providers the name is simply the provider id —
* there is one of each — and the named types (see `credential-types.ts`, epic #245 A3a) let a
* user keep more than one of a type under names they choose.
*/
/**
* The credential forms a provider credential can take.
*
* `api_key` is one secret, for the providers the model factory authenticates that way (OpenAI,
* Anthropic, Google AI Studio, OpenRouter, Groq, …) — the eleven fixed provider ids. The record
* is a discriminated union on `type`, so `gcp_service_account` later becomes a new type with its
* own payload fields rather than a new design; `azure_openai` (epic #245, A3a),
* `openai_compatible` (epic #245, A3b), `bedrock` (epic #245, A3c) and `vertex` (epic #245,
* A3d) are the four such types.
*/
const ProviderCredentialTypeSchema = z.enum([
	"api_key",
	"azure_openai",
	"openai_compatible",
	"bedrock",
	"vertex"
]);
/** Every credential type, in schema order. */
const PROVIDER_CREDENTIAL_TYPES = ProviderCredentialTypeSchema.options;
/**
* The public, non-secret facts a custom OpenAI-compatible credential publishes (epic #245, A3b).
*
* A credential's wire form is the same fields whatever its type, and a type that has a fact a
* settings screen should show publishes it as **its own** `details` object rather than as a new
* top-level field — {@link ProviderCredentialSchema} is where each type says what it publishes,
* so Bedrock's `{ region }`, Vertex's `{ email, project, location }` and this type's host arrive
* as their own variants rather than as keys one map grows. It is **not** the credential's
* payload — the payload is sealed and comes back open only on the model-call path — and nothing
* secret may ever be put in it. The whole point of a dedicated object is that the values are
* chosen for display: a base URL's *host*, never the URL (whose path is the user's and may name
* a resource), and never any part of a key.
*/
const OpenAICompatibleCredentialDetailsSchema = z.object({ 
/**
* The host of a custom credential's base URL, e.g. `api.example.com` or `127.0.0.1:11434` (a
* port is part of the host). The scheme and the path are dropped: the host is what tells one
* custom endpoint from another in a list.
*/
base_url_host: z.string().min(1) });
/**
* The public facts an Amazon Bedrock credential publishes (epic #245, A3c): its **region**.
*
* A Bedrock request is addressed by region, and one account's keys in one region is one
* credential — so `last4` alone leaves two Bedrock rows indistinguishable. The region is not a
* secret and is what a list shows (`BEDROCK_REGIONS` is the vocabulary the request validates
* against; a `details` read back is a string, the stored value).
*/
const BedrockCredentialDetailsSchema = z.object({ region: z.string().min(1) });
/**
* The public facts a Google Vertex credential publishes (epic #245, A3d): the service-account
* **email**, the **project** and the **location**.
*
* None of the three is a secret, and each is what tells two Vertex credentials apart — one
* service account may serve several projects, and one project several regions. `last4` is the
* private key **id**'s last four characters, an identifier Google prints beside the account, and
* never any part of the private key: the key itself stays inside the sealed payload.
*/
const VertexCredentialDetailsSchema = z.object({
	/** The service account's address, e.g. `vertex@my-project.iam.gserviceaccount.com`. */
	email: z.string().min(1),
	/** The Google Cloud project the models run in. */
	project: z.string().min(1),
	/** The Vertex AI location, one of {@link VERTEX_LOCATIONS}. */
	location: z.string().min(1)
});
/** The fields every credential's metadata carries, whatever its type. */
const ProviderCredentialMetadataBaseSchema = z.object({
	id: ProviderCredentialIdSchema,
	/**
	* The credential's name: the `provider` half of every model id it serves, e.g. `anthropic`
	* or `azure-eu`. Unique per user; for the eleven fixed providers it is the provider id.
	*/
	name: z.string().min(1),
	/** The last four characters of the stored secret, for recognition only. */
	last4: z.string(),
	created_at: TimestampSchema,
	/** When the credential was last added or replaced. */
	updated_at: TimestampSchema,
	/**
	* When the credential last passed validation against the provider. Absent until a save has
	* validated it — a credential is validated on save, so a stored one normally carries this.
	*/
	validated_at: TimestampSchema.optional()
});
/**
* The `api_key` metadata: the fixed providers. It publishes no type-specific facts, so it has no
* `details` — its metadata is byte-for-byte what it was before the field existed.
*/
const ApiKeyProviderCredentialMetadataSchema = ProviderCredentialMetadataBaseSchema.extend({ type: z.literal("api_key") });
/**
* The `azure_openai` metadata: it publishes none either. The resource endpoint is part of the
* sealed payload, and one whose path may name a resource is not a list's to show.
*/
const AzureOpenAIProviderCredentialMetadataSchema = ProviderCredentialMetadataBaseSchema.extend({ type: z.literal("azure_openai") });
/** The `openai_compatible` metadata, with the base URL's host a list may show (#245, A3b). */
const OpenAICompatibleProviderCredentialMetadataSchema = ProviderCredentialMetadataBaseSchema.extend({
	type: z.literal("openai_compatible"),
	details: OpenAICompatibleCredentialDetailsSchema.optional()
});
/** The `bedrock` metadata, with the region a list may show (#245, A3c). */
const BedrockProviderCredentialMetadataSchema = ProviderCredentialMetadataBaseSchema.extend({
	type: z.literal("bedrock"),
	details: BedrockCredentialDetailsSchema.optional()
});
/** The `vertex` metadata, with the email, project and location a list may show (#245, A3d). */
const VertexProviderCredentialMetadataSchema = ProviderCredentialMetadataBaseSchema.extend({
	type: z.literal("vertex"),
	details: VertexCredentialDetailsSchema.optional()
});
/**
* A stored provider credential, as the API returns it: **metadata only**.
*
* The secret itself never appears here or anywhere else in a response — `last4` is what a UI
* shows so a user can tell one key from another. A credential is keyed by its owner and its
* `name`, so there is at most one per name per user: `PUT` replaces it.
*
* A **discriminated union on `type`**: each type carries exactly the public facts it publishes,
* so `details` is typed for the type that has it — a base-URL host on a custom credential, a
* region on a Bedrock one, the email, project and location on a Vertex one — and a type with
* none carries no `details` key at all. That is what keeps the JSON of the types that existed
* before this field byte-for-byte unchanged.
*/
const ProviderCredentialSchema = z.discriminatedUnion("type", [
	ApiKeyProviderCredentialMetadataSchema,
	AzureOpenAIProviderCredentialMetadataSchema,
	OpenAICompatibleProviderCredentialMetadataSchema,
	BedrockProviderCredentialMetadataSchema,
	VertexProviderCredentialMetadataSchema
]);
/**
* The `api_key` form of {@link PutProviderCredentialRequestSchema}: a single secret.
*
* `api_key` is **write-only**. It is accepted on this request and never echoed back: no
* response, event, log line or error message may carry it (epic #65, A5).
*/
const ApiKeyProviderCredentialSchema = z.object({
	type: z.literal("api_key"),
	/** The secret itself. Sent once, stored encrypted, never returned. */
	api_key: z.string().min(1)
});
/** The most deployments one Azure OpenAI credential may carry. */
const MAX_AZURE_DEPLOYMENTS = 64;
/** Whether `value` is an absolute `https:` URL — the only scheme Azure endpoints may use. */
function isHttpsEndpoint(value) {
	try {
		return new URL(value).protocol === "https:";
	} catch {
		return false;
	}
}
/**
* The `azure_openai` form of {@link PutProviderCredentialRequestSchema} (epic #245, A3a).
*
* Azure OpenAI is addressed by deployment, not by a model id, and Microsoft offers no endpoint
* that lists them: the user types the deployment names, and each becomes a model
* (`<credential name>/<deployment>`). The fields are what a request needs besides the name the
* credential is stored under:
*
* - `endpoint` — the resource endpoint, e.g. `https://my-resource.openai.azure.com`. It must be
*   **`https`**; the server additionally refuses one that resolves to a private address
*   (the SSRF guard), so a loopback or metadata endpoint cannot be stored.
* - `api_key` — write-only, exactly like the `api_key` form's.
* - `deployments` — the deployment names. At least one; the server checks the first on save and
*   refuses a credential Azure rejects.
*/
const AzureOpenAICredentialSchema = z.object({
	type: z.literal("azure_openai"),
	/** The Azure OpenAI resource endpoint; `https` only. */
	endpoint: z.string().refine(isHttpsEndpoint, { message: "the endpoint must be an absolute https URL" }),
	/** The secret itself. Sent once, stored encrypted, never returned. */
	api_key: z.string().min(1),
	/** The deployment names; each becomes a model id `<name>/<deployment>`. */
	deployments: z.array(z.string().min(1)).min(1).max(64)
});
/**
* Whether `value` is an absolute `http:` or `https:` URL — the schemes a custom base URL may
* use. `http` is allowed on purpose (a self-hosted endpoint on a private network is the case
* this type exists for); the SSRF guard, not the scheme, is what refuses an address a request
* must not reach.
*/
function isHttpUrl(value) {
	try {
		const protocol = new URL(value).protocol;
		return protocol === "http:" || protocol === "https:";
	} catch {
		return false;
	}
}
/**
* The `openai_compatible` form of {@link PutProviderCredentialRequestSchema} (epic #245, A3b).
*
* Any server that speaks the OpenAI chat-completions API — a self-hosted Ollama or vLLM, a
* gateway, a proxy — is addressed by a base URL the user supplies, and its model ids are
* whatever its `/models` endpoint lists. The fields are what a request needs besides the name
* the credential is stored under:
*
* - `base_url` — the OpenAI-compatible API root, e.g. `https://api.example.com/v1` or
*   `http://127.0.0.1:11434/v1`. It must be an absolute `http`/`https` URL; the server
*   additionally refuses one that resolves to a private address unless the self-host setting
*   allows it (the SSRF guard), and it checks the endpoint on save with `GET {base_url}/models`.
* - `api_key` — write-only when the endpoint wants one, and **optional**: a local server may
*   take no key at all. Exactly like the `api_key` form's, it is accepted on this request and
*   never returned.
*/
const OpenAICompatibleCredentialSchema = z.object({
	type: z.literal("openai_compatible"),
	/** The OpenAI-compatible API root; an absolute `http`/`https` URL. */
	base_url: z.string().refine(isHttpUrl, { message: "the base URL must be an absolute http or https URL" }),
	/** The secret, when the endpoint wants one. Sent once, stored encrypted, never returned. */
	api_key: z.string().min(1).optional()
});
/**
* The `bedrock` form of {@link PutProviderCredentialRequestSchema} (epic #245, A3c).
*
* Amazon Bedrock authenticates with an IAM principal's static access keys, and a request is
* addressed by **region** — there is no endpoint to type, and no deployment to name: the
* catalog is the region's own `ListFoundationModels`, and a model id's second half is a Bedrock
* model id (`anthropic.claude-sonnet-4-20250514-v1:0`). The fields are therefore the three
* credentials SigV4 signs with, plus the region:
*
* - `access_key_id` — the access key ID (`AKIA…`). Sent once, stored encrypted, never returned;
*   only its last four characters come back, as `last4`.
* - `secret_access_key` — the secret. Write-only, exactly like the `api_key` form's.
* - `session_token` — the token temporary credentials (STS, SSO, an assumed role) carry, when
*   there is one. Optional: long-lived IAM user keys have none.
* - `region` — an AWS region from {@link BEDROCK_REGIONS}. Validated against that list, not
*   accepted as free text: the region is spliced into an AWS hostname, and a string that is not
*   a real region could not name one (`bedrock.ts` has the rule and the list).
*
* The whole payload is **sealed as one secret** — keys, token and region together. The region is
* not itself secret, but a credential is stored as the single JSON value its type parsed to, so
* there is one sealed blob per credential and no second place a field could live; the region
* also rides on the metadata as `details.region`, which is what a list shows.
*
* // extension: assume-role and instance-profile credentials are out of v1 (epic #245, decision
* M4). A role ARN is a different shape and a different signing path, and the save-time check
* cannot complete an `sts:AssumeRole` on a user's behalf without a trust relationship that says
* it may. Static keys are checked on save; a role would have to be checked by assuming it.
*/
const BedrockCredentialSchema = z.object({
	type: z.literal("bedrock"),
	/** The IAM access key ID; `last4` is its last four characters. */
	access_key_id: z.string().min(1),
	/** The IAM secret access key. Sent once, stored encrypted, never returned. */
	secret_access_key: z.string().min(1),
	/** The session token temporary credentials carry; absent for long-lived IAM user keys. */
	session_token: z.string().min(1).optional(),
	/** The AWS region the credential runs in; one of {@link BEDROCK_REGIONS}. */
	region: z.enum(BEDROCK_REGIONS)
});
/** The fields {@link ServiceAccountKey} requires, in the order an error message lists them. */
const SERVICE_ACCOUNT_FIELDS = [
	"project_id",
	"private_key_id",
	"private_key",
	"client_email"
];
/**
* The service-account key in `value` — the JSON text Google's console hands out — or `null`
* when it is not one.
*
* The check is a real one rather than a look at the file name: the document must parse, must
* say `type: service_account`, and must carry the fields a request needs. It is what the
* protocol validates a `vertex` payload with, and the server parses the same document again
* when it turns the payload into a credential; the one parser is what keeps the two from
* disagreeing about what a service-account key is.
*/
function parseServiceAccountKey(value) {
	let parsed;
	try {
		parsed = JSON.parse(value);
	} catch {
		return null;
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
	const document = parsed;
	if (document.type !== "service_account") return null;
	for (const field of SERVICE_ACCOUNT_FIELDS) {
		const value = document[field];
		if (typeof value !== "string" || value.trim().length === 0) return null;
	}
	return {
		type: "service_account",
		project_id: document.project_id,
		private_key_id: document.private_key_id,
		private_key: document.private_key,
		client_email: document.client_email
	};
}
/** Whether `value` is a service-account key — what a `vertex` payload's JSON is checked with. */
function isServiceAccountKey(value) {
	return parseServiceAccountKey(value) !== null;
}
/**
* The Google Cloud regions a Vertex credential may name.
*
* The location is not free text because it is not only a label: it is the **host** every
* request goes to (`<location>-aiplatform.googleapis.com`), so a typo or an invented region
* would be a credential that saves and then cannot make one request. The list is Google's
* published set of Vertex AI locations, `global` included — the endpoint for models that are
* served from a global host rather than a region.
*/
const VERTEX_LOCATIONS = [
	"global",
	"us-central1",
	"us-east1",
	"us-east4",
	"us-east5",
	"us-south1",
	"us-west1",
	"us-west4",
	"northamerica-northeast1",
	"southamerica-east1",
	"europe-central2",
	"europe-north1",
	"europe-southwest1",
	"europe-west1",
	"europe-west2",
	"europe-west3",
	"europe-west4",
	"europe-west6",
	"europe-west8",
	"europe-west9",
	"asia-east1",
	"asia-east2",
	"asia-northeast1",
	"asia-northeast2",
	"asia-northeast3",
	"asia-south1",
	"asia-south2",
	"asia-southeast1",
	"asia-southeast2",
	"australia-southeast1",
	"australia-southeast2",
	"me-central1",
	"me-central2",
	"me-west1",
	"africa-south1"
];
/**
* A Google Cloud **project id**: lowercase, six to thirty characters with a letter at each end.
*
* Google's own rule, checked here so a typo is a 400 next to the field rather than a 403 from
* Vertex on save. A project *number* is not accepted: the key's own `project_id` is an id, and
* the document the reader pasted carries the one they mean.
*/
const GCP_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
/**
* The `vertex` form of {@link PutProviderCredentialRequestSchema} (epic #245, A3d).
*
* Vertex AI authenticates with a Google Cloud **service-account key**: the JSON document the
* console hands out, which carries the private key that signs a short-lived OAuth token. The
* fields are:
*
* - `service_account` — that document, **as text**, exactly as it was pasted or uploaded. It is
*   sealed whole (the server never re-serialises it into a different shape) and handed whole to
*   the client library, which is why this is a string and not a modelled object: the document
*   is Google's, not this protocol's.
* - `project` — the project the models are run in, and the one the save-time check lists
*   publisher models from. It defaults, in the forms, to the document's own `project_id`; a
*   service account with access to several projects may name another.
* - `location` — one of {@link VERTEX_LOCATIONS}. Google has no endpoint that lists regions, so
*   the list above is the check, and the host is derived from the value rather than typed.
*
* `gcp_service_account` is deliberately **not** a type of its own: this one is a Vertex
* credential, and the key is the form its authentication takes. Workload identity federation,
* an ADC-only setup and a bare project id are all refused — decision M4, and the reason is the
* same one that makes this protocol carry the key rather than read it from the environment: the
* server runs on GCP, so an ADC fallback would silently run a user's chat on openharness's own
* service account.
*/
const VertexCredentialSchema = z.object({
	type: z.literal("vertex"),
	/** The service-account key JSON, as text. Write-only: never returned, logged or echoed. */
	service_account: z.string().refine(isServiceAccountKey, { message: "the service account must be the JSON key file Google Cloud issued for a service account (its `type` is `service_account` and it carries the key); download one from the service account's Keys page" }),
	/** The Google Cloud project the models run in. */
	project: z.string().refine((value) => GCP_PROJECT_ID_PATTERN.test(value), { message: "the project must be a Google Cloud project id, e.g. `my-project-123456`" }),
	/** The Vertex AI location; the host every request goes to is derived from it. */
	location: z.enum(VERTEX_LOCATIONS)
});
/**
* Body of `PUT /v1/provider-credentials/{name}`. Response: {@link ProviderCredentialSchema}.
*
* A discriminated union on `type`. The path's `name` is the credential's name — the provider
* half of the model ids it serves; for the eleven fixed providers it is the provider id. The
* body carries the secret.
*
* A fresh session is required to add, replace or delete a credential (epic #65, A2): the
* server refuses a stale one. A credential that fails validation on save answers 422
* `invalid_provider_credential`.
*/
const PutProviderCredentialRequestSchema = z.discriminatedUnion("type", [
	ApiKeyProviderCredentialSchema,
	AzureOpenAICredentialSchema,
	OpenAICompatibleCredentialSchema,
	BedrockCredentialSchema,
	VertexCredentialSchema
]);
/**
* The {@link ProviderCredentialDetails} a PUT body's type publishes, or `undefined` for a type
* with none (epic #245, A3b).
*
* This is the whole mapping from "what a user saved" to "what the metadata may show", and it
* lives here — beside the schemas — because two sides must agree on it: the server derives a
* credential's stored `details` from the body it seals, and a client that fakes the server
* (the web app's and the TUI's `@openharness/client/testing`) must produce the same answer.
* Only facts safe to publish are read out — a custom base URL's **host**, never its path and
* never any part of a key; a Bedrock credential's **region**; and a Vertex credential's
* service-account **email**, **project** and **location**, read through
* {@link parseServiceAccountKey} so the email comes from the document that was sealed and never
* from a second parse. No part of a private key is ever returned, logged or echoed.
*/
function credentialDetails(body) {
	if (body.type === "openai_compatible") return { base_url_host: new URL(body.base_url).host };
	if (body.type === "bedrock") return { region: body.region };
	if (body.type === "vertex") {
		const key = parseServiceAccountKey(body.service_account);
		if (key === null) return;
		return {
			email: key.client_email,
			project: body.project,
			location: body.location
		};
	}
}
/**
* Response of `GET /v1/provider-credentials`: every credential the caller owns, metadata
* only.
*
* No pagination envelope: a user has one credential per name at most, so the list is short by
* construction. The array is empty, never absent, when nothing is stored — which is also the
* state a fresh account starts in.
*/
const ListProviderCredentialsResponseSchema = z.object({ data: z.array(ProviderCredentialSchema) });
//#endregion
//#region src/resources/session.ts
/**
* The `session` resource and the endpoints that manage it:
*
* - `POST   /v1/sessions`
* - `GET    /v1/sessions`
* - `GET    /v1/sessions/{session_id}`
* - `DELETE /v1/sessions/{session_id}`
* - `POST   /v1/sessions/{session_id}/compact` (`/compact [instructions]`, epic #277 K8; #283)
*
* A session is a durable, append-only event log; the resource here is the header of that log.
* Its `status` mirrors the last status event in the log, and its `model` and `system` are the
* configuration it runs, always set — frozen at creation time, along with the `agent` preset
* it was created from, when there was one. The one exception to "frozen": a `user.message`
* carrying a `model` switches what the session runs from that message on (#111), and a
* `user.message` carrying a `mode` switches — or detaches — the mode a chat follows (#245, M6).
*
* `DELETE` answers `204` and removes the session and its whole log (epic #116, U5) — an
* owner-scoped operation, so another user's session answers `404`. It is the explicit
* exception to the append-only rule, alongside compaction, and it is irreversible; open
* streams for the session receive a final `session.deleted` event and close.
*
* Since epic #340 a session may belong to another one: `parent_session_id`, `parent_task_id`,
* `root_session_id` and `depth` place it in a tree, and `interactive` says whether a person is
* on the other end. Both are fixed at creation, and the tree is the server's to write — no
* request carries it.
*/
/** Longest session title Anthropic accepts. */
const SESSION_TITLE_MAX_LENGTH = 500;
/**
* How many initial events a session may be created with. Anthropic's limit.
*/
const MAX_INITIAL_EVENTS = 50;
/**
* The session's state: `idle` when the agent is waiting for input, `running` while it works.
*
* Anthropic's `SessionStatus` also has `rescheduling` and `terminated`. openharness models
* retries as `session.status_rescheduled` events in the log rather than as a resting status,
* and v1 has no termination, so the resource reports only these two (see `AGENTS.md`). The
* event log remains the precise record; this field is a summary of it.
*/
const SessionStatusSchema = z.enum(["idle", "running"]);
/**
* The agent a session runs, snapshotted when the session was created — or `null` for a
* session created from a model alone.
*
* Editing the agent afterwards does not change existing sessions: a session replays to the
* same conversation forever, which is what makes the log the source of truth. The snapshot is
* the preset the session was created *from*, not the configuration it runs: what it runs is
* {@link SessionSchema}'s `model` and `system`, which the request may have overridden.
*
* Anthropic's `BetaManagedAgentsSessionAgent` also carries `description`, `mcp_servers`,
* `skills`, `tools` and `version`; v1 keeps the four fields the brain needs (see
* `AGENTS.md`).
*/
const SessionAgentSchema = z.object({
	id: AgentIdSchema,
	name: z.string().min(1).max(256),
	model: ModelConfigSchema,
	system: z.string().nullable()
});
/** The `session` resource. */
const SessionSchema = z.object({
	id: SessionIdSchema,
	type: z.literal("session"),
	/**
	* // extension: the user this session belongs to (epic #65, A4).
	*
	* Read-only, set by the server from the authenticated caller at creation; no request ever
	* carries it. A session whose owner is not the caller answers `404`, exactly as an agent
	* does, so a session's existence never leaks across users.
	*
	* Required since #61: the server sets it on every session it creates, so a session without
	* one cannot exist — and a response that omits it fails this schema rather than passing as
	* unowned.
	*/
	owner_id: UserIdSchema,
	status: SessionStatusSchema,
	title: z.string().max(500).nullable(),
	metadata: MetadataSchema,
	/**
	* // extension: the configuration the session actually runs, always set (issue #93).
	*
	* A session no longer has to be created from an agent: it is created from a model, and an
	* agent — when there is one — is the preset it snapshotted. `model` is that effective
	* model: the agent's, the request's override of it, or the inline model of an agent-less
	* session. Anthropic has no equivalent field; there the session's `agent` always carries
	* the model.
	*/
	model: ModelConfigSchema,
	/**
	* // extension: the effective system prompt, always present (issue #93).
	*
	* The agent's `system`, the request's override of it, or `null` — for an agent-less session
	* whose request named no `system`. Like `model`, an Anthropic session has no field of its
	* own for it.
	*/
	system: z.string().nullable(),
	/**
	* // extension: the mode this chat follows (#245, M6), or `null` for a chat without one.
	*
	* A chat started from a mode follows it live: the next request resolves the mode as it is
	* now, and runs its model, effort and prompt addition — even after the mode was edited. It
	* is set by the message or create request that picked the mode, and cleared by a message
	* that picks a plain `model` or by deleting the mode, which lands the chat on the model it
	* last ran. `model` above stays the resolved model the session last ran, so a chat whose
	* mode is gone still has a model to continue on.
	*/
	mode: ModeIdSchema.nullable(),
	/**
	* The preset the session was created from, snapshotted — or `null` for a model-first
	* session. `model` and `system` above are what the session runs; this is where it came from.
	*/
	agent: SessionAgentSchema.nullable(),
	/**
	* // extension: whether a person is on the other end (epic #340, S1).
	*
	* Set when the session is created (a `POST /v1/sessions` may ask for a non-interactive one)
	* and **never changed**. A non-interactive session's model cannot stop to ask the user
	* anything: `ask_user` is not offered to it at all, and a call whose effective policy is
	* `ask` is answered at once with an error result rather than pausing the turn. A person may
	* still send it messages and interrupt it — "non-interactive" is about the model asking,
	* not about the user being locked out.
	*
	* Subagents are its first user (#344); `oh -p`, API runs and scheduled runs reuse it later.
	* Anthropic has no equivalent: there a session is as interactive as its client makes it.
	*/
	interactive: z.boolean(),
	/**
	* // extension: the session this one belongs to, or `null` for a top-level chat
	* (epic #340, S3).
	*
	* A subagent is a real session — the brain, the scheduler, compaction, usage, replay and
	* crash recovery work on it unchanged — that belongs to another session. Children are not
	* top-level chats: `GET /v1/sessions` lists `parent_session_id: null` by default and
	* `?parent_session_id=` lists one session's children. A child inherits no history: its
	* first `user.message` is the prompt its parent wrote.
	*
	* Read-only, and **not client-settable**: only the server creates children (#344).
	*/
	parent_session_id: SessionIdSchema.nullable(),
	/**
	* // extension: the task this session was started for, or `null` (epic #340, S3; S4).
	*
	* The `task_` id the parent's `session.task_started` carries (#343), so a child session can
	* be traced back to the card it belongs to. `null` for a top-level chat and for a child the
	* server created outside a task.
	*
	* Read-only, and **not client-settable**: only the server creates children.
	*/
	parent_task_id: TaskIdSchema.nullable(),
	/**
	* // extension: the top-level chat this session belongs to — its own id when it *is* the
	* top-level chat (epic #340, S3).
	*
	* Every session in a tree shares one root, which is what says which tree a session belongs
	* to: S6's live cap is asked **from** the root (`countLiveDescendants(root_session_id)`), and
	* "every live descendant of this root" is one walk of the subtree. It is **not** a filter any
	* query makes: a tree read is a recursion over `parent_session_id`, because a root column
	* cannot answer "every descendant of *this* session" — the branch under a middle child — and
	* the walks are what `listDescendants`, the live count and the delete cascade share.
	* Read-only, and not client-settable: the store derives it from the parent at creation.
	*/
	root_session_id: SessionIdSchema,
	/**
	* // extension: how far below the top-level chat this session sits (epic #340, S3).
	*
	* `0` for a top-level chat, and one more than its parent's depth for a child, so it is what
	* S6's nesting limit compares against. Read-only, and not client-settable: the store derives
	* it from the parent at creation.
	*/
	depth: z.number().int().nonnegative(),
	created_at: TimestampSchema,
	updated_at: TimestampSchema
});
/**
* Body of `POST /v1/sessions`. Response: {@link SessionSchema}.
*
* A session is created from an agent, a model, or both, and **at least one of the two is
* required** — the refinement below is where the requirement is stated, so a request naming
* neither fails with a clear message rather than creating a session that cannot run.
*
* - **From an agent.** `agent` is an `agent_` id: the session snapshots the current agent
*   configuration (its name, model and system) and runs it. An explicit `model` or `system`
*   in the request overrides what the agent contributes.
* - **From a model.** Without an `agent`, `model` is required and `system` defaults to
*   `null`: this is model-first chat, where the user picks a model and the agent is an
*   optional preset (epic #92).
* - **From a mode** (#245, M6). `mode` is a `mode_` id: the session is created on the user's
*   mode, which the server resolves to a model for the session's header. A `mode` this way
*   stands in for `model`, and the session follows the mode live from then on.
*
* Anthropic additionally accepts an inline agent reference with a `version`, or one with
* per-session model overrides; openharness has neither agent versioning nor overrides in that
* shape (see `AGENTS.md`) — its overrides are these top-level `model` and `system` fields.
*/
const CreateSessionRequestSchema = z.object({
	/** The agent whose configuration the session snapshots. Required unless `model` is given. */
	agent: AgentIdSchema.optional(),
	/** The model the session runs, `provider/model`. Overrides the agent's; required without one. */
	model: ModelConfigSchema.optional(),
	/** The system prompt the session runs with. Overrides the agent's; `null` without one. */
	system: z.string().nullable().optional(),
	/**
	* // extension: the id of the user's mode the session is created on (#245, M6). The
	* server resolves it to the session's header model; the chat then follows the mode live.
	*/
	mode: ModeIdSchema.optional(),
	/**
	* // extension: whether a person is on the other end (epic #340, S1). Omitted, the session
	* is interactive — a chat someone is typing into. `false` creates one whose model cannot
	* stop to ask anything: `ask_user` is never offered to it and an `ask` call is answered
	* with an error instead of pausing. Set once, at creation; there is no way to change it.
	*
	* The session's **tree** fields (`parent_session_id`, `parent_task_id`,
	* `root_session_id`, `depth`) are deliberately absent here: only the server creates a
	* child session (#344), and a value a client sent would be stripped like any unknown
	* field.
	*/
	interactive: z.boolean().optional(),
	title: z.string().max(500).nullable().optional(),
	metadata: MetadataSchema.optional(),
	/**
	* Events to append to the new session's log, in order, before it starts running. They are
	* stored exactly as events sent later to the events endpoint would be.
	*/
	initial_events: z.array(UserEventInputSchema).max(50).optional()
}).refine((request) => request.agent !== void 0 || request.model !== void 0 || request.mode !== void 0, { message: "a session needs an agent, a model or a mode: pass \"agent\", \"model\" for a model-first session, or \"mode\" for one on a mode" });
/**
* Body of `POST /v1/sessions/{session_id}/compact` (epic #277, K8; #283).
*
* The user's optional guidance for the summary — "keep the API decisions in detail" — bounded by
* {@link COMPACT_INSTRUCTIONS_MAX_LENGTH}. The request always produces a stored `session.compact`
* event; the body has no `type` because the endpoint, not the caller, decides that. Omit it (or
* send `{}`) for a plain `/compact`.
*/
const CompactSessionRequestSchema = z.object({ 
/** The user's guidance for the summary, or omitted for none. */
instructions: z.string().min(1).max(COMPACT_INSTRUCTIONS_MAX_LENGTH).optional() });
/**
* Response of `POST /v1/sessions/{session_id}/compact`: the stored — or already pending —
* `session.compact` request, deep-readonly.
*
* The route is idempotent while a request is pending, so `data` is the request this call
* produced, or the one already waiting for an answer. A client reads the outcome, when there is
* one, from the log or the stream — a `session.compaction` after this event.
*/
const CompactSessionResponseSchema = z.object({ data: SessionCompactEventSchema });
/**
* Query parameters of `GET /v1/sessions`.
*
* The list is ordered by `(created_at, id)`, newest first, and `page` resumes at a keyset
* position in it — not at an item offset, which would shift while a client pages through a
* list that sessions are still being added to.
*
* The list is also **the top-level chats** by default: a session a chat started (#344) is
* part of its parent, not a chat of its own, so it is left out unless it is asked for. Naming
* a `parent_session_id` answers that session's children instead.
*/
const ListSessionsQuerySchema = z.object({
	/** Maximum results per page. Defaults to `DEFAULT_PAGE_LIMIT`, capped at `MAX_PAGE_LIMIT`. */
	limit: PageLimitSchema.optional(),
	/** Cursor from a previous response's `next_page`: the `(created_at, id)` of its last session. */
	page: PageCursorStringSchema.optional(),
	/** Return only sessions created with this agent. Anthropic's `agent_id` filter. */
	agent_id: AgentIdSchema.optional(),
	/**
	* // extension: return only this session's children (epic #340, S3). Omitted, the list is
	* the caller's top-level chats — every session whose `parent_session_id` is `null`.
	*/
	parent_session_id: SessionIdSchema.optional()
});
/** Response of `GET /v1/sessions`: the Anthropic list envelope. */
const ListSessionsResponseSchema = z.object({
	data: z.array(SessionSchema),
	next_page: NextPageSchema
});
//#endregion
//#region src/resources/usage.ts
/**
* The usage surface — `GET /v1/sessions/{session_id}/usage` and `GET /v1/me/usage`
* (epic #245, A2; issue #247) — the shapes of what a session and a user spent.
*
* ```
* GET /v1/sessions/{session_id}/usage  -> { session_id, totals, cost, by_model, descendants, searches }
* GET /v1/me/usage?from=&to=&tz=       -> { from, to, tz, totals, cost, by_model, by_day, searches }
* ```
*
* Both are **reads of the log**, and both answer in the same currency of facts:
*
* - **Tokens are stored, cost is computed.** Every request's counters are in its
*   `span.model_request_end`; a price is not in the log at all. So a response is assembled when
*   it is asked for, from the tokens the log holds and the model catalog's prices, and nothing
*   about cost is ever written down (epic #245).
* - **Searches are counted, and priced where a rate is published** (epic #303, #305; #335).
*   `searches` is how many the covered log holds, and the fee a provider charges **beside** the
*   tokens goes into the same `cost` the token rates do (`./cost.ts`'s `SEARCH_PRICE_PER_CALL`)
*   — a count for the providers that charge nothing extra, and never a made-up number for one
*   nobody publishes a rate for.
* - **A price nobody published is named, not guessed.** A request whose model the catalog has no
*   price for contributes its tokens and nothing to the cost; a total **sums the priced requests
*   and counts the unpriced ones** (`unpriced_requests`), and its `cost` is `null` only when
*   nothing in it could be priced (decided 2026-10-09). One such request no longer makes a whole
*   session unreadable, and no part of the money is ever an estimate. `null` is what a client
*   renders as "—".
* - **Every route is scoped to its owner.** The session route is the caller's session or a 404
*   (A4); the user route can only ever be the caller — there is no id in the path — so a user
*   never sees another's usage, and there is no operator-wide view at all.
* - **Usage is broken down by model, never by mode.** A session may switch models
*   mid-conversation (epic #116, U3), so `by_model` is what tells the cheap requests from the
*   expensive ones; there is no second axis.
* - **A session's totals include its subtree** (epic #340, S8; #344). A subagent is a real
*   session with its own log, so its requests are billed to it; the session route sums the
*   whole tree anyway — a chat that delegates should not look cheap — and `descendants` is the
*   subtree's own share of the same numbers. The per-user route is unchanged: every session it
*   counts is already there on its own, ancestor or not.
*
* // extension: the shapes are openharness's own. Anthropic tracks usage per session and
* reports it on the session object and in `session.usage` events; it has no per-user, per-range
* usage endpoint, and its cost figures are platform-computed and stored rather than derived
* from the reader's own vendored prices.
*/
/**
* The token counters of a usage total: the four a `span.model_request_end` reports, summed over
* every request the total covers.
*
* The protocol has one shape for these four numbers — {@link ModelUsageSchema} — and this is the
* name a usage response reads them under, because the total of a session is not one request's
* usage. A counter is always present and always a number: a model that reports nothing about a
* counter reports `0` for it (the brain's `toModelUsage` is where that happens), so "unknown"
* is never spelled as a missing count — it is the absence of a *cost* that says so.
*/
const UsageTotalsSchema = ModelUsageSchema;
/**
* A cost in US dollars: a non-negative number of dollars, at the list prices the model catalog
* carries.
*
* `null` — on every shape below — means **unknown**, and it is the only way this package says
* so: a model the catalog has no price for, or one whose cache rates nobody published, makes
* the request it belongs to unpriced rather than free. Whole cents are not rounded into the wire
* type: rounding is a presentation decision, and the reader is the one who knows how many digits
* it wants.
*/
const MoneySchema = z.number().nonnegative().nullable();
/**
* The money of a total: `cost`, the sum of the requests that could be priced, and
* `unpriced_requests`, how many had no price (epic #245, A2; #247, decided 2026-10-09).
*
* Every total below carries these two fields together, so the shape is defined once here. A total
* **sums the priced requests and counts the unpriced ones** instead of turning unknown as soon as
* one request has no price: `cost` is the sum, or `null` when nothing in the total was priced,
* and `unpriced_requests` names the unknown part — it is never folded into the number as an
* estimate. A reader shows "—" for a `null` cost, and renders a count beside a number.
*/
const TotalCostSchema = z.object({
	/** The sum of the priced requests, or `null` when nothing in the total could be priced. */
	cost: MoneySchema,
	/** How many requests in the total had no published price. */
	unpriced_requests: z.number().int().nonnegative()
});
/**
* One model's share of a usage answer: its totals, how many requests ran on it, and what they
* cost.
*
* `requests` is a count of `span.model_request_end` events, so a reply the brain retried counts
* twice — which is right: both attempts really ran, and both are really in the tokens.
* `unpriced_requests` is how many of those requests had no price: they are among the `requests`
* and their tokens are in `usage`, but no money is claimed for them.
*/
const ModelUsageBreakdownSchema = z.object({
	/** The `provider/model` the requests named — the model that served them. */
	model: z.string().min(1),
	/** The tokens those requests reported, summed. */
	usage: UsageTotalsSchema,
	/** How many requests ran on this model. */
	requests: z.number().int().nonnegative(),
	...TotalCostSchema.shape
});
/**
* How many searches an answer covers (epic #303, #305).
*
* A count and not a price: the deployment pays the search provider, the operator's plan is not
* in this repository, and inventing a rate for one would be exactly the estimate the usage
* surface refuses to make (epic #245). So a search is counted — the number of `web_search`
* calls the log holds — and no money is claimed for it.
*
* It is a sibling of the token totals rather than a member of them, because tokens and searches
* are not the same kind of thing: `totals` is a `ModelUsage` of four counters, and a reader that
* summed a search count into one would be adding apples to oranges.
*/
const UsageSearchesSchema = z.number().int().nonnegative();
/**
* What a chat's **descendants** spent (epic #340, S8; #344): the same totals the session's own
* are, over the subtree under it and nothing else.
*
* A subagent is a real session with its own log, so its tokens are billed to it — but a chat
* that delegates work should not look cheap, which is why the session route's `totals`, `cost`
* and `by_model` cover the whole tree and this object says how much of that was the helpers'.
* The shape is the session's own without `session_id`: the four counters, the cost and its
* `unpriced_requests`, the per-model split and the search count.
*/
const DescendantUsageSchema = z.object({
	/** Every request the chat's descendants made, summed. */
	totals: UsageTotalsSchema,
	/** What `totals` cost, and how many of those requests had no price. */
	...TotalCostSchema.shape,
	/** The descendants' totals per model, biggest first. */
	by_model: z.array(ModelUsageBreakdownSchema),
	/** How many `web_search` calls the descendants made; see {@link UsageSearchesSchema}. */
	searches: UsageSearchesSchema
});
/**
* Response of `GET /v1/sessions/{session_id}/usage`: everything one session spent.
*
* Owner-scoped: another user's session is the 404 an unknown id gets (A4). The route reads the
* session's whole log — skipping what a rewind replaced, so a branch the reader took back is not
* billed — and answers the totals over all of it, with the per-model split beside them.
*
* Since #344 the totals cover the session **and every session below it in the tree** (S8), so a
* chat's cost includes the work it delegated; `descendants` carries the subtree's own share of
* those numbers, and a session with no children answers zeroes in it.
*
* A session whose log holds no `span.model_request_end` at all (a chat with no reply yet)
* answers zeroed totals, an empty `by_model` and a `null` cost: nothing was spent, and nothing
* was priced either. `searches` is `0` in that case too — a count, which has no unknown.
*/
const SessionUsageSchema = z.object({
	/** The session these totals are for. */
	session_id: SessionIdSchema,
	/** Every request the session and its descendants made, summed. */
	totals: UsageTotalsSchema,
	/** What `totals` cost, and how many of the requests in it had no price. */
	...TotalCostSchema.shape,
	/** The same totals per model, biggest first. */
	by_model: z.array(ModelUsageBreakdownSchema),
	/** What the sessions below this one spent (S8; #344) — zeroed when there are none. */
	descendants: DescendantUsageSchema,
	/** How many `web_search` calls this session and its descendants made. */
	searches: UsageSearchesSchema
});
/**
* One local day's usage (issue #247).
*
* `day` is a calendar day in the zone the request named: the request timestamps are grouped by
* the day they fell on *there*, which is the only reading of "today" a reader recognises. A day
* with no request is **absent** from `by_day` rather than present as a zero — the same rule the
* rest of this protocol follows about unknown and empty.
*/
const DailyUsageSchema = z.object({
	/** The local day, `YYYY-MM-DD`, in the response's `tz`. */
	day: LocalDaySchema,
	/** What was spent in it. */
	totals: UsageTotalsSchema,
	/** What `totals` cost that day, and how many of its requests had no price. */
	...TotalCostSchema.shape,
	/** How many `web_search` calls that day made. */
	searches: UsageSearchesSchema
});
/**
* Response of `GET /v1/me/usage`: what the caller spent between two local days, by model and by
* day.
*
* The range is inclusive on both ends and is read in `tz` — the caller's own zone, which the web
* app reads from `Intl.DateTimeFormat().resolvedOptions().timeZone` and `oh` the same way in
* Node. `tz` is echoed back because a day means different instants in different zones: a
* response says which reading of "the 3rd" it answered.
*
* `by_model` is the range as a whole; `by_day` is the same requests cut by local day, so the two
* answer different questions and neither is derived from the other on the wire (a client that
* shows a chart of the days needs the days, and one that shows the expensive models needs the
* models). Both are ordered — models by total tokens descending (then by id, so the order is
* total whatever the prices turn out to be), days ascending — so a client renders them as they
* arrive.
*/
const UserUsageSchema = z.object({
	/** The first local day the totals cover, inclusive. */
	from: LocalDaySchema,
	/** The last local day the totals cover, inclusive. */
	to: LocalDaySchema,
	/** The IANA zone the days were read in, e.g. `Asia/Kolkata`; `UTC` when the caller named none. */
	tz: z.string().min(1),
	/** Every request in the range, summed. */
	totals: UsageTotalsSchema,
	/** What `totals` cost, and how many requests in the range had no price. */
	...TotalCostSchema.shape,
	/** The range's totals per model, biggest first. */
	by_model: z.array(ModelUsageBreakdownSchema),
	/** The range's totals per local day, ascending; days with no request are absent. */
	by_day: z.array(DailyUsageSchema),
	/** How many `web_search` calls the range made; see {@link UsageSearchesSchema}. */
	searches: UsageSearchesSchema
});
/**
* Query parameters of `GET /v1/me/usage`.
*
* `from` and `to` default to the **current month so far** in `tz` (the first of the month
* through today, both inclusive) — the range the Settings screen opens on. `tz` defaults to
* `UTC`, and a zone the server does not recognise is a 400 `invalid_request_error` rather than
* a silently different answer. A `from` after `to` is a 400 too.
*/
const UserUsageQuerySchema = z.object({
	/** The first day to include; defaults to the first of the current month in `tz`. */
	from: LocalDaySchema.optional(),
	/** The last day to include; defaults to today in `tz`. */
	to: LocalDaySchema.optional(),
	/** An IANA time zone, e.g. `Europe/Berlin`; defaults to `UTC`. */
	tz: z.string().min(1).optional()
});
//#endregion
//#region src/titles.ts
/**
* Naming a session after something said in it.
*
* A title is a **label**, never a summary, and deriving one is deliberately dumb: the first
* non-empty line, whitespace collapsed, cut to fit `SESSION_TITLE_MAX_LENGTH`. Something
* smarter (a model, a keyword extractor) would name sessions better and is not the point —
* this has to be instant, free, offline and the same every time, because a list that renames
* itself under its reader is worse than one that shows a truncated first line.
*
* It lives here rather than in the server because **two packages need it and neither is the
* other's host** (epic #340): the server derives a title from the first `user.message` a chat
* is sent (`apps/server/src/titles.ts`), and the brain derives one from
* the `description` a parent model gave in its `agent` tool call (#344) — a child session is
* created by the brain through the store, so the server's route-level auto-titling never sees
* it. Two copies of "the first line, collapsed, truncated" would be two answers to what a title
* is, and a title that fits one limit but not the other is a write the log refuses.
*
* Nothing here is the wire: no schema imported a shape from this file, and a frontend is free
* to ignore it (the web app and `oh` read the title the server stored, they never derive one).
*/
/** What a title that had to be cut ends with. Part of the length, not extra. */
const ELLIPSIS = "…";
/** Half a code point, at the end of a string: a cut that must not land there. */
const HIGH_SURROGATE_END = /[\uD800-\uDBFF]$/;
/**
* The title for a session whose naming text is `text`, or `null` when there is no text to name
* it after.
*
* The first non-empty line wins: a message that opens with `Fix the SSE reload bug` and then
* explains itself for forty lines is listed under the first line. Whitespace inside that line
* is collapsed to single spaces and the ends are trimmed, so a title is one clean line whatever
* the input looked like. A line longer than {@link SESSION_TITLE_MAX_LENGTH} is cut to fit, the
* ellipsis included; the cut never lands in the middle of a surrogate pair.
*
* @param text what names the session: a first message's text, or a task's description
*/
function deriveSessionTitle(text) {
	const line = text.split(/\r\n|\r|\n/).find((candidate) => candidate.trim() !== "");
	if (line === void 0) return null;
	return truncateTitle(line.trim().replace(/\s+/gu, " "));
}
/**
* Cut a title to the protocol's limit.
*
* The ellipsis takes the place of the last character rather than extending the string, so the
* result is at most `SESSION_TITLE_MAX_LENGTH` — a title the log can always store.
*/
function truncateTitle(title) {
	if (title.length <= 500) return title;
	const kept = title.slice(0, 499);
	return (HIGH_SURROGATE_END.test(kept) ? kept.slice(0, -1) : kept) + ELLIPSIS;
}
//#endregion
//#region src/todo.ts
/**
* The `todo_write` tool's vocabulary, and the one way to read a list back out of a log
* (epic #303, [#305](https://github.com/amirtuval/openharness/issues/305)).
*
* A model that is working through several steps can be given a list, and the tool it writes one
* with replaces the **whole** list every time — there is no per-item edit, so "the list" is one
* value rather than a sequence of mutations. Nothing stores it beside the call: the newest
* `todo_write` call and its result **are** the state, which is the same rule every other piece
* of the log follows (D9) — a client replaying a session sees the list the model last wrote, and
* a session that never wrote one has none.
*
* `readTodoList` is that reading, and it lives here because **this is what both sides of the
* log can reach**: the brain writes the call, and the web app and `oh` render it, and both
* import this package. What a list *looks like* on screen is the client's
* ([#308](https://github.com/amirtuval/openharness/issues/308)).
*/
/** The name the model calls the tool by, and the name a reader looks for in the log. */
const TODO_WRITE_TOOL_NAME = "todo_write";
/**
* What one item's state is.
*
* `pending` is not started, `in_progress` is what the model is on now, `done` is finished —
* Anthropic's own three states, and the ones a client renders differently.
*/
const TodoStatusSchema = z.enum([
	"pending",
	"in_progress",
	"done"
]);
/** One item of the list: what to do, and where it stands. */
const TodoItemSchema = z.object({
	/** The task, as the model wrote it. */
	content: z.string().min(1),
	/** Where the task stands. */
	status: TodoStatusSchema
});
/**
* The whole list, in the order the model wrote it.
*
* An empty list is meaningful — it is a model clearing a list it no longer needs — and is told
* apart from "no list has ever been written" by {@link readTodoList}'s `null`.
*/
const TodoListSchema = z.array(TodoItemSchema);
/**
* What a `todo_write` call carries: the whole list.
*
* An object wrapping the array rather than the array itself, because that is what a tool call's
* input must be ({@link ToolInput}): a model's arguments are an object, and a call whose
* arguments were a bare array is not a call any registered tool can be run with. The array is
* the list as the issue spells it.
*/
const TodoWriteInputSchema = z.object({ todos: TodoListSchema });
/**
* The list a log holds, or `null` when no `todo_write` call has taken effect in it.
*
* The **newest successful call wins**, and its own input is the list: a call is the whole list,
* so the last one the model made is the current state. A call that failed — refused under a
* policy, refused by the tool's schema, timed out, interrupted, or `execution lost` — changed
* nothing and is skipped, which is why only a call with a non-error result counts: a call the
* brain stored and never answered is a call that never ran.
*
* A `session.rewind` that took a call back removes it from the array the caller passes (the
* replay read skips what a range covers), so a branch nobody is on contributes nothing. The
* events are read in the order they are given: a log is in `seq` order, so the last match wins.
*
* @param events a session's log, as a replay read handed it over
*/
function readTodoList(events) {
	const results = /* @__PURE__ */ new Map();
	for (const event of events) if (event.type === EVENT_TYPES.agentToolResult) results.set(event.tool_use_id, event.is_error);
	let list = null;
	for (const event of events) {
		if (event.type !== EVENT_TYPES.agentToolUse || event.name !== "todo_write") continue;
		if (results.get(event.id) !== false) continue;
		const parsed = TodoWriteInputSchema.safeParse(event.input);
		if (parsed.success) list = parsed.data.todos;
	}
	return list;
}
//#endregion
//#region src/web-search.ts
/**
* The `web_search` tool's name (epic #303, [#335](https://github.com/amirtuval/openharness/issues/335)).
*
* `web_search` is the one built-in tool this build does **not** run: it is offered by attaching
* the chat model's own provider's native search tool to the request, and the provider executes
* the search inside that one model request. Nothing about it is a `@openharness/hands` tool
* ({@link https://github.com/amirtuval/openharness/issues/305} built it on an operator-paid
* search API, and #335 removed that).
*
* The name still lives here, beside `ask_user` and `todo_write`, because it is the spelling
* three packages have to agree on and none of them owns: a user's stored tool settings and a
* mode's override are keyed by it (`builtin.web_search`, see `./resources/tool-settings`), a
* request's `span.model_request_start.tools` records it, and a client renders it. Keeping the
* name is what makes the settings a user already saved — and the modes they wrote — carry over
* from the operator-key era unchanged.
*/
/** The name the model calls the tool by, and the name a reader looks for in the log. */
const WEB_SEARCH_TOOL_NAME = "web_search";
//#endregion
//#region src/index.ts
/**
* `@openharness/protocol` — the wire contract every other package codes against.
*
* It holds zod schemas and the TypeScript types inferred from them for the four things that
* cross a boundary in openharness:
*
* - **resources** — `agent` and `session`, and the request/response bodies of the endpoints
*   that manage them (`./resources`)
* - **events** — the session log's vocabulary, its unions, and the events API (`./events`)
* - **errors** — the Anthropic error envelope and its status codes (`./errors`)
* - **ids and constants** — id generation and parsing, page cursors, header names, and the
*   session → partition hash (`./ids`, `./pagination`, `./constants`)
* - **providers** — the model providers openharness knows, the one list every side references
*   (`./providers`, epic #245), and the credential types beyond its fixed ids
*   (`./credential-types`)
* - **reasoning** — how much a model is asked to think, mapped per provider (`./reasoning`)
*
* Everything here is pure: schemas, types and a few helpers, with `zod` as the only runtime
* dependency and no I/O anywhere.
*
* The API follows Anthropic's Managed Agents API for the subset v1 supports. Every place
* this package deviates from it or adds to it is marked `// extension:` at the definition and
* listed in `AGENTS.md`.
*/
/** This package's name; a cheap way for a dependent to prove the import resolved. */
const PACKAGE_NAME = "@openharness/protocol";
//#endregion
export { AGENT_DESCRIPTION_MAX_LENGTH, AGENT_MESSAGE_TOOL_NAME, AGENT_NAME_MAX_LENGTH, AGENT_STOP_TOOL_NAME, AGENT_TOOL_NAME, ANTHROPIC_BETA_HEADER, ANTHROPIC_VERSION_HEADER, API_ERROR_STATUS_BY_TYPE, API_ERROR_TYPES, API_VERSION_DATE, API_VERSION_PREFIX, ASK_USER_HEADER_MAX_LENGTH, ASK_USER_MAX_OPTIONS, ASK_USER_MAX_QUESTIONS, ASK_USER_MIN_OPTIONS, ASK_USER_MIN_QUESTIONS, ASK_USER_TOOL_NAME, AfterSeqSchema, AgentEventSchema, AgentIdSchema, AgentMcpToolResultEventSchema, AgentMcpToolUseEventSchema, AgentMessageEventSchema, AgentMessageToolInputSchema, AgentOpaqueContentEventSchema, AgentSchema, AgentStopToolInputSchema, AgentToolInputSchema, AgentToolResultEventSchema, AgentToolUseEventSchema, ApiErrorBodySchema, ApiErrorSchema, ApiErrorTypeSchema, ApiKeyProviderCredentialMetadataSchema, ApiKeyProviderCredentialSchema, AskUserAnswerSchema, AskUserAnswersSchema, AskUserChoiceOptionSchema, AskUserChoiceQuestionSchema, AskUserConfirmQuestionSchema, AskUserInputSchema, AskUserQuestionSchema, AskUserTextQuestionSchema, AzureOpenAICredentialSchema, AzureOpenAIProviderCredentialMetadataSchema, BEDROCK_REGIONS, BEDROCK_REGION_PATTERN, BedrockCredentialDetailsSchema, BedrockCredentialSchema, BedrockProviderCredentialMetadataSchema, BuiltinToolSettingSchema, COMPACTION_THRESHOLD_MAX, COMPACTION_THRESHOLD_MIN, COMPACT_INSTRUCTIONS_MAX_LENGTH, CREDENTIAL_NAME_MAX_LENGTH, CREDENTIAL_NAME_PATTERN, CREDENTIAL_TYPES, ClearedResultsSchema, CompactSessionRequestSchema, CompactSessionResponseSchema, ConnectMcpServerRequestSchema, ConnectMcpServerResponseSchema, ContentBlockSchema, ContentBlocksSchema, ContentDeltaSchema, ContextSummaryCoversSchema, ContextSummaryEventSchema, ContextSummaryProgressEventSchema, ContextSummaryReasonSchema, CreateAgentRequestSchema, CreateMcpServerRequestSchema, CreateModeRequestSchema, CreateSessionRequestSchema, DEFAULT_BEDROCK_REGION, DEFAULT_EVENT_ORDER, DEFAULT_MAX_AGENT_DEPTH, DEFAULT_MAX_LIVE_TASKS, DEFAULT_MCP_TOOL_PERMISSION, DEFAULT_MODEL_PATTERN, DEFAULT_PAGE_LIMIT, DEFAULT_PARTITION_COUNT, DEFAULT_USER_THEME, DEFAULT_USER_TOOL_SETTINGS, DailyUsageSchema, DeltaTypeSchema, DescendantUsageSchema, EVENT_TYPES, EventIdSchema, EventInputSchema, EventSeqSchema, GetMeResponseSchema, GetPreferencesResponseSchema, GetSkillResponseSchema, ID_PREFIXES, ImportSkillsResponseSchema, JSON_CONTENT_TYPE, JsonValueSchema, KeyCursorSchema, LAST_EVENT_ID_HEADER, ListAgentsQuerySchema, ListAgentsResponseSchema, ListEventsQuerySchema, ListEventsResponseSchema, ListMcpServersResponseSchema, ListModelsQuerySchema, ListModelsResponseSchema, ListModesResponseSchema, ListOrderSchema, ListProviderCredentialsResponseSchema, ListSessionsQuerySchema, ListSessionsResponseSchema, ListSkillsResponseSchema, ListToolSettingsQuerySchema, ListToolSettingsResponseSchema, LocalDaySchema, MAX_AZURE_DEPLOYMENTS, MAX_EVENT_DELTAS, MAX_INITIAL_EVENTS, MAX_MCP_HEADERS, MAX_MCP_SERVERS_PER_USER, MAX_MODES_PER_USER, MAX_PAGE_LIMIT, MAX_SKILLS_PER_USER, MAX_SKILL_FILES, MCP_HEADER_NAME_MAX_LENGTH, MCP_HEADER_VALUE_MAX_LENGTH, MCP_LAST_ERROR_MAX_LENGTH, MCP_SERVER_NAME_FALLBACK, MCP_SERVER_NAME_MAX_LENGTH, MCP_SERVER_NAME_PATTERN, MCP_TOOL_NAME_MAX_LENGTH, MCP_TOOL_NAME_PATTERN, MCP_TOOL_NAME_SEPARATOR, MESSAGE_PARENT_TOOL_NAME, METADATA_MAX_KEY_LENGTH, METADATA_MAX_PAIRS, METADATA_MAX_VALUE_LENGTH, MODE_DEFAULT_MODEL, MODE_ITEM_DEFAULT, MODE_NAME_MAX_LENGTH, MODE_SUBAGENT_DESCRIPTION_MAX_LENGTH, McpAuthTypeSchema, McpHeadersSchema, McpOAuthClientSchema, McpServerIdSchema, McpServerNameSchema, McpServerSchema, McpServerStatusSchema, McpServerUrlSchema, McpToolDefinitionSchema, McpToolSummarySchema, MessageParentToolInputSchema, MessageSkillSchema, MetadataSchema, ModeIdSchema, ModeItemDefaultSchema, ModeModelSchema, ModeReferenceSchema, ModeSchema, ModeToolOverrideSchema, ModelConfigSchema, ModelCostSchema, ModelEntrySchema, ModelRequestEndEventSchema, ModelRequestPurposeSchema, ModelRequestStartEventSchema, ModelUsageBreakdownSchema, ModelUsageSchema, MoneySchema, NextPageSchema, OpaquePartSchema, OpenAICompatibleCredentialDetailsSchema, OpenAICompatibleCredentialSchema, OpenAICompatibleProviderCredentialMetadataSchema, PACKAGE_NAME, PAGE_CURSOR_PREFIX, PROVIDERS, PROVIDER_CREDENTIAL_TYPES, PROVIDER_IDS, PageCursorSchema, PageCursorStringSchema, PageLimitSchema, PreferencesDefaultsSchema, PreviewSkillImportRequestSchema, PreviewSkillImportResponseSchema, ProcessedAtSchema, ProviderCatalogStatusSchema, ProviderCredentialIdSchema, ProviderCredentialSchema, ProviderCredentialTypeSchema, PutPreferencesRequestSchema, PutProviderCredentialRequestSchema, PutToolSettingsRequestSchema, QueuedProcessedAtSchema, REASONING_EFFORTS, REQUEST_ID_HEADER, RESERVED_SKILL_NAMES, ReasoningEffortRunSchema, ReasoningEffortSchema, RefreshSkillPreviewResponseSchema, RefreshSkillRequestSchema, RequestReminderSchema, RetryStatusSchema, RetryStatusTypeSchema, SEARCH_PRICE_PER_CALL, SESSION_TITLE_MAX_LENGTH, SKILL_ARGUMENTS_MAX_LENGTH, SKILL_CLONE_MAX_BYTES, SKILL_DESCRIPTION_MAX_LENGTH, SKILL_FILE_MAX_BYTES, SKILL_IMPORT_FAILURES, SKILL_IMPORT_MESSAGES, SKILL_INFLATE_MAX_BYTES, SKILL_MAX_BYTES, SKILL_MD_FILENAME, SKILL_MD_MAX_BYTES, SKILL_NAME_MAX_LENGTH, SKILL_NAME_PATTERN, SKILL_PROBLEM_CODES, SSE_CONTENT_TYPE, STORED_EVENT_TYPES, SUMMARY_MAX_PASSES_MAX, SUMMARY_MAX_PASSES_MIN, SUMMARY_MODEL_SAME_AS_CHAT, SendEventsRequestSchema, SendEventsResponseSchema, SeqCursorSchema, SessionAgentSchema, SessionCompactEventInputSchema, SessionCompactEventSchema, SessionCompactionEventSchema, SessionCompactionOutcomeSchema, SessionDeletedEventSchema, SessionErrorEventSchema, SessionErrorSchema, SessionErrorTypeSchema, SessionEventSchema, SessionIdSchema, SessionModelUsageSchema, SessionRewindEventInputSchema, SessionRewindEventSchema, SessionSchema, SessionSkillLoadedEventSchema, SessionStatusIdleEventSchema, SessionStatusRescheduledEventSchema, SessionStatusRunningEventSchema, SessionStatusSchema, SessionTaskNotificationEventSchema, SessionTaskStartedEventSchema, SessionTaskStatusEventSchema, SessionUsageEventSchema, SessionUsageSchema, SkillFileContentSchema, SkillFileEncodingSchema, SkillFileSchema, SkillFrontmatterSchema, SkillIdSchema, SkillImportCandidateSchema, SkillImportRequestSchema, SkillImportSourceSchema, SkillNameSchema, SkillProblemSchema, SkillSchema, SkillSourceSchema, SourceSchema, SourcesSchema, SpanErrorSchema, SpanErrorTypeSchema, SpanEventSchema, StopReasonSchema, StoredEventDeltaSchema, StoredEventSchema, StoredEventStartSchema, StoredEventTypeSchema, StreamEventSchema, StreamEventsQuerySchema, SummaryModelSchema, SupersedesSchema, TASK_DESCRIPTION_MAX_LENGTH, TASK_KINDS, TASK_NOTIFICATION_KINDS, TASK_SOURCE_KEY_MAX_LENGTH, TASK_STATUSES, TASK_WAIT_DEFAULT_TIMEOUT_MS, TASK_WAIT_MAX_TIMEOUT_MS, TASK_WAIT_TOOL_NAME, TODO_WRITE_TOOL_NAME, TOKENS_PER_CHARACTER_RATIO, TOOL_NAME_MAX_LENGTH, TaskEventSchema, TaskIdSchema, TaskKindSchema, TaskNotificationKindSchema, TaskStatusSchema, TaskWaitInputSchema, TextBlockSchema, TimestampSchema, TodoItemSchema, TodoListSchema, TodoStatusSchema, TodoWriteInputSchema, ToolConfirmationRememberSchema, ToolConfirmationResultSchema, ToolInputSchema, ToolNameSchema, ToolPermissionSchema, ToolReferenceSchema, ToolResultTruncationSchema, ToolSettingEntrySchema, ToolSourceSchema, TotalCostSchema, TruncationSchema, ULID_LENGTH, UpdateAgentRequestSchema, UpdateMcpServerRequestSchema, UpdateModeRequestSchema, UpdateSkillRequestSchema, UsageSearchesSchema, UsageTotalsSchema, UserEventInputSchema, UserEventSchema, UserIdSchema, UserInterruptEventInputSchema, UserInterruptEventSchema, UserMessageEventInputSchema, UserMessageEventSchema, UserPreferencesSchema, UserSchema, UserThemeSchema, UserToolConfirmationEventInputSchema, UserToolConfirmationEventSchema, UserToolSettingsSchema, UserUsageQuerySchema, UserUsageSchema, VERTEX_LOCATIONS, VertexCredentialDetailsSchema, VertexCredentialSchema, VertexProviderCredentialMetadataSchema, WEB_SEARCH_TOOL_NAME, apiErrorBody, askUserAnswerProblems, askUserInputProblems, bedrockCredentialDetails, bedrockRegionOf, builtinToolInForce, credentialDetails, credentialTypeInfo, credentialTypeName, decodePageCursor, defaultCredentialName, deriveSessionTitle, encodeKeyCursor, encodeSeqCursor, estimateToolDefinitionTokens, estimateToolsTokens, formatAskUserAnswers, generateId, httpStatusForErrorType, isAgentId, isApiErrorType, isBedrockRegion, isEventId, isId, isMcpServerId, isMcpServerName, isMcpServerUrl, isMcpToolCall, isMcpToolOfferedName, isModeId, isPageCursor, isProviderCredentialId, isQueuedEventType, isReservedCredentialName, isReservedSkillName, isServiceAccountKey, isSessionId, isSkillId, isSkillName, isStoredEvent, isTaskId, isToolCallEvent, isToolResultEvent, isUlid, isValidCredentialName, mcpServerInForce, mcpServerNameFromUrl, mcpToolOfferedName, mcpToolSummary, modeItemInForce, newAgentId, newEventId, newMcpServerId, newModeId, newProviderCredentialId, newSessionId, newSkillId, newTaskId, parseAskUserInput, parseId, parseServiceAccountKey, parseTaskWaitInput, partitionOf, readTodoList, requestedMcpAuth, searchCost, skillBody, skillFileSize, skillInForce, skillSettingsOf, skillsInForce, taskWaitInputProblems, taskWaitTimeoutMs, toolCallId, toolCallInput, toolCallName, toolCallOfferedName, toolCallPermission, toolCallServer, toolCallSource, toolResultCallId, toolResultIsError, totalCost, tryDecodePageCursor, tryParseId, ulid, usageCost };

//# sourceMappingURL=index.js.map